import React, { useState, useEffect, useCallback } from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Legend,
  CartesianGrid,
  Cell,
  ReferenceLine,
} from "recharts";

// Custom Vault Logo - 3D vault with Ripple triskelion handle
function RippleLogo({ size = 50 }) {
  const id = Math.random().toString(36).substr(2, 9);
  
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" style={{ filter: 'drop-shadow(0 0 15px rgba(0, 207, 255, 0.7))' }}>
      <defs>
        {/* 3D vault body gradient */}
        <linearGradient id={`vaultBody-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#4a5568" />
          <stop offset="30%" stopColor="#2d3748" />
          <stop offset="70%" stopColor="#1a202c" />
          <stop offset="100%" stopColor="#171923" />
        </linearGradient>
        
        {/* Vault door metallic gradient */}
        <radialGradient id={`vaultDoor-${id}`} cx="35%" cy="35%">
          <stop offset="0%" stopColor="#718096" />
          <stop offset="50%" stopColor="#4a5568" />
          <stop offset="100%" stopColor="#2d3748" />
        </radialGradient>
        
        {/* Ripple blue gradient */}
        <linearGradient id={`rippleBlue-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00E5FF" />
          <stop offset="50%" stopColor="#00CFFF" />
          <stop offset="100%" stopColor="#0099DD" />
        </linearGradient>
        
        {/* Gold/bronze accent */}
        <linearGradient id={`goldAccent-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#ffd700" />
          <stop offset="50%" stopColor="#daa520" />
          <stop offset="100%" stopColor="#b8860b" />
        </linearGradient>
        
        {/* Shine highlight */}
        <linearGradient id={`shine-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.4)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0)" />
        </linearGradient>
      </defs>
      
      {/* Vault body - 3D effect with multiple layers */}
      {/* Back shadow */}
      <rect x="12" y="18" width="78" height="72" rx="6" fill="#0a0a0a" opacity="0.5" />
      
      {/* Main vault body */}
      <rect x="8" y="14" width="78" height="72" rx="6" fill={`url(#vaultBody-${id})`} stroke="#4a5568" strokeWidth="2" />
      
      {/* Front face highlight */}
      <rect x="10" y="16" width="40" height="35" rx="4" fill={`url(#shine-${id})`} opacity="0.3" />
      
      {/* Vault door */}
      <circle cx="47" cy="50" r="28" fill={`url(#vaultDoor-${id})`} stroke="#5a6a7a" strokeWidth="3" />
      
      {/* Door outer ring */}
      <circle cx="47" cy="50" r="24" fill="none" stroke={`url(#rippleBlue-${id})`} strokeWidth="2" opacity="0.8" />
      
      {/* Ripple Triskelion Handle - three connected spheres */}
      <g transform="translate(47, 50)">
        {/* Center connecting lines */}
        <line x1="0" y1="-10" x2="8.66" y2="5" stroke={`url(#rippleBlue-${id})`} strokeWidth="3" strokeLinecap="round" />
        <line x1="0" y1="-10" x2="-8.66" y2="5" stroke={`url(#rippleBlue-${id})`} strokeWidth="3" strokeLinecap="round" />
        <line x1="8.66" y1="5" x2="-8.66" y2="5" stroke={`url(#rippleBlue-${id})`} strokeWidth="3" strokeLinecap="round" />
        
        {/* Three spheres of triskelion */}
        <circle cx="0" cy="-10" r="6" fill={`url(#rippleBlue-${id})`} />
        <circle cx="0" cy="-10" r="3" fill="rgba(255,255,255,0.4)" />
        
        <circle cx="8.66" cy="5" r="6" fill={`url(#rippleBlue-${id})`} />
        <circle cx="8.66" cy="5" r="3" fill="rgba(255,255,255,0.4)" />
        
        <circle cx="-8.66" cy="5" r="6" fill={`url(#rippleBlue-${id})`} />
        <circle cx="-8.66" cy="5" r="3" fill="rgba(255,255,255,0.4)" />
      </g>
      
      {/* Locking bolts around door */}
      <circle cx="20" cy="35" r="3" fill={`url(#goldAccent-${id})`} />
      <circle cx="20" cy="65" r="3" fill={`url(#goldAccent-${id})`} />
      <circle cx="74" cy="35" r="3" fill={`url(#goldAccent-${id})`} />
      <circle cx="74" cy="65" r="3" fill={`url(#goldAccent-${id})`} />
      
      {/* Status light */}
      <circle cx="80" cy="22" r="4" fill="#00ff88" />
      <circle cx="80" cy="22" r="6" fill="none" stroke="#00ff88" strokeWidth="1.5" opacity="0.6" />
      <circle cx="80" cy="22" r="8" fill="none" stroke="#00ff88" strokeWidth="1" opacity="0.3" />
      
      {/* Hinges on left side */}
      <rect x="6" y="28" width="6" height="12" rx="2" fill="#3a4a5a" />
      <rect x="6" y="60" width="6" height="12" rx="2" fill="#3a4a5a" />
    </svg>
  );
}

// Mini version for stat cards
function RippleIcon({ size = 24 }) {
  const id = Math.random().toString(36).substr(2, 9);
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" opacity="0.7">
      <defs>
        <linearGradient id={`miniRipple-${id}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#00E5FF" />
          <stop offset="100%" stopColor="#00CFFF" />
        </linearGradient>
      </defs>
      {/* Simplified vault */}
      <rect x="15" y="20" width="65" height="60" rx="5" fill="none" stroke={`url(#miniRipple-${id})`} strokeWidth="4" />
      <circle cx="47" cy="50" r="18" fill="none" stroke={`url(#miniRipple-${id})`} strokeWidth="3" />
      {/* Mini triskelion */}
      <circle cx="47" cy="42" r="4" fill={`url(#miniRipple-${id})`} />
      <circle cx="53" cy="54" r="4" fill={`url(#miniRipple-${id})`} />
      <circle cx="41" cy="54" r="4" fill={`url(#miniRipple-${id})`} />
    </svg>
  );
}

// Base ETF data - XRP holdings update slowly (via manual updates or future API)
// AUM and prices are calculated dynamically based on live XRP price
const baseETFData = [
  { ticker: 'XRPC', name: 'Canary Capital', xrpLocked: 178.5, sharesOutstanding: 21.5, fee: 0.45, exchange: 'NASDAQ', launchDate: 'Nov 13, 2025' },
  { ticker: 'XRP', name: 'Bitwise', xrpLocked: 146.3, sharesOutstanding: 16.8, fee: 0.34, exchange: 'NYSE', launchDate: 'Nov 20, 2025' },
  { ticker: 'XRPZ', name: 'Franklin Templeton', xrpLocked: 131.1, sharesOutstanding: 15.5, fee: 0.19, exchange: 'NYSE Arca', launchDate: 'Nov 24, 2025' },
  { ticker: 'GXRP', name: 'Grayscale', xrpLocked: 121.5, sharesOutstanding: 8.1, fee: 0.35, exchange: 'NYSE Arca', launchDate: 'Nov 24, 2025' },
  { ticker: 'TOXR', name: '21Shares', xrpLocked: 114.1, sharesOutstanding: 13.3, fee: 0.30, exchange: 'Cboe BZX', launchDate: 'Dec 11, 2025' },
  { ticker: 'XRPR', name: 'REX-Osprey', xrpLocked: 49.8, sharesOutstanding: 7.8, fee: 0.75, exchange: 'Cboe BZX', launchDate: 'Oct 29, 2025' },
];

// Futures & Leveraged XRP ETFs (don't hold spot XRP)
const futuresETFData = [
  { ticker: 'XXRP', name: 'Teucrium', leverage: '2x', type: 'Leveraged', fee: 1.89, exchange: 'NYSE Arca', launchDate: 'Apr 8, 2025', divYield: 6.23, description: '2x daily XRP futures' },
  { ticker: 'XRPI', name: 'Volatility Shares', leverage: '1x', type: 'Futures', fee: 0.94, exchange: 'NASDAQ', launchDate: 'May 22, 2025', divYield: 2.34, description: '1:1 XRP futures exposure' },
  { ticker: 'XRPT', name: 'Volatility Shares', leverage: '2x', type: 'Leveraged', fee: 1.15, exchange: 'NASDAQ', launchDate: 'Jul 2025', divYield: 1.8, description: '2x daily XRP futures' },
  { ticker: 'UXRP', name: 'ProShares', leverage: '2x', type: 'Leveraged', fee: 1.67, exchange: 'NYSE Arca', launchDate: 'Jul 14, 2025', divYield: 0.01, description: 'Ultra XRP (2x)' },
  { ticker: 'KXRP', name: 'Kurv', leverage: '—', type: 'Income', fee: 0.99, exchange: 'NYSE Arca', launchDate: 'Mar 11, 2026', divYield: null, description: 'Options-based income strategy' },
];

// Upcoming Futures ETFs
const upcomingFuturesETFs = [
  // None currently - KURV launched Mar 11, 2026
];

// ─── HYPERLIQUID BOT CONFIG ────────────────────────────────────────────────
const HYPERLIQUID_BOTS = [
  {
    id: "bot1",
    label: "Bot 1 — LONG",
    address: "0x0F5EAac44610e86eeF687c3934def36295daF214",
    color: "#00e5c0",
    dimColor: "#00e5c033",
  },
  {
    id: "bot2",
    label: "Bot 2 — SHORT",
    address: "0x03402B6EC72576790147A93494a6913830C5619e",
    color: "#f59e0b",
    dimColor: "#f59e0b33",
  },
];

const HL_API = "https://api.hyperliquid.xyz/info";

// Hyperliquid API helpers
async function hlFetch(body) {
  const res = await fetch(HL_API, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`HL API error: ${res.status}`);
  return res.json();
}

async function fetchAccountState(address) {
  return hlFetch({ type: "clearinghouseState", user: address });
}

async function fetchPortfolio(address) {
  try {
    // Try to get historical portfolio data
    const res = await hlFetch({ type: "portfolio", user: address });
    return res;
  } catch (e) {
    // If portfolio endpoint fails, return null - we'll compute from fills
    console.log("Portfolio fetch failed, will compute from fills:", e.message);
    return null;
  }
}

async function fetchFills(address) {
  return hlFetch({ type: "userFills", user: address });
}

// Format helpers for trading dashboard
const fmtUSD = (v) =>
  v == null
    ? "—"
    : Number(v).toLocaleString("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 2 });

const fmtTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtDateShort = (ts) => {
  const d = new Date(ts);
  return d.toLocaleString("en-US", { month: "short", day: "numeric" });
};

// Ripple Partners & XRP Community X Accounts
const defaultXAccounts = [
  // Official Ripple
  { handle: 'Ripple', displayName: 'Ripple', verified: true, category: 'official' },
  { handle: 'RippleXDev', displayName: 'RippleX Developers', verified: true, category: 'official' },
  
  // Major Banking Partners
  { handle: 'santaborcoUK', displayName: 'Santander UK', verified: true, category: 'bank' },
  { handle: 'BankofAmerica', displayName: 'Bank of America', verified: true, category: 'bank' },
  { handle: 'sbigroup', displayName: 'SBI Holdings', verified: true, category: 'bank' },
  { handle: 'StanChart', displayName: 'Standard Chartered', verified: true, category: 'bank' },
  { handle: 'BNYMellon', displayName: 'BNY Mellon', verified: true, category: 'bank' },
  { handle: 'CIBC', displayName: 'CIBC', verified: true, category: 'bank' },
  { handle: 'dbsbank', displayName: 'DBS Bank', verified: true, category: 'bank' },
  
  // Payment Partners
  { handle: 'AmericanExpress', displayName: 'American Express', verified: true, category: 'payments' },
  { handle: 'Mastercard', displayName: 'Mastercard', verified: true, category: 'payments' },
  { handle: 'MoneyGram', displayName: 'MoneyGram', verified: true, category: 'payments' },
  { handle: 'WesternUnion', displayName: 'Western Union', verified: true, category: 'payments' },
  { handle: 'Tranglo', displayName: 'Tranglo', verified: false, category: 'payments' },
  
  // Institutional & ETF Issuers
  { handle: 'BlackRock', displayName: 'BlackRock', verified: true, category: 'institutional' },
  { handle: 'FTI_US', displayName: 'Franklin Templeton', verified: true, category: 'institutional' },
  { handle: 'Grayscale', displayName: 'Grayscale', verified: true, category: 'institutional' },
  { handle: 'BitwiseInvest', displayName: 'Bitwise', verified: true, category: 'institutional' },
  { handle: '21Shares', displayName: '21Shares', verified: true, category: 'institutional' },
  { handle: 'CanaryFunds', displayName: 'Canary Capital', verified: false, category: 'institutional' },
  
  // Ripple Acquisitions
  { handle: 'HiddenRoadHQ', displayName: 'Hidden Road', verified: false, category: 'ripple-owned' },
  { handle: 'metaborc', displayName: 'Metaco', verified: false, category: 'ripple-owned' },
  
  // XRP Community Analysts
  { handle: 'baboracles', displayName: 'Bab', verified: false, category: 'analyst' },
  { handle: 'beyond_broke', displayName: 'Jake Claver', verified: false, category: 'analyst' },
  { handle: 'WallStreetBulls', displayName: 'Wall Street Bulls', verified: true, category: 'analyst' },
  { handle: 'XRPcryptowolf', displayName: 'CryptoWolf', verified: false, category: 'analyst' },
  { handle: 'DigPerspectives', displayName: 'Digital Perspectives', verified: false, category: 'analyst' },
  { handle: 'Leerzeit', displayName: 'Mr. Huber', verified: false, category: 'analyst' },
  { handle: 'BlockchainBacker', displayName: 'Blockchain Backer', verified: false, category: 'analyst' },
  { handle: 'CryptoEri', displayName: 'Crypto Eri', verified: false, category: 'analyst' },
  { handle: 'JohnEDeaton1', displayName: 'John Deaton', verified: true, category: 'analyst' },
  { handle: 'attorneyjeremy1', displayName: 'Jeremy Hogan', verified: false, category: 'analyst' },
  { handle: 'sentosumosaba', displayName: 'Zach Rector', verified: false, category: 'analyst' },
  
  // News & Media
  { handle: 'CoinDesk', displayName: 'CoinDesk', verified: true, category: 'news' },
  { handle: 'Cointelegraph', displayName: 'Cointelegraph', verified: true, category: 'news' },
  { handle: 'TheBlock__', displayName: 'The Block', verified: true, category: 'news' },
];

const mockNewsItems = [
  { id: 1, handle: 'Ripple', text: 'Ripple secures EMI license from UK FCA. Another major jurisdiction unlocked for RLUSD and cross-border payments.', time: '2h', likes: 4521, retweets: 892, category: 'official' },
  { id: 2, handle: 'sbigroup', text: 'SBI Remit expands XRP-powered ODL corridors to Vietnam and Indonesia. Settlement times reduced from days to seconds.', time: '3h', likes: 2341, retweets: 445, category: 'bank' },
  { id: 3, handle: 'WallStreetBulls', text: 'BREAKING: XRP ETF weekly inflows hit $89M. XRPC leading with $34M. Institutional adoption accelerating.', time: '4h', likes: 2891, retweets: 567, category: 'analyst' },
  { id: 4, handle: 'Mastercard', text: 'Piloting XRPL-based stablecoin settlements for card payments. Digital assets are entering everyday commerce.', time: '5h', likes: 8934, retweets: 2103, category: 'payments' },
  { id: 5, handle: 'beyond_broke', text: 'ETFs are eating through OTC supply faster than expected. ~800M XRP absorbed in week one. When dark pools run dry, exchanges become the only source. Strap in.', time: '5h', likes: 3421, retweets: 812, category: 'analyst' },
  { id: 6, handle: 'baboracles', text: 'Hidden Road now processing $3T+ in cleared trades. DTCC tokenization goes live April 2026. Connect the dots.', time: '6h', likes: 1923, retweets: 445, category: 'analyst' },
  { id: 7, handle: 'FTI_US', text: 'Franklin Templeton and DBS Group launch tokenized money market fund on XRP Ledger for institutional investors.', time: '7h', likes: 5672, retweets: 1203, category: 'institutional' },
  { id: 8, handle: 'DigPerspectives', text: 'GTreasury (Ripple-owned) is a SWIFT certified partner AND integrated with J.P. Morgan. Trojan horse strategy in full effect.', time: '8h', likes: 3102, retweets: 721, category: 'analyst' },
  { id: 9, handle: 'dbsbank', text: 'DBS partners with Franklin Templeton on tokenized fund. Settlement on XRP Ledger enables T+0 clearing.', time: '10h', likes: 4521, retweets: 892, category: 'bank' },
  { id: 10, handle: 'XRPcryptowolf', text: 'Supply squeeze update: Exchange reserves down another 12M XRP this week. OTC desks reporting low inventory.', time: '12h', likes: 2456, retweets: 534, category: 'analyst' },
  { id: 11, handle: 'HiddenRoadHQ', text: 'Post-trade clearing volumes continue to grow. XRP Ledger integration for institutional settlement on track for Q2.', time: '14h', likes: 1892, retweets: 367, category: 'ripple-owned' },
  { id: 12, handle: 'BitwiseInvest', text: 'XRP ETF (ticker: XRP) sees continued institutional demand. AUM approaching $310M milestone.', time: '16h', likes: 3421, retweets: 678, category: 'institutional' },
  { id: 13, handle: 'Leerzeit', text: 'Citadel Securities in Ripple\'s $500M round. The biggest market maker on Wall Street is positioning for tokenized finance.', time: '1d', likes: 5672, retweets: 1203, category: 'analyst' },
  { id: 14, handle: 'JohnEDeaton1', text: 'SEC case officially closed. XRP clarity achieved. Now watch the floodgates open for institutional adoption.', time: '1d', likes: 12450, retweets: 3421, category: 'analyst' },
  { id: 15, handle: 'CoinDesk', text: 'Ripple\'s banking partnerships exceed 300 institutions globally. RippleNet volume up 9x year-over-year.', time: '1d', likes: 4521, retweets: 1102, category: 'news' },
  { id: 16, handle: 'BlockchainBacker', text: 'Technical analysis: XRP completing 7-year cup and handle. Measured move target aligns with institutional thesis.', time: '2d', likes: 3891, retweets: 867, category: 'analyst' },
];

const educationArticles = [
  { 
    title: 'What is an XRP ETF?', 
    description: 'Complete guide to understanding XRP spot ETFs, how they work, and their benefits for investors.', 
    category: 'Education', 
    readTime: '8 min', 
    icon: '📚',
    content: `**What Is a Spot XRP ETF?**

A spot XRP ETF (Exchange-Traded Fund) is a regulated investment vehicle that holds actual XRP tokens and trades on traditional stock exchanges like the NYSE and NASDAQ. When you buy shares of an XRP ETF, you gain direct exposure to XRP's price movements without managing cryptocurrency wallets, private keys, or exchanges.

**How It Works:**

1. The ETF issuer (Bitwise, Franklin Templeton, etc.) purchases real XRP
2. They store it with institutional custodians (Coinbase Custody, BitGo)
3. They issue shares representing fractional ownership of that XRP
4. You buy/sell shares through your regular brokerage account
5. Share price tracks XRP's market value (minus fees)

**Key Benefits:**

**Simplicity:** No crypto exchanges, no wallets, no seed phrases. Buy and sell like any stock.

**Security:** Institutional-grade custody with insurance. No risk of losing keys or getting hacked personally.

**Tax Reporting:** Your broker provides 1099 forms. No complex crypto tax calculations.

**Retirement Accounts:** Hold XRP exposure in your 401k, IRA, or Roth IRA if your plan allows.

**Regulation:** SEC-approved products with investor protections.

**The Tradeoff:**

ETFs charge expense ratios (0.19% to 0.75% annually), and you don't actually own XRP — you own shares representing XRP. You can't transfer it to a wallet, use it for payments, or stake it.

**Who Should Use XRP ETFs:**

• Traditional investors comfortable with stocks but not crypto
• Retirement account holders wanting XRP exposure
• Anyone prioritizing simplicity over direct ownership
• Investors seeking regulated, insured exposure

**Who Should Buy Actual XRP:**

• Those who want full control and self-custody
• Users who may need XRP for transactions
• Long-term holders avoiding ongoing fees
• Those comfortable with crypto security practices

**Bottom Line:**

XRP ETFs democratize access to XRP for millions of investors who previously couldn't or wouldn't touch cryptocurrency directly. They're a gateway, not a replacement for direct ownership.`
  },
  { 
    title: 'How to Invest in XRP ETFs', 
    description: 'Step-by-step guide to buying XRP ETFs through Vanguard, Fidelity, Schwab, and other brokers.', 
    category: 'Guide', 
    readTime: '10 min', 
    icon: '💰',
    content: `**Step-by-Step: Buying XRP ETFs**

**Step 1: Choose Your Broker**

Most major brokers now offer XRP ETFs:

• **Fidelity** — Full access, no commission
• **Charles Schwab** — Full access, no commission
• **Vanguard** — Available (may require calling to enable)
• **TD Ameritrade** — Full access via Schwab integration
• **Robinhood** — Available for most ETFs
• **E*TRADE** — Full access

**Step 2: Fund Your Account**

Transfer cash via:
• Bank ACH transfer (free, 1-3 days)
• Wire transfer (fast, may have fees)
• Check deposit

**Step 3: Search for XRP ETF Tickers**

Main spot XRP ETFs:
• **XRP** — Bitwise XRP ETF
• **XRPC** — Canary Capital XRP ETF
• **XRPZ** — Franklin Templeton XRP ETF
• **GXRP** — Grayscale XRP Trust ETF
• **TOXR** — 21Shares XRP ETF
• **XRPR** — REX-Osprey XRP ETF

**Step 4: Place Your Order**

• **Market Order:** Buy immediately at current price
• **Limit Order:** Set your maximum price (recommended)
• **Dollar Amount:** Some brokers allow fractional shares

**Step 5: Confirm and Monitor**

Your shares appear in your portfolio within seconds. Track performance alongside your other investments.

**Buying in Retirement Accounts:**

**401k:** Check if your plan offers a self-directed brokerage option (SDBA). Vanguard, Fidelity, and Schwab plans often include this. You may need to call HR or your plan administrator.

**IRA/Roth IRA:** Open a self-directed IRA at any major broker. Full access to all XRP ETFs. Roth IRA gains are tax-free!

**HSA:** Many HSA providers offer investment options. Check if yours includes brokerage access.

**Which ETF Should You Buy?**

**Lowest Fees:** XRPZ (Franklin Templeton) at 0.19%
**Highest Volume:** XRP (Bitwise) — best liquidity
**First Mover:** XRPC (Canary Capital) — largest AUM
**Institutional Trust:** GXRP (Grayscale) — established brand

**Pro Tips:**

• Use limit orders to avoid paying above market price
• Consider dollar-cost averaging (buying weekly/monthly)
• Compare expense ratios — 0.19% vs 0.75% adds up over years
• Check your broker's trading hours (some allow extended hours)

**Tax Considerations:**

• ETF shares held 1+ year qualify for long-term capital gains (0-20%)
• Short-term gains taxed as ordinary income (up to 37%)
• Dividends (if any) reported on 1099
• Losses can offset gains (tax-loss harvesting)`
  },
  { 
    title: 'XRP ETF Comparison 2026', 
    description: 'In-depth comparison of all spot XRP ETFs including fees, AUM, custody, and performance.', 
    category: 'Comparison', 
    readTime: '12 min', 
    icon: '⚖️',
    content: `**Complete XRP ETF Comparison**

**The Contenders:**

| ETF | Issuer | Fee | Exchange | Custody |
|-----|--------|-----|----------|---------|
| XRPC | Canary Capital | 0.45% | NASDAQ | Coinbase |
| XRP | Bitwise | 0.34% | NYSE | Coinbase |
| XRPZ | Franklin | 0.19% | NYSE Arca | Coinbase |
| GXRP | Grayscale | 0.35% | NYSE Arca | Coinbase |
| TOXR | 21Shares | 0.30% | Cboe BZX | Coinbase |
| XRPR | REX-Osprey | 0.75% | Cboe BZX | BitGo |

**Expense Ratio Breakdown:**

Over 10 years on $100,000 investment (assuming 10% annual return):

• XRPZ (0.19%): Fees = $4,900 | Final: $254,100
• TOXR (0.30%): Fees = $7,700 | Final: $251,300
• XRP (0.34%): Fees = $8,700 | Final: $250,300
• GXRP (0.35%): Fees = $8,900 | Final: $250,100
• XRPC (0.45%): Fees = $11,400 | Final: $247,600
• XRPR (0.75%): Fees = $18,800 | Final: $240,200

**Winner: XRPZ (Franklin Templeton)** — saves $14,000+ over XRPR

**Liquidity & Volume:**

Higher volume = tighter bid-ask spreads = lower trading costs

1. XRP (Bitwise) — Highest daily volume
2. XRPC (Canary) — Strong volume, first mover
3. XRPZ (Franklin) — Growing rapidly
4. GXRP (Grayscale) — Moderate
5. TOXR (21Shares) — Lower but improving
6. XRPR (REX-Osprey) — Lowest volume

**AUM (Assets Under Management):**

Larger AUM = more stability, less closure risk

1. XRPC — Largest, first to market advantage
2. XRP — Close second, best ticker
3. XRPZ — Growing fast on low fees
4. GXRP — Grayscale brand trust
5. TOXR — Solid institutional backing
6. XRPR — Smallest but pioneering

**Custody & Security:**

All major ETFs use institutional custodians:
• **Coinbase Custody:** XRPC, XRP, XRPZ, GXRP, TOXR
• **BitGo:** XRPR

Both are SOC 2 certified with $300M+ insurance policies.

**Our Recommendations:**

**Best Overall:** XRPZ (Franklin Templeton)
Lowest fees from a $1.5T asset manager. Long-term winner.

**Best for Trading:** XRP (Bitwise)
Highest liquidity, tightest spreads, simple ticker.

**Best for Institutions:** GXRP (Grayscale)
Established trust structure, familiar to advisors.

**Budget Pick:** TOXR (21Shares)
Low 0.30% fee, solid European pedigree.

**The Bottom Line:**

For long-term holding: **XRPZ** (lowest fees compound)
For active trading: **XRP** (best liquidity)
For retirement accounts: **XRPZ** or **XRP**`
  },
  { 
    title: 'Understanding ETF Flows', 
    description: 'Learn how to interpret XRP ETF inflows and outflows for better investment decisions.', 
    category: 'Analysis', 
    readTime: '11 min', 
    icon: '📊',
    content: `**Why ETF Flows Matter**

ETF flows reveal what institutional investors are doing with their money. Understanding flows helps you:
• Gauge market sentiment
• Anticipate supply/demand shifts
• Make more informed decisions

**Key Terms:**

**Inflows:** New money entering the ETF (bullish signal)
**Outflows:** Money leaving the ETF (bearish signal)
**Net Flows:** Inflows minus outflows
**AUM:** Total assets under management

**How ETF Creation Works:**

When demand exceeds supply:
1. Authorized Participants (APs) buy XRP on the market
2. They deliver XRP to the ETF custodian
3. ETF issues new shares to the AP
4. AP sells shares to investors

This process removes XRP from circulation, creating buying pressure.

**How ETF Redemption Works:**

When supply exceeds demand:
1. APs return ETF shares to the issuer
2. ETF redeems shares for underlying XRP
3. AP receives XRP from custody
4. AP sells XRP on the market

This process adds XRP back to circulation, creating selling pressure.

**Reading Flow Data:**

**Consecutive Inflow Days:** Strong institutional conviction
Example: XRP ETFs had 35+ consecutive inflow days post-launch

**Large Single-Day Inflows:** Major allocation decision
Example: $50M+ single day = likely pension/endowment entry

**Outflows After Rally:** Profit-taking (normal)
Example: January 7, 2026 saw first outflow after 25% XRP rally

**Sustained Outflows:** Potential trend reversal
Watch for 5+ consecutive outflow days

**XRP ETF Flow Patterns:**

Since November 2025 launch:
• 35 consecutive inflow days (record for crypto ETFs)
• Zero outflow days through December
• $1.3B+ cumulative inflows
• First outflow January 7, 2026 ($40.8M) — quickly recovered

Compare to Bitcoin ETFs:
• Multiple outflow days in same period
• $1.65B single-day outflow on Dec 31

**What Flows DON'T Tell You:**

• Short-term price direction (markets are complex)
• Individual investor sentiment (ETFs = institutions)
• Actual XRP utility/adoption
• Regulatory developments

**Using Flows in Your Strategy:**

**Confirmation Tool:** Flows confirm or contradict your thesis
**Contrarian Signal:** Extreme flows often precede reversals
**Trend Indicator:** Sustained flows suggest durable trends

**Where to Track Flows:**

• SoSoValue (free, comprehensive)
• The Block (detailed charts)
• XRP-Insights.com (XRP-specific)
• ETF.com (general ETF data)

**Pro Tip:** Don't react to single-day flows. Look for patterns over weeks/months.`
  },
  { 
    title: 'DTCC Tokenization & XRP', 
    description: 'How DTCC\'s April 2026 tokenization rollout connects to Ripple and institutional XRP adoption.', 
    category: 'Deep Dive', 
    readTime: '15 min', 
    icon: '🏛️',
    content: `**The DTCC Connection**

The Depository Trust & Clearing Corporation (DTCC) processes over $2 quadrillion in securities annually. They're the backbone of U.S. financial markets. And they're going digital.

**What Is DTCC?**

DTCC is the post-trade infrastructure for:
• Equities (stocks)
• Fixed income (bonds)
• Derivatives
• Mutual funds

Every stock trade you make flows through DTCC for settlement.

**The Tokenization Initiative:**

In 2024-2025, DTCC announced plans to tokenize securities settlement, targeting April 2026 for initial rollout. Key elements:

• Digital representation of securities
• Real-time settlement (vs T+1 or T+2)
• 24/7 trading capability
• Reduced counterparty risk
• Lower operational costs

**The Ripple Connection:**

Ripple has been positioning for this moment:

**1. Hidden Road Acquisition ($1.2B)**
Prime brokerage and clearing services. Direct access to institutional settlement flows.

**2. Metaco Acquisition**
Institutional digital asset custody. DTCC-compatible infrastructure.

**3. Standard Custody Partnership**
Regulated custody solutions for tokenized assets.

**4. RLUSD Stablecoin**
USD-backed stablecoin for settlement. Launched December 2024.

**Why XRP Could Benefit:**

**Bridge Currency:** XRP can settle cross-border legs of tokenized trades in 3-5 seconds.

**Liquidity:** $50B+ market cap provides deep liquidity for large settlements.

**Regulatory Clarity:** "Not a security" ruling removes institutional barriers.

**ODL Infrastructure:** Ripple's On-Demand Liquidity already processes $15B+ annually.

**The Bear Case:**

• DTCC hasn't publicly named XRP as a partner
• They could build proprietary solutions
• Other blockchains (Ethereum, Solana) are competing
• Timelines often slip in finance

**The Bull Case:**

• Ripple's acquisition spree targets exactly this use case
• XRP's speed/cost advantages are unmatched
• SEC clarity gives XRP unique regulatory position
• Ripple has 300+ financial institution relationships

**What to Watch:**

• DTCC partnership announcements (Q1-Q2 2026)
• Hidden Road integration progress
• RLUSD adoption in settlement corridors
• Regulatory developments (CLARITY Act, stablecoin bills)

**Investment Implication:**

If DTCC integrates XRP for even 1% of settlement volume, that's $20 trillion annually flowing through XRP. At current velocity, that would require significantly higher XRP prices to provide adequate liquidity.

**Timeline:**

• Q1 2026: DTCC pilot programs
• Q2 2026: Initial tokenization rollout
• 2026-2027: Broader adoption
• 2028+: Full integration

This is the institutional adoption thesis in action.`
  },
  { 
    title: 'The Institutional Thesis', 
    description: 'Breaking down Ripple\'s $4B+ acquisition spree: Hidden Road, GTreasury, Rail, and more.', 
    category: 'Research', 
    readTime: '20 min', 
    icon: '🔬',
    content: `**Ripple's Master Plan**

Since late 2024, Ripple has spent over $4 billion acquiring companies that position XRP at the center of institutional finance. This isn't random — it's a coordinated infrastructure play.

**The Acquisition Spree:**

**Hidden Road ($1.25B) — January 2025**
• Prime brokerage for institutions
• Clearing and settlement services
• $3T+ in annual clearing volume
• Direct access to hedge funds, banks, asset managers

**Metaco ($250M) — May 2023**
• Institutional custody technology
• Used by Citi, BNP Paribas, Société Générale
• Tokenization infrastructure
• DTCC-compatible systems

**Standard Custody ($250M est.) — 2024**
• Regulated custody solutions
• SOC 2 certified
• Insurance coverage
• Compliant infrastructure

**Tranglo (40% stake) — 2021**
• Southeast Asia payments
• 130+ countries coverage
• Mobile money integration
• Last-mile delivery

**The Strategy:**

Ripple is building a full-stack institutional infrastructure:

**Layer 1: Custody** (Metaco, Standard Custody)
Secure storage for institutional XRP holdings

**Layer 2: Trading** (Hidden Road)
Prime brokerage, clearing, lending

**Layer 3: Settlement** (ODL, RLUSD)
Real-time cross-border payments

**Layer 4: Distribution** (ETFs, RippleNet)
Access channels for institutions

**Why This Matters for XRP:**

Each acquisition creates XRP demand:

• **Hidden Road** clients need XRP for settlement
• **Custody** solutions hold XRP for institutions
• **ODL** uses XRP as bridge currency
• **RLUSD** transactions burn XRP fees

**The Numbers:**

Current institutional XRP adoption:
• 300+ financial institutions on RippleNet
• 40% using XRP for ODL
• $15B+ ODL volume in 2024
• $2B+ XRP ETF AUM

Projected growth:
• 500+ institutions by 2026
• 60%+ ODL adoption
• $50B+ ODL volume
• $10B+ ETF AUM

**Competitive Moat:**

Ripple's infrastructure creates switching costs:

1. **Integration Depth:** Banks spend months integrating
2. **Regulatory Relationships:** Years of compliance work
3. **Network Effects:** More partners = more value
4. **Liquidity:** XRP has deepest crypto liquidity for payments

**Key Risks:**

• Execution risk on integration
• Competition from SWIFT, JPM Coin
• Regulatory changes
• Crypto market volatility

**The Bear Case:**

• Ripple could succeed without XRP price appreciation
• ODL doesn't require high XRP prices
• Institutional adoption is slower than retail expects
• Competition is fierce

**The Bull Case:**

• ETF inflows lock supply
• Institutional custody removes XRP from circulation
• ODL volume requires liquidity (higher prices help)
• DTCC tokenization could be massive catalyst

**Investment Framework:**

Think of Ripple's acquisitions as infrastructure for a future where:
• Securities settle in real-time
• Cross-border payments are instant
• Banks hold digital assets
• XRP is the bridge

If that future materializes, current XRP prices look cheap.

**What to Watch:**

• Hidden Road integration timeline
• DTCC partnership announcements
• ODL volume growth
• ETF flow trends
• RLUSD adoption metrics

This is a 3-5 year thesis, not a trade.`
  },
];

// Wealth Strategy Articles
const wealthStrategyArticles = [
  { 
    title: 'Buy, Borrow, Die: The Billionaire Playbook', 
    description: 'How the ultra-wealthy avoid taxes by never selling appreciated assets. Instead, they borrow against them and pass to heirs with stepped-up basis.',
    category: 'Tax Strategy', 
    readTime: '12 min', 
    icon: '🏦',
    content: `**The Core Strategy**

The ultra-wealthy rarely sell their assets. Instead, they follow a three-step playbook that legally minimizes taxes across generations:

**Step 1: BUY**
Acquire appreciating assets (stocks, real estate, crypto) and hold them. As long as you don't sell, you pay zero capital gains tax — no matter how much the asset appreciates.

**Step 2: BORROW**
Need cash? Don't sell. Borrow against your assets instead. Loans are NOT taxable income. You can get loans at 2-5% interest against a portfolio earning 8-15% annually. Common options include margin loans, securities-backed lines of credit (SBLOCs), and crypto collateral loans.

**Step 3: DIE**
When you pass away, your heirs receive your assets at their current market value (stepped-up basis). All unrealized gains are permanently erased. Your heirs can then sell immediately with zero capital gains, or continue the cycle.

**Example with XRP:**
• You bought 50,000 XRP at $0.50 ($25,000 cost basis)
• XRP reaches $200 (portfolio worth $10M)
• Selling triggers $9.975M in capital gains = ~$2M+ in taxes
• Instead: Borrow $500K against your $10M portfolio at 4%
• You pay $20K/year interest instead of $2M+ in taxes
• Your heirs inherit at $200/XRP basis — gains erased

**Why This Works:**
This is 100% legal. The tax code favors unrealized gains, loans aren't income, and stepped-up basis is law (IRC Section 1014). The wealthy have used this for decades.

**Key Risks:**
• Asset depreciation can trigger margin calls
• Interest costs add up over time
• Tax laws could change
• Requires discipline not to sell

**Action Items:**
1. Adopt a "never sell" mindset for core holdings
2. Build relationships with wealth managers who offer SBLOCs
3. Research crypto lending platforms for XRP collateral
4. Work with an estate planning attorney on trust structures`
  },
  { 
    title: 'Living on Dividends Forever', 
    description: 'How to convert crypto gains into a permanent income stream using high-yield dividend stocks like SCHD, JEPI, and O without touching principal.',
    category: 'Income', 
    readTime: '15 min', 
    icon: '📈',
    content: `**The Goal: Never Touch Principal**

The ultimate financial freedom isn't having millions — it's having income that flows forever without depleting your wealth. Dividend investing lets your money work for you permanently.

**The Math:**
• $2M in dividend stocks at 5% yield = $100,000/year
• $5M at 5% = $250,000/year
• $10M at 5% = $500,000/year
• Your principal remains intact (and often grows)

**Top Dividend Vehicles:**

**SCHD (Schwab US Dividend Equity ETF)**
• Yield: ~3.5%
• Focus: Quality dividend growth companies
• Why: Consistent dividend raises, low 0.06% fee
• Best for: Long-term growth + income

**JEPI (JPMorgan Equity Premium Income)**
• Yield: ~8-10%
• Focus: Covered call strategy on S&P 500
• Why: High monthly income, lower volatility
• Best for: Immediate high income needs

**O (Realty Income Corporation)**
• Yield: ~5-6%
• Focus: Triple-net lease retail properties
• Why: Monthly dividends, 25+ years of raises
• Best for: Reliable monthly income

**QYLD (Global X NASDAQ Covered Call)**
• Yield: ~11-12%
• Focus: Covered calls on NASDAQ 100
• Why: Maximum income generation
• Caution: Limited upside, potential principal decay

**XRP to Dividends Strategy:**

Phase 1: Hold XRP until target price
Phase 2: Sell 40-60% of position at peak
Phase 3: Deploy into dividend portfolio:
  • 40% SCHD (growth + income)
  • 30% JEPI (high income)
  • 20% O (monthly reliability)
  • 10% individual dividend aristocrats

**Tax Optimization:**
• Qualified dividends taxed at 0-20% (vs 37% ordinary income)
• Hold dividend stocks in taxable accounts for lower rates
• Use tax-advantaged accounts for bonds/REITs

**The Power of Never Selling:**
Once your dividend income covers expenses, you never touch principal. Your wealth compounds forever and passes to heirs intact.

**Sample Portfolio at $5M:**
• $2M SCHD → $70K/year
• $1.5M JEPI → $135K/year  
• $1M O → $55K/year
• $500K QYLD → $55K/year
• **Total: $315,000/year forever**`
  },
  { 
    title: 'The Step-Up Basis Loophole', 
    description: 'When you die, your heirs inherit assets at current market value — wiping out all unrealized capital gains. How to use this legally.',
    category: 'Estate Planning', 
    readTime: '10 min', 
    icon: '👨‍👩‍👧‍👦',
    content: `**What Is Stepped-Up Basis?**

When you die, your heirs inherit your assets at their current fair market value (FMV) — not what you originally paid. All unrealized capital gains are permanently erased.

**IRC Section 1014** makes this law.

**Example:**
• You bought XRP at $0.50/token
• At death, XRP is worth $300/token
• Your $0.50 cost basis is "stepped up" to $300
• Heirs can sell immediately at $300 with ZERO capital gains tax
• The gains from $0.50 → $300 are never taxed by anyone

**Why This Matters for XRP Holders:**

If XRP reaches $300-500:
• 50,000 XRP bought at $0.50 = $25K cost basis
• At $300 = $15M value, $14.975M in gains
• Selling triggers ~$3M+ in federal taxes
• Dying instead: Heirs get $15M with $15M basis
• Tax savings: $3,000,000+

**Strategies to Maximize Step-Up:**

**1. Hold Your Winners**
Never sell highly appreciated assets. Let them step up at death.

**2. Sell Your Losers**
Harvest capital losses while alive for tax deductions.

**3. Gift Strategically**
Gifted assets do NOT get stepped-up basis (carry-over basis instead). Only bequeathed assets qualify.

**4. Use Trusts Wisely**
• Revocable trusts: Assets get step-up ✓
• Irrevocable trusts: Usually no step-up ✗
• Consult estate attorney for complex situations

**5. Spousal Planning**
In community property states, surviving spouse gets full step-up on jointly held assets. In common law states, only deceased's half steps up.

**Community Property States:**
Arizona, California, Idaho, Louisiana, Nevada, New Mexico, Texas, Washington, Wisconsin

**Important Limitations:**
• Step-up only applies at death
• Gifting does NOT trigger step-up
• Some assets (IRAs, 401ks) don't get step-up
• Congress periodically debates eliminating this

**Action Items:**
1. Identify your most appreciated assets
2. Plan to hold them until death if possible
3. Use borrowing for liquidity instead of selling
4. Work with estate attorney on trust structures
5. Keep detailed records of cost basis for heirs`
  },
  { 
    title: 'Crypto Collateral Loans Explained', 
    description: 'How to borrow against your XRP holdings without selling. Platforms, LTV ratios, interest rates, and liquidation risks.',
    category: 'DeFi', 
    readTime: '14 min', 
    icon: '🔐',
    content: `**What Are Crypto Collateral Loans?**

Crypto collateral loans let you borrow fiat or stablecoins by pledging your crypto as collateral — without selling. You keep ownership and upside potential while accessing liquidity.

**How It Works:**
1. Deposit XRP (or other crypto) as collateral
2. Borrow 25-50% of its value (varies by platform)
3. Pay interest on the loan
4. Repay loan to retrieve collateral
5. If collateral value drops too much → liquidation

**Key Terms:**

**LTV (Loan-to-Value):**
How much you can borrow vs collateral value
• 25% LTV: Borrow $25K against $100K crypto (safest)
• 50% LTV: Borrow $50K against $100K crypto (riskier)

**Liquidation Threshold:**
The LTV at which your collateral is sold
• Typically 65-85% depending on platform
• Example: 50% LTV loan with 80% liquidation threshold
• If crypto drops 38%, you get liquidated

**Top Platforms for XRP Collateral:**

**Nexo**
• LTV: Up to 50%
• Rates: 6.9-13.9% APR
• Min loan: $50
• Pros: Instant approval, no credit check
• Cons: Interest rates vary by tier

**Celsius (Post-Restructure)**
• Status: Check current availability
• Was popular for low rates

**AAVE (DeFi)**
• XRP not directly supported
• Would need wrapped XRP on Ethereum

**Binance Loans**
• LTV: Up to 65%
• Rates: Variable
• Pros: Large platform, competitive rates
• Cons: Regulatory uncertainty in some regions

**Risk Management:**

**Liquidation Protection:**
• Start with 25-30% LTV, not maximum
• Keep extra collateral ready to add
• Set price alerts for margin call levels
• Avoid over-borrowing in volatile markets

**Example Safe Loan:**
• 100,000 XRP at $10 = $1,000,000 collateral
• Borrow at 30% LTV = $300,000 loan
• Liquidation at 75% LTV
• XRP would need to drop to $4.00 (-60%)
• Safe buffer for volatility

**Tax Advantages:**
• Loans are NOT taxable income
• Interest may be deductible (consult CPA)
• You avoid capital gains entirely
• Maintain full ownership and upside

**When NOT to Use:**
• In extreme bear markets
• If you can't handle volatility
• If you need 100% of collateral value
• Without emergency funds to add collateral

**Action Steps:**
1. Research platforms available in your region
2. Start small to learn the process
3. Never borrow more than 30-35% LTV
4. Set price alerts below liquidation threshold
5. Keep stablecoins ready to add collateral`
  },
  { 
    title: 'IUL vs Whole Life: The Great Debate', 
    description: 'Understanding both sides of the insurance debate. IUL offers growth potential, Whole Life offers guarantees. Which fits your strategy?',
    category: 'Insurance', 
    readTime: '18 min', 
    icon: '🛡️',
    content: `**The Big Debate in Insurance**

There's an ongoing debate in the financial community: IUL (Indexed Universal Life) vs Whole Life for wealth building. Both have passionate advocates. Here's an honest look at both sides.

**IUL (Indexed Universal Life)**

Cash value growth tied to stock market index performance (S&P 500, etc.) with floors and caps.

**IUL Advantages:**
✓ Higher growth potential (historically 6-10% vs 4-5%)
✓ Flexible premiums — pay more or less as needed
✓ 0% floor protects against market losses
✓ Tax-free policy loans
✓ No contribution limits
✓ Can access cash value in years 1-3 with proper funding

**IUL Disadvantages:**
✗ No guarantees — costs can rise over time
✗ Caps limit upside (typically 8-12%)
✗ Policies can lapse if underfunded
✗ More complex, requires monitoring
✗ Insurance costs increase with age
✗ Illustrations may not match reality

**Whole Life Insurance**

Guaranteed cash value growth with dividends from mutual insurance companies.

**Whole Life Advantages:**
✓ Guaranteed cash value growth
✓ Guaranteed death benefit
✓ Guaranteed level premiums
✓ Dividends from mutual companies (4-6%)
✓ Time-tested (100+ years of history)
✓ True "set it and forget it"

**Whole Life Disadvantages:**
✗ Lower growth potential
✗ Higher premiums required
✗ Less flexibility
✗ Takes longer to build significant cash value

**The Key Insight: Funding & Design Matter Most**

With EITHER product, a properly structured, maximum-funded policy can provide cash access in years 1-3, NOT 7-10 years.

**The difference:**
• Minimally funded policy → 7-10 years to access meaningful cash
• Properly structured, overfunded policy → Access in year 1-3

**What "Properly Structured" Means:**
• Minimize base premium, maximize Paid-Up Additions (PUA)
• Use term riders to reduce base insurance costs
• Stay below MEC limits (7-pay test)
• Work with a specialist, not a typical agent

**The Nelson Nash Perspective:**

Nelson Nash, creator of the Infinite Banking Concept, specifically advocated for whole life from mutual companies because of the guarantees. However, modern practitioners have adapted the concept using IUL for those willing to accept more risk for potentially higher returns.

**Who Should Consider IUL:**
✓ Comfortable with some uncertainty
✓ Want higher growth potential
✓ Plan to actively monitor policy
✓ Longer time horizon to ride out volatility

**Who Should Consider Whole Life:**
✓ Want certainty and guarantees
✓ Prefer "set and forget"
✓ Building generational wealth
✓ Following traditional Infinite Banking

**Top Companies:**

**For Whole Life (Mutual):**
• MassMutual
• Penn Mutual
• Guardian
• New York Life
• Northwestern Mutual

**For IUL:**
• Pacific Life
• National Life Group
• Allianz
• Securian

**Action Steps:**

1. Decide your priority: guarantees vs growth potential
2. Find a specialist (IBC practitioner or LASER Fund advisor)
3. Get illustrations from multiple carriers
4. Ensure policy is designed for cash value, not max death benefit
5. Understand that FUNDING is more important than product choice
6. Start early — time is your greatest asset`
  },
  { 
    title: 'The Three-Tier Exit Strategy', 
    description: 'A framework for XRP holders: Tier 1 (dividends), Tier 2 (sacred hold), Tier 3 (trading/yield). How to allocate based on your goals.',
    category: 'Strategy', 
    readTime: '16 min', 
    icon: '🎯',
    content: `**The Philosophy: Never Go to Zero**

The worst outcome isn't missing the top — it's panic selling at the bottom or selling everything before a major move. The Three-Tier Strategy ensures you always have exposure while taking life-changing profits.

**The Three Tiers:**

**TIER 1: FREEDOM FUND (40-50%)**
*Goal: Generate permanent income*

• Sell this portion at your target prices
• Convert to dividend-producing assets
• Creates income that replaces your job
• Once deployed, NEVER touch the principal

Target Assets:
• SCHD, JEPI, O for dividends
• Real estate for rental income
• I-Bonds, treasuries for safety

Example: 50,000 XRP at $200 = $10M
• Tier 1 (45%): $4.5M → dividends
• At 5% yield = $225K/year forever

**TIER 2: SACRED HOLD (30-40%)**
*Goal: Generational wealth / $500+ exposure*

• NEVER sell this portion
• Use for collateral loans only
• Benefits from potential $500-1000+ prices
• Passes to heirs with stepped-up basis
• This is your "what if I'm wrong" insurance

Example: 50,000 XRP
• Tier 2 (35%): 17,500 XRP held forever
• At $500 = $8.75M
• At $1000 = $17.5M
• Borrow against for lifestyle

**TIER 3: TRADING/YIELD (15-25%)**
*Goal: Active income + dry powder*

• Use for trading swings
• Stake for yield where safe
• Deploy in DeFi opportunities
• Rebalance during volatility
• Can be more aggressive

Example: 50,000 XRP
• Tier 3 (20%): 10,000 XRP
• Trade cycles: sell rallies, buy dips
• Or stake for 3-5% yields
• Or provide liquidity

**Allocation by Risk Tolerance:**

**Conservative:**
• Tier 1: 50%
• Tier 2: 40%
• Tier 3: 10%

**Moderate:**
• Tier 1: 45%
• Tier 2: 35%
• Tier 3: 20%

**Aggressive:**
• Tier 1: 35%
• Tier 2: 35%
• Tier 3: 30%

**Exit Price Targets:**

Not financial advice — example framework:
• $50-100: Consider 10-20% of Tier 1
• $100-200: Majority of Tier 1
• $200-350: Remaining Tier 1
• $350-500+: Only touch Tier 3
• Never touch Tier 2

**Psychological Benefits:**

1. **Removes Emotion:** Rules are pre-set
2. **Guarantees Exposure:** Always have XRP
3. **Takes Profits:** Life-changing money secured
4. **Avoids Regret:** Never all-in or all-out
5. **Simplifies Decisions:** Framework handles it

**Implementation Steps:**

1. Calculate your total XRP holdings
2. Decide your tier percentages
3. Determine your exit prices
4. Write it down (commit in writing)
5. Set price alerts at each level
6. Execute mechanically when hit
7. Review quarterly, adjust if needed

**Critical Rules:**

• Set tiers BEFORE big price moves
• Don't change tiers during FOMO/FUD
• Tier 2 is SACRED — never sell
• Rebalance Tier 3 actively
• Tier 1 proceeds go to dividends ASAP

**Your Situation:**

With 42,000 XRP:
• Tier 1 (45%): 18,900 XRP
• Tier 2 (35%): 14,700 XRP  
• Tier 3 (20%): 8,400 XRP

At $200: Tier 1 = $3.78M → $189K/year dividends
Tier 2 + 3 = 23,100 XRP still riding

**This is how you win either way.**`
  },
  { 
    title: 'Yield Farming: AMMs & Exchange Staking', 
    description: 'Earn 3-12% annual yield by providing liquidity to AMMs or staking on exchanges. How it works, risks, and best platforms.',
    category: 'DeFi Yield', 
    readTime: '14 min', 
    icon: '🌾',
    content: `**What Is Yield Farming?**

Yield farming lets you earn passive income on your crypto by:
1. **Staking** on exchanges (simplest)
2. **Providing liquidity** to AMMs (higher yields, more risk)
3. **Lending** on DeFi protocols

**Option 1: Exchange Staking (Easiest)**

Many centralized exchanges offer staking rewards:

**Typical XRP Staking Yields:**
• Binance: 1-3% APY (flexible)
• Kraken: 1-2% APY
• Crypto.com: 2-4% APY (locked)
• OKX: 1-3% APY

**Pros:**
✓ Simple — just click "stake"
✓ No smart contract risk
✓ Withdraw anytime (flexible options)
✓ No impermanent loss

**Cons:**
✗ Lower yields than DeFi
✗ Counterparty risk (exchange custody)
✗ KYC required

**Option 2: AMM Liquidity Providing**

Provide liquidity to Automated Market Makers (AMMs) and earn trading fees.

**How It Works:**
1. Deposit equal value of two tokens (e.g., XRP + USDC)
2. Your tokens are used for trades on the DEX
3. You earn a share of all trading fees
4. Remove liquidity anytime to get tokens back

**Popular AMMs:**
• **XRPL DEX** — Native XRP Ledger orderbook
• **Uniswap** (via wrapped XRP)
• **SushiSwap**
• **Curve** (stablecoin focused)

**Typical Yields:**
• Stablecoin pairs: 3-8% APY
• Volatile pairs: 10-30% APY (but higher IL risk)

**CRITICAL: Impermanent Loss**

When you provide liquidity, if one token's price changes significantly vs the other, you can end up with less value than just holding.

**Example:**
• Deposit $5,000 XRP + $5,000 USDC ($10,000 total)
• XRP doubles in price
• Your LP position: ~$7,070 XRP + ~$7,070 USDC = $14,140
• If you just held: $10,000 XRP + $5,000 USDC = $15,000
• **Impermanent loss: $860 (5.7%)**

**Mitigating IL:**
• Use stablecoin pairs (USDC/USDT) — no IL
• Provide liquidity in ranging markets
• Farm high APY to offset IL
• Use concentrated liquidity strategically

**Option 3: XRPL Native Yield**

The XRP Ledger has its own DEX and AMM:

**XRPL AMM Features:**
• Native to XRPL (no bridging)
• Lower fees than Ethereum
• Auction slots for arbitrageurs
• Continuous auction mechanism

**Yield Expectations:**
• Conservative (staking): 1-3% APY
• Moderate (stablecoin LP): 3-8% APY
• Aggressive (volatile LP): 10-30% APY

**Risk Ladder:**

**Lowest Risk:**
1. Exchange flexible staking
2. Exchange locked staking
3. Stablecoin LP on major AMMs

**Medium Risk:**
4. Blue-chip token pairs on AMMs
5. XRPL native AMM

**Highest Risk:**
6. New/small token pairs
7. Leveraged yield farming
8. Unaudited protocols

**Action Steps:**

1. **Start small** — Test with amount you can afford to lose
2. **Understand IL** — Use IL calculators before depositing
3. **Check audits** — Only use audited protocols
4. **Diversify** — Don't put all eggs in one pool
5. **Monitor** — Check positions regularly
6. **Calculate real yield** — Subtract IL from APY`
  },
  { 
    title: 'Stablecoin Lending: 6-12% Yields', 
    description: 'Sell crypto to stablecoins, then lend for consistent high yields. Platforms, rates, and risk comparison.',
    category: 'Fixed Income', 
    readTime: '12 min', 
    icon: '💵',
    content: `**The Strategy**

Instead of holding volatile crypto, convert to stablecoins (USDT, USDC, DAI) and lend them out for consistent 6-12% annual yields — far better than bank savings accounts (0.5%) or even CDs (4-5%).

**Why Lend Stablecoins?**

• **Predictable income** — Know exactly what you'll earn
• **No price volatility** — $1 stays ~$1
• **High demand** — Traders/institutions borrow to leverage
• **Compound growth** — Reinvest interest automatically

**Where to Lend:**

**Centralized Platforms (Easiest):**

**Nexo**
• USDC/USDT: 8-12% APY
• Tiers based on NEXO token holdings
• Daily interest payouts
• Regulated in multiple jurisdictions

**Crypto.com**
• USDC: 6-8% APY
• Flexible or locked terms
• Higher rates for CRO stakers

**Binance Earn**
• USDT/USDC: 5-10% APY
• Flexible and locked options
• Large platform, deep liquidity

**Decentralized Platforms (Higher Yields, More Risk):**

**Aave**
• USDC/DAI: 4-8% APY (variable)
• Battle-tested protocol
• Non-custodial
• Ethereum, Polygon, Arbitrum

**Compound**
• USDC: 3-7% APY
• Pioneer in DeFi lending
• Governance token rewards

**MakerDAO (DAI Savings Rate)**
• DAI: 5-8% APY
• Backed by protocol revenue
• Very stable historically

**Curve/Convex**
• Stablecoin LPs: 8-15% APY
• Includes trading fees + CRV rewards
• Some impermanent loss risk

**Risk Comparison:**

| Platform Type | Yield | Risk Level | Custody |
|--------------|-------|------------|---------|
| Big Bank Savings | 0.5% | Very Low | FDIC insured |
| CDs/T-Bills | 4-5% | Low | Insured |
| CEX Lending | 6-10% | Medium | Exchange holds |
| DeFi Lending | 4-12% | Medium-High | Smart contract |
| DeFi LP Farming | 10-20%+ | High | Smart contract |

**Risks to Understand:**

**Smart Contract Risk:**
DeFi protocols can have bugs. Use only audited, established platforms.

**Stablecoin Depegging:**
USDC, USDT can temporarily lose $1 peg. Diversify across stables.

**Platform Insolvency:**
CEX platforms can fail (Celsius, BlockFi). Don't overconcentrate.

**Regulatory Risk:**
Stablecoin regulations evolving. Some platforms may restrict access.

**Yield Compression:**
High yields attract capital, which lowers yields over time.

**Strategy: Ladder Your Lending**

Don't put everything in one place:

**Example $500K Stablecoin Portfolio:**
• $150K Nexo USDC (8%) = $12,000/year
• $150K Aave USDC (5%) = $7,500/year
• $100K Crypto.com locked (7%) = $7,000/year
• $100K DAI Savings Rate (6%) = $6,000/year
• **Total: $32,500/year (6.5% blended)**

**Tax Considerations:**

• Interest income is taxed as ordinary income
• Not capital gains rates
• Track all earnings for tax reporting
• Some platforms provide tax forms

**Stablecoin Selection:**

**USDC (Circle):**
✓ Most transparent reserves
✓ Regulated US company
✓ Monthly attestations
✓ Briefly depegged during SVB crisis

**USDT (Tether):**
✓ Highest liquidity
✓ Most trading pairs
✗ Less transparent reserves
✗ Regulatory concerns

**DAI (MakerDAO):**
✓ Decentralized/algorithmic
✓ Crypto-collateralized
✗ More complex mechanism
✗ Can depeg in extreme markets

**Action Steps:**

1. Decide your risk tolerance
2. Choose 2-3 platforms to diversify
3. Start with established platforms (Aave, Nexo)
4. Monitor rates — they change frequently
5. Reinvest interest for compounding
6. Keep records for taxes`
  },
  { 
    title: 'Infinite Banking: Be Your Own Bank', 
    description: 'Use properly structured life insurance as your personal banking system. Access cash in years 1-3 with the right design.',
    category: 'Insurance', 
    readTime: '16 min', 
    icon: '🏛️',
    content: `**What Is Infinite Banking?**

Infinite Banking (IBC) uses dividend-paying whole life insurance as a personal financing system. You build cash value, borrow against it tax-free, and recapture interest you'd normally pay to banks.

**The Truth About Timing**

❌ MYTH: "Takes 7-10 years to access cash"
✅ REALITY: Properly structured policies allow access in years 1-3

The difference is DESIGN and FUNDING:

**Minimally Funded Policy:**
• Most premium goes to insurance costs
• Cash value builds slowly
• May take 7-10 years to be useful

**Properly Structured, Overfunded Policy:**
• Minimized base premium
• Maximized Paid-Up Additions (PUA)
• Cash value builds rapidly
• Access 80-90% of premium in year 1

**How to Structure for Early Access:**

1. **Minimize base premium** — Only enough for policy to qualify
2. **Maximize PUA rider** — This is where your cash goes
3. **Add term rider** — Reduces base insurance cost
4. **Stay below MEC** — Don't trigger Modified Endowment Contract
5. **Work with IBC specialist** — Critical for proper design

**How Borrowing Works:**

• You borrow FROM the insurance company
• Your cash value is COLLATERAL
• Your cash value KEEPS GROWING while borrowed
• No credit check, no approval process
• Set your own repayment schedule
• Interest paid goes back to the insurance system

**Example: Year 1 Access**

**Properly Structured Policy:**
• Annual premium: $50,000
• Base premium: $5,000 (minimum)
• PUA contribution: $45,000
• Year 1 cash value: ~$42,000-$45,000
• Immediately borrowable: $38,000-$40,000

**vs Typical Policy:**
• Annual premium: $50,000
• Base premium: $50,000 (maximum)
• PUA: $0
• Year 1 cash value: ~$15,000-$20,000
• Takes years to build meaningful cash

**Why Whole Life for IBC?**

Nelson Nash, creator of Infinite Banking, specified whole life because:

✓ **Guaranteed cash value** — Grows every year, never decreases
✓ **Guaranteed death benefit** — Locked in from day one
✓ **Guaranteed premiums** — Never increase
✓ **Dividends** — Mutual companies share profits (4-6%)
✓ **Non-correlated asset** — Independent of stock market
✓ **100+ year track record** — Paid through wars, depressions, crises

**Top Mutual Insurance Companies:**

1. **MassMutual** — Strong dividends, great for IBC
2. **Penn Mutual** — Flexible design, overloan protection
3. **Guardian** — Excellent financial strength
4. **New York Life** — Largest, very stable
5. **Lafayette Life** — Highest early cash value

**Using IBC with Crypto Gains:**

**Scenario:**
• Sell $500K of XRP gains
• Pay ~$100K capital gains tax
• Net: $400K

**IBC Strategy:**
• Fund $80K/year for 5 years
• Year 1: ~$70K accessible
• Year 5: ~$380K+ cash value
• Borrow to buy rental property
• Rental income pays policy premium
• Cash value keeps growing
• Death benefit passes to heirs tax-free

**The Cycle:**
1. Build cash value
2. Borrow against it
3. Invest the borrowed funds
4. Pay yourself back
5. Repeat (your money works twice)

**Key Advantages:**

✓ Tax-free access via loans
✓ Uninterrupted compounding
✓ Asset protection (in most states)
✓ No contribution limits
✓ Guaranteed growth
✓ Legacy planning built in

**Finding the Right Policy:**

1. Search for "Infinite Banking Concept" or "IBC Practitioner"
2. Verify they structure for MAX cash value, not max death benefit
3. Get illustrations from 2-3 mutual companies
4. Ensure PUA rider is included and maximized
5. Understand the break-even (properly structured = year 1-3)
6. Ask about overloan protection rider

**Resources:**
• "Becoming Your Own Banker" by Nelson Nash
• Viking Financial Group
• Banking Truths
• The Money Advantage

**Bottom Line:**
Infinite Banking works, but ONLY with proper policy design. The "it takes 10 years" myth comes from poorly structured policies. Find an IBC specialist who understands cash value maximization.`
  },
];

// XRPFi DeFi How-To Guides
const defiGuides = [
  {
    title: 'How to Use Flare Network & FXRP',
    description: 'Step-by-step guide to bridging your XRP to Flare Network and minting FXRP to access DeFi opportunities.',
    category: 'DeFi Guide',
    readTime: '10 min',
    icon: '☀️',
    content: `**What is Flare Network?**

Flare is a Layer 1 blockchain designed to bring smart contract functionality to assets like XRP. Through the FAssets system, you can convert your XRP into FXRP — a 1:1 wrapped token that works in DeFi.

**Why Use Flare?**
• Access DeFi with your XRP (lending, staking, LP)
• FXRP is overcollateralized and non-custodial
• Growing ecosystem of XRPFi protocols
• Earn yield on previously idle XRP

**Step-by-Step: Bridging XRP to FXRP**

**Step 1: Set Up Wallets**
• XRP Wallet: Use Xaman (formerly XUMM) or Ledger
• EVM Wallet: MetaMask, Bifrost, or Ledger
• Add Flare Network to MetaMask:
  - Network: Flare
  - RPC: https://flare-api.flare.network/ext/C/rpc
  - Chain ID: 14
  - Symbol: FLR

**Step 2: Visit the FAssets Portal**
• Go to: portal.flare.network
• Connect both your XRP wallet and EVM wallet
• Select "Mint FXRP"

**Step 3: Choose an Agent**
• Agents are collateral providers who back your FXRP
• Compare agents by: collateral ratio, fees, availability
• Higher collateral ratio = more security

**Step 4: Deposit XRP**
• Enter amount of XRP to bridge
• Confirm transaction in your XRP wallet
• Wait for confirmation (~3-5 minutes)

**Step 5: Receive FXRP**
• FXRP appears in your Flare wallet
• It's now ready for DeFi!

**What Can You Do With FXRP?**
• Stake on Firelight → earn stXRP
• Deposit in earnXRP vault → 4-10% APY
• Provide liquidity on SparkDEX
• Lend on Kinetic Markets
• Use as collateral for loans

**Redeeming FXRP Back to XRP**
• Go to FAssets portal
• Select "Redeem FXRP"
• Choose an agent with available liquidity
• Confirm and receive XRP back

**Risks to Consider:**
• Smart contract risk (audited by OpenZeppelin)
• Bridge risk (FAssets is decentralized)
• Gas fees on Flare (paid in FLR)
• Agent availability for redemption

**Pro Tips:**
• Start with a small amount to test
• Keep some FLR for gas fees
• Monitor your positions regularly
• Join Flare Discord for support`
  },
  {
    title: 'How to Stake on Firelight Finance',
    description: 'Complete guide to liquid staking your FXRP on Firelight to earn stXRP and participate in DeFi insurance.',
    category: 'DeFi Guide',
    readTime: '8 min',
    icon: '🔥',
    content: `**What is Firelight?**

Firelight is a liquid staking protocol on Flare that lets you stake FXRP and receive stXRP — a liquid staking token you can use in DeFi while still earning staking rewards.

**How It Works:**
1. Deposit FXRP into Firelight
2. Receive stXRP 1:1 as your receipt
3. stXRP earns rewards over time
4. Use stXRP in other DeFi protocols
5. Redeem stXRP for FXRP + rewards anytime

**The DeFi Insurance Model**

Firelight's unique feature: your staked FXRP backs DeFi insurance. Protocols pay fees for coverage against hacks, and those fees are distributed to stakers.

Phase 1 (NOW): Stake and earn Firelight Points
Phase 2 (Q1 2026): Rewards begin when insurance goes live

**Step-by-Step: Staking on Firelight**

**Prerequisites:**
• FXRP in your Flare wallet (see Flare guide)
• Some FLR for gas fees
• MetaMask or compatible wallet

**Step 1: Visit Firelight**
• Go to: app.firelight.xyz
• Connect your wallet

**Step 2: Approve FXRP**
• Click "Stake"
• Approve FXRP spending (one-time)
• Confirm in wallet

**Step 3: Stake FXRP**
• Enter amount to stake
• Confirm transaction
• Receive stXRP in your wallet

**What to Do With stXRP:**
• Hold it — value increases as rewards accrue
• LP on SparkDEX stXRP/FXRP pool
• Use as collateral on Kinetic
• Provide to earnXRP vault

**Unstaking Process:**
• Go to Firelight app
• Select "Unstake"
• Enter stXRP amount
• Receive FXRP (may have waiting period)

**Current Status:**
• Phase 1 is LIVE
• Rewards start in Phase 2
• Earning Firelight Points now
• Points may convert to rewards/tokens

**Risks:**
• Protocol is new (launched Dec 2025)
• Smart contract risk (audited)
• Rewards not yet active
• Liquidity constraints on unstaking

**Why Stake Now?**
• Early adopter advantages
• Accumulate Firelight Points
• Position for Phase 2 rewards
• Learn the system with small amounts`
  },
  {
    title: 'How to Use earnXRP Yield Vault',
    description: 'Guide to depositing FXRP in the earnXRP vault for automated yield strategies returning 4-10% APY.',
    category: 'DeFi Guide',
    readTime: '8 min',
    icon: '💰',
    content: `**What is earnXRP?**

earnXRP is a yield vault that automatically deploys your FXRP across multiple DeFi strategies. You deposit, and the vault does the work — no manual strategy management needed.

**Who Built It:**
• Upshift Finance — vault infrastructure
• Clearstar — risk management
• Flare Network — XRPFi ecosystem

**How the Vault Works:**

Your FXRP is deployed across:
1. **Carry Trades** — Borrow stables, earn yield differential
2. **Liquidity Provision** — AMM LP positions
3. **Staking via Firelight** — stXRP rewards
4. **Stability Pool** — Earn from liquidations

All returns compound back into XRP.

**Expected Returns:**
• Small vaults ($1-10M): 7-10% APY
• Larger vaults ($50-100M): 3-4% APY
• Returns vary with market conditions
• No guaranteed yields

**Step-by-Step: Using earnXRP**

**Step 1: Get FXRP**
• Bridge XRP to Flare first (see Flare guide)
• Have FXRP in your wallet

**Step 2: Visit Upshift**
• Go to: app.upshift.finance
• Connect wallet
• Find the earnXRP vault

**Step 3: Deposit FXRP**
• Enter amount to deposit
• Approve FXRP (first time)
• Confirm deposit transaction
• Receive earnXRP tokens (vault shares)

**Step 4: Monitor & Compound**
• Returns auto-compound
• Check vault performance
• No action needed

**Step 5: Withdraw Anytime**
• Select "Withdraw"
• Enter earnXRP amount to redeem
• Receive FXRP + earned yield

**Key Features:**
✓ Non-custodial — you control withdrawal
✓ Auto-compounding — no manual claims
✓ Diversified — multiple strategies
✓ XRP-denominated — no stablecoin conversion

**Current Limits:**
• Initial cap: 5M FXRP
• No per-user limit
• Fees waived first 30 days
• Withdraw anytime

**Risks:**
• Smart contract risk across multiple protocols
• Strategy performance varies
• Impermanent loss on LP positions
• Protocol dependency (Kinetic, Firelight, etc.)

**Is It Worth It?**
• Good for passive XRP yield
• Better than exchange staking (1-3%)
• Requires trust in vault managers
• Start small, test withdrawal process`
  },
  {
    title: 'How to Use mXRP (Midas)',
    description: 'Guide to Midas tokenized XRP product offering 6-8% base yield through institutional strategies.',
    category: 'DeFi Guide',
    readTime: '7 min',
    icon: '🪙',
    content: `**What is mXRP?**

mXRP is a tokenized XRP product by Midas that lets you earn yield on your XRP through institutional-grade strategies. It's available cross-chain via Axelar.

**How It Works:**
1. Deposit XRP or USDC
2. Receive mXRP tokens
3. mXRP accrues yield over time
4. Redeem mXRP for XRP + yield

**Yield Sources:**
• Institutional lending strategies
• Market making
• Options strategies
• DeFi integrations

**Target Yields:**
• Base: 6-8% APY
• Additional DeFi yields possible
• Returns vary with market conditions

**Step-by-Step: Getting mXRP**

**Option 1: Direct on Midas**
• Visit: midas.app
• Complete KYC (required)
• Deposit XRP or USDC
• Receive mXRP

**Option 2: Via DeFi (No KYC)**
• Swap for mXRP on DEXs
• Available on multiple chains
• Check liquidity before large swaps

**Supported Chains:**
• Ethereum
• Arbitrum
• Optimism
• Base
• More via Axelar

**Current Stats:**
• ~$20M TVL
• Launched Sept 2025
• Partnership with Axelar & Hyperithm

**Using mXRP in DeFi:**
• LP on Uniswap/Curve
• Collateral on lending protocols
• Yield stacking strategies

**Redeeming mXRP:**
• Go to Midas platform
• Select redeem
• Receive XRP + accrued yield
• May have redemption queue

**Key Differences vs earnXRP:**
| Feature | mXRP | earnXRP |
|---------|------|---------|
| KYC | Required | Not required |
| Chains | Multi-chain | Flare only |
| Strategy | Institutional | On-chain DeFi |
| Transparency | Less | More |
| Min deposit | Varies | None |

**Risks:**
• Counterparty risk (Midas custody)
• Strategy opacity
• KYC requirements
• Redemption delays possible

**Best For:**
• Users comfortable with KYC
• Want multi-chain access
• Prefer institutional strategies
• Larger depositors`
  },
];

// Jake Claver Theories
const claverTheories = [
  {
    id: 1,
    title: 'Supply Shock Theory',
    emoji: '💥',
    summary: 'ETF inflows are rapidly draining OTC and dark pool XRP supply, setting up an imminent supply crunch.',
    details: 'XRP spot ETFs have absorbed ~800M XRP in their first week from an estimated 1-2B available in private liquidity venues. When OTC supply runs dry, ETFs must buy from exchanges, triggering visible price discovery. "You\'re going to see crazy price movement when there\'s no more available supply through OTC desks and dark pools."',
    confidence: 'High',
    timeframe: 'Q1-Q2 2026',
    defaultOccurred: false,
  },
  {
    id: 2,
    title: 'Crisis Catalyst Theory',
    emoji: '🌊',
    summary: 'A financial crisis could force institutions to adopt XRP before regulatory clarity is complete.',
    details: 'XRP is the only major digital asset with clear "not a security" status from U.S. courts. In a liquidity crisis scenario, institutions may turn to XRP as the only legally clear option for real-time settlement. "The only option they\'re going to have, if there\'s enough liquidity in the asset, is XRP to save the day."',
    confidence: 'Medium',
    timeframe: 'Pre-Clarity Act',
    defaultOccurred: false,
  },
  {
    id: 3,
    title: 'Liquidity Requirement Thesis',
    emoji: '🏦',
    summary: 'XRP must reach high prices to provide sufficient liquidity for trillion-dollar institutional settlements.',
    details: 'For XRP to settle trillions in institutional transfers, the price per token must be high enough to provide adequate liquidity. This is a utility-driven valuation model, not speculation. The network processes $434B+ in transactions daily, and SWIFT handles $155T annually — capturing even a fraction requires massive liquidity.',
    confidence: 'Medium',
    timeframe: '2026-2028',
    defaultOccurred: false,
  },
  {
    id: 4,
    title: 'ISO 20022 / SWIFT Integration',
    emoji: '🔗',
    summary: 'RippleNet\'s ISO 20022 compliance positions XRP as the bridge asset for modernized global payments.',
    details: 'Ripple joined the ISO 20022 Standards Body in 2020. RippleNet is built to send/receive ISO 20022-formatted messages, allowing seamless bank integration. While XRP itself isn\'t "ISO certified" (it\'s a messaging standard, not token certification), RippleNet\'s compliance means XRP can serve as the settlement layer for ISO 20022 workflows.',
    confidence: 'High',
    timeframe: 'Ongoing',
    defaultOccurred: true,
    defaultDate: 'Nov 2025',
  },
  {
    id: 5,
    title: 'NDA / Silent Adoption Theory',
    emoji: '🤫',
    summary: 'Ripple\'s 1,700+ NDAs signal massive institutional partnerships being built behind the scenes.',
    details: '"Ripple signing over 1,700 non-disclosure agreements probably isn\'t random. These likely cover talks with major players—governments, global banks, payment networks, universities, and Fortune 500 firms—all laying the groundwork to use XRP. The pieces for mass adoption have been falling into place behind the scenes."',
    confidence: 'Speculative',
    timeframe: 'Unknown',
    defaultOccurred: false,
  },
  {
    id: 6,
    title: 'Reverse Carry Trade Unwind',
    emoji: '🇯🇵',
    summary: 'Japan\'s carry trade unwind could trigger a global liquidity event benefiting XRP.',
    details: 'The Japanese yen carry trade unwind is pushing markets to levels not seen since the early 2000s. AI projections suggest a disorderly unwind accelerating in Q1 2026, peaking around March. This macro event could force central banks to seek real-time settlement solutions, potentially turning to XRP.',
    confidence: 'Speculative',
    timeframe: 'Q1 2026',
    defaultOccurred: false,
  },
  {
    id: 7,
    title: 'Domino Theory',
    emoji: '🎯',
    summary: 'Multiple catalysts will trigger cascading adoption: ETFs → regulatory clarity → institutional FOMO.',
    details: 'Key dominoes: XRP ETF launches ✓, stablecoin bill passage, Clarity Act, SWIFT ISO 20022 migration ✓, DTCC tokenization (April 2026). Each event builds on the last. "Real world events will play out, and XRP will become the backbone of markets in the future."',
    confidence: 'Medium',
    timeframe: '2025-2027',
    defaultOccurred: false,
  },
];

// XRP Rich List Data
// Rich list data with full addresses for live API fetching
// Fallback balances used if API fails (estimated from XRPScan Sept 2025)
const richListWallets = [
  // VERIFIED exchange addresses from XRPScan/CoinCarp rich list (Jan 2026)
  // These are fetched dynamically via Cloud Function - fallbackBalance used only if API fails
  { rank: 1, name: 'Binance', address: 'rs8ZPbYqgecRcDzQpJYAMhSxSi5htsjnza', type: 'exchange', fallbackBalance: 1705000000 },
  { rank: 2, name: 'Upbit', address: 'rDxJNbV23mu9xsWoQHoBqZQvc77YcbJXwb', type: 'exchange', fallbackBalance: 1272000000 },
  { rank: 3, name: 'Bitbank', address: 'rw7m3CtVHwGSdhFjV4MyJozmZJv3DYQnsA', type: 'exchange', fallbackBalance: 572000000 },
  { rank: 4, name: 'Upbit (2)', address: 'r4G689g4KePYLKkyyumM1iUppTP4nhZwVC', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 5, name: 'Upbit (3)', address: 'rJo4m69u9Wd1F8fN2RbgAsJEF6a4hW1nSi', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 6, name: 'Upbit (4)', address: 'rLgn612WAgRoZ285YmsQ4t7kb8Ui3csdoU', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 7, name: 'Upbit (5)', address: 'rs48xReB6gjKtTnTfii93iwUhjhTJsW78B', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 8, name: 'Upbit (6)', address: 'rJWbw1u3oDDRcYLFqiWFjhGWRKVcBAWdgp', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 9, name: 'Upbit (7)', address: 'rMNUAfSz2spLEbaBwPnGtxTzZCajJifnzH', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 10, name: 'Upbit (8)', address: 'r38a3PtqW3M7LRESgaR4dyHjg3AxAmiZCt', type: 'exchange', fallbackBalance: 500000000 },
  { rank: 11, name: 'Korbit', address: 'rsYFhEk4uFvwvvKJomHL7KhdF29r2sw9KD', type: 'exchange', fallbackBalance: 116000000 },
];

// Distribution tiers - what you need to be in each percentile
const distributionTiers = [
  { tier: 'Big Whale', label: '🐋 Big Whale', minBalance: 100000, emoji: '🐋', color: '#ff6b9d', percentile: 0.01 },
  { tier: 'Whale', label: '🐳 Whale', minBalance: 50000, emoji: '🐳', color: '#a78bfa', percentile: 0.1 },
  { tier: 'Shark', label: '🦈 Shark', minBalance: 10000, emoji: '🦈', color: '#00CFFF', percentile: 1 },
  { tier: 'Dolphin', label: '🐬 Dolphin', minBalance: 5000, emoji: '🐬', color: '#00ff88', percentile: 3 },
  { tier: 'Fish', label: '🐟 Fish', minBalance: 1000, emoji: '🐟', color: '#f59e0b', percentile: 10 },
  { tier: 'Octopus', label: '🐙 Octopus', minBalance: 500, emoji: '🐙', color: '#ec4899', percentile: 20 },
  { tier: 'Crab', label: '🦀 Crab', minBalance: 200, emoji: '🦀', color: '#ef4444', percentile: 35 },
  { tier: 'Shrimp', label: '🦐 Shrimp', minBalance: 100, emoji: '🦐', color: '#6b7a8a', percentile: 50 },
  { tier: 'Worm', label: '🪱 Worm', minBalance: 1, emoji: '🪱', color: '#4a5568', percentile: 100 },
];

// Overall stats
const whaleStats = {
  totalAccounts: 6650000,
  activeAccounts: 4840747,
  top10Percent: 18.56,
  top50Percent: 43.5,
  rippleEscrow: 37400000000, // ~37.4B (Jan 2026)
  circulatingSupply: 57560000000,
};

// Top 50 Wallets (from XRPScan/CoinCarp rich list - Jan 2026)
const top50Wallets = [
  { rank: 1, name: 'Ripple Escrow (9)', address: 'rB3WNZc45gxzW31zxfXdkx8HusAhoqscPn', balance: 5000000234, type: 'escrow' },
  { rank: 2, name: 'Ripple Escrow (10)', address: 'r9UUEXn3cx2seufBkDa8F86usfjWM6HiYp', balance: 5000000220, type: 'escrow' },
  { rank: 3, name: 'Ripple Escrow (11)', address: 'rDdXiA3M4mYTQ4cFpWkVXfc2UaAXCFWeCK', balance: 5000000216, type: 'escrow' },
  { rank: 4, name: 'Ripple Escrow (12)', address: 'rKDvgGUsNPZxsgmoemfrgXPS2Not4co2op', balance: 5000000201, type: 'escrow' },
  { rank: 5, name: 'Ripple Escrow (13)', address: 'rMhkqz3DeU7GUUJKGZofusbrTwZe6bDyb1', balance: 4500000206, type: 'escrow' },
  { rank: 6, name: 'Ripple Escrow (28)', address: 'r9NpyVfLfUG8hatuCCHKzosyDtKnBdsEN3', balance: 4500000204, type: 'escrow' },
  { rank: 7, name: 'Ripple Escrow (14)', address: 'rN8pqRwLYuuvY7pUHurybPC8P6rLqVsu6o', balance: 3100000200, type: 'escrow' },
  { rank: 8, name: 'Ripple Escrow (26)', address: 'rKwJaGmB5Hz24Qs2iyCaTdUuL1WsEXUWy5', balance: 3000000201, type: 'escrow' },
  { rank: 9, name: 'Ripple (14)', address: 'rPyCQm8E5j78PDbrfKF24fRC7qUAk1kDMZ', balance: 1838960918, type: 'ripple' },
  { rank: 10, name: 'Binance', address: 'rs8ZPbYqgecRcDzQpJYAMhSxSi5htsjnza', balance: 1705412412, type: 'exchange' },
  { rank: 11, name: 'Ripple (15)', address: 'rsXT3AQqhHDusFs3nQQuwcA1yXRLZJAXKw', balance: 1510568522, type: 'ripple' },
  { rank: 12, name: 'Ripple (27)', address: 'rMQ98K56yXJbDGv49ZSmW51sLn94Xe1mu1', balance: 1330027112, type: 'ripple' },
  { rank: 13, name: 'Upbit', address: 'rDxJNbV23mu9xsWoQHoBqZQvc77YcbJXwb', balance: 1272201955, type: 'exchange' },
  { rank: 14, name: 'Unknown Whale', address: 'rLD5k36bJkNk1HkYSSCJwM4jBXChHjRViQ', balance: 1238620936, type: 'whale' },
  { rank: 15, name: 'Chris Larsen', address: 'rKveEyR1SrkWbJX214xcfH43ZsoGMb3PEv', balance: 845920824, type: 'founder' },
  { rank: 16, name: 'Unknown Whale', address: 'rJ9Ey7HbscSECamgDRzvw5wrVbFUgaUDt7', balance: 713000024, type: 'whale' },
  { rank: 17, name: 'Bitbank', address: 'rw7m3CtVHwGSdhFjV4MyJozmZJv3DYQnsA', balance: 572310931, type: 'exchange' },
  { rank: 18, name: 'Unknown Whale', address: 'r99QSej32nAcjQAri65vE5ZXjw6xpUQ2Eh', balance: 552642523, type: 'whale' },
  { rank: 19, name: 'Unknown Whale', address: 'rBntsdo3fAS5sb3pqe7LvvxTS8qngFYAe1', balance: 504366089, type: 'whale' },
  { rank: 20, name: 'Unknown Whale', address: 'rw2hzLZgiQ9q62KCuaTWuFHWfiX7JWg3wY', balance: 500000258, type: 'whale' },
  { rank: 21, name: 'Jed McCaleb (Locked)', address: 'rGKHDyj4L6pc7DzRB6LWCR4YfZfzXj2Bdh', balance: 500000202, type: 'founder' },
  { rank: 22, name: 'Unknown Whale', address: 'rsjFB8mPWqiZgPUaVh8XYqdfa59PE2d5LG', balance: 500000201, type: 'whale' },
  { rank: 23, name: 'Unknown Whale', address: 'rDqGA2GfveHypDguQ1KXrJzYymFZmKxEsF', balance: 500000200, type: 'whale' },
  { rank: 24, name: 'Unknown Whale', address: 'rHGfmgv54kpc3QCZGRXEQKUhLPndbasbQr', balance: 500000200, type: 'whale' },
  { rank: 25, name: 'Unknown Whale', address: 'rp6aTJmW3nq1aKt3Jmuz4DPRxksT5PBjpH', balance: 500000200, type: 'whale' },
  { rank: 26, name: 'Unknown Whale', address: 'rfL1mn4VTCoHdhHhHMwqpShCFUaDBRk6Z5', balance: 500000112, type: 'whale' },
  { rank: 27, name: 'Unknown Whale', address: 'rwa7YXssGVAL9yPKw6QJtCen2UqZbRQqpM', balance: 500000110, type: 'whale' },
  { rank: 28, name: 'Unknown Whale', address: 'rNcAdhSLXBrJ3aZUq22HaNtNEPpB5fR8Ri', balance: 500000070, type: 'whale' },
  { rank: 29, name: 'Upbit (2)', address: 'r4G689g4KePYLKkyyumM1iUppTP4nhZwVC', balance: 500000023, type: 'exchange' },
  { rank: 30, name: 'Upbit (3)', address: 'rJo4m69u9Wd1F8fN2RbgAsJEF6a4hW1nSi', balance: 500000022, type: 'exchange' },
  { rank: 31, name: 'Upbit (4)', address: 'rLgn612WAgRoZ285YmsQ4t7kb8Ui3csdoU', balance: 500000022, type: 'exchange' },
  { rank: 32, name: 'Upbit (5)', address: 'rs48xReB6gjKtTnTfii93iwUhjhTJsW78B', balance: 500000022, type: 'exchange' },
  { rank: 33, name: 'Upbit (6)', address: 'rJWbw1u3oDDRcYLFqiWFjhGWRKVcBAWdgp', balance: 500000022, type: 'exchange' },
  { rank: 34, name: 'Upbit (7)', address: 'rMNUAfSz2spLEbaBwPnGtxTzZCajJifnzH', balance: 500000022, type: 'exchange' },
  { rank: 35, name: 'Upbit (8)', address: 'r38a3PtqW3M7LRESgaR4dyHjg3AxAmiZCt', balance: 500000022, type: 'exchange' },
  { rank: 36, name: 'Unknown Whale', address: 'rDfrrrBJZshSQDvfT2kmL9oUBdish52unH', balance: 500000001, type: 'whale' },
  { rank: 37, name: 'Unknown Whale', address: 'rD6tdgGHG7hwGTA6P39aE7W89fbqxXRjzk', balance: 500000001, type: 'whale' },
  { rank: 38, name: 'Unknown Whale', address: 'r476293LUcDqtjiSGJ5Dh44J1xBCDWeX3', balance: 500000000, type: 'whale' },
  { rank: 39, name: 'Unknown Whale', address: 'rEvwSpejhGTbdAXbxRTpGAzPBQkBRZxN5s', balance: 479949328, type: 'whale' },
  { rank: 40, name: 'Bitstamp', address: 'rDsbeomae4FXwgQTJp9Rs64Qg9vDiTCdBv', balance: 450000000, type: 'exchange' },
  { rank: 41, name: 'Kraken', address: 'rLHzPsX6oXkzU2qL12kHCH8G8cnZv1rBJh', balance: 387499225, type: 'exchange' },
  { rank: 42, name: 'Unknown Whale', address: 'rpPcmcGQ5iTXDc5zF5owxwTifkTs1qYrA6', balance: 384812266, type: 'whale' },
  { rank: 43, name: 'Crypto.com', address: 'rJb5KsHsDHF1YS5B5DU6QCkH5NsPaKQTcy', balance: 331950784, type: 'exchange' },
  { rank: 44, name: 'Unknown Whale', address: 'rJpj1Mv21gJzsbsVnkp1U4nqchZbmZ9pM5', balance: 325800010, type: 'whale' },
  { rank: 45, name: 'Unknown Whale', address: 'rwshjBngGqMRJgGYvEJXGMkg5DS2GX3U3q', balance: 317105728, type: 'whale' },
  { rank: 46, name: 'Bitfinex', address: 'rLW9gnQo7BQhU6igk5keqYnH3TVrCxGRzm', balance: 300188448, type: 'exchange' },
  { rank: 47, name: 'Gate.io', address: 'rHWcuuZoFvDS6gNbmHSdpb7u1hZzxvCoMt', balance: 292958978, type: 'exchange' },
  { rank: 48, name: 'Unknown Whale', address: 'rhREXVHV938ToGkdJQ9NCYEY4x8kSEtjna', balance: 282499978, type: 'whale' },
  { rank: 49, name: 'Unknown Whale', address: 'rN1yT2hkfMt89CJVsXdvnKqRJbqm7TC8uo', balance: 279680000, type: 'whale' },
  { rank: 50, name: 'Unknown Whale', address: 'rKNwXQh9GMjaU8uTqKLECsqyib47g5dMvo', balance: 262330987, type: 'whale' },
];

const totalSupply = 57560; // Circulating supply in millions (57.56B)

// ─── HYPERLIQUID SUBCOMPONENTS ─────────────────────────────────────────────
function BotStatCard({ label, value, valueColor, sub }) {
  return (
    <div style={{
      padding: '18px 20px',
      background: '#080f0f',
    }}>
      <div style={{ fontSize: 13, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: valueColor || '#e2e8f0' }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: '#475569', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function BotSectionTitle({ children }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '16px 20px 10px',
      fontSize: 13,
      color: '#64748b',
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      fontWeight: 600,
    }}>
      <span style={{ flex: 1, height: 1, background: '#1e3a3a' }} />
      <span>{children}</span>
      <span style={{ flex: 1, height: 1, background: '#1e3a3a' }} />
    </div>
  );
}

function BotPanel({ bot, data, fills, loading, error }) {
  const state = data?.clearinghouseState ?? data;
  const equity = parseFloat(state?.marginSummary?.accountValue ?? 0);
  const positions = state?.assetPositions ?? [];
  
  // Calculate unrealized PNL from positions (more accurate than marginSummary)
  const unrealizedPnl = positions.reduce((sum, p) => {
    return sum + parseFloat(p.position?.unrealizedPnl ?? 0);
  }, 0);

  return (
    <div style={{
      background: '#080f0f',
      border: `2px solid ${bot.color}88`,
      borderRadius: 8,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '18px 22px',
        borderBottom: `2px solid ${bot.color}66`,
        background: '#050f0f',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ width: 10, height: 10, borderRadius: '50%', background: bot.color, display: 'inline-block' }} />
          <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: '0.04em', color: bot.color }}>{bot.label}</span>
        </div>
        <span style={{ fontSize: 13, color: '#475569', letterSpacing: '0.06em', fontFamily: 'monospace' }}>
          {bot.address.slice(0, 6)}…{bot.address.slice(-4)}
        </span>
      </div>

      {loading ? (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 12,
          padding: 48,
          color: '#334155',
          fontSize: 12,
        }}>
          <div style={{
            width: 24,
            height: 24,
            border: '2px solid #0f2a2a',
            borderTop: `2px solid ${bot.color}`,
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <span>Fetching live data…</span>
        </div>
      ) : error ? (
        <div style={{
          padding: '16px 18px',
          color: '#f87171',
          fontSize: 12,
          background: '#1a0a0a',
          borderRadius: 4,
          margin: 16,
        }}>⚠ {error}</div>
      ) : (
        <>
          {/* Stats row */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr 1fr',
            gap: 1,
            background: '#0a1a1a',
            borderBottom: '1px solid #0f2a2a',
          }}>
            <BotStatCard label="Total Equity" value={fmtUSD(equity)} valueColor="#e2e8f0" />
            <BotStatCard
              label="Unrealized PNL"
              value={fmtUSD(unrealizedPnl)}
              valueColor={unrealizedPnl >= 0 ? '#00e5c0' : '#f87171'}
            />
            <BotStatCard label="Open Positions" value={positions.length} valueColor={bot.color} />
          </div>

          {/* Positions */}
          <BotSectionTitle>Open Positions</BotSectionTitle>
          {positions.length === 0 ? (
            <div style={{ padding: '16px 22px', fontSize: 15, color: '#475569' }}>No open positions</div>
          ) : (
            <div style={{ overflowX: 'auto', padding: '0 0 8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr>
                    {['Coin', 'Side', 'Size', 'Entry', 'Mark', 'PNL'].map((h) => (
                      <th key={h} style={{
                        padding: '10px 18px',
                        textAlign: 'left',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid #1e3a3a',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {positions.map((p, i) => {
                    const pos = p.position;
                    const pnl = parseFloat(pos.unrealizedPnl ?? 0);
                    const side = parseFloat(pos.szi) > 0 ? 'LONG' : 'SHORT';
                    return (
                      <tr key={i} style={i % 2 === 0 ? { background: '#05100f' } : {}}>
                        <td style={{ padding: '12px 18px', color: bot.color, fontWeight: 600, fontSize: 16 }}>{pos.coin}</td>
                        <td style={{ padding: '12px 18px', color: side === 'LONG' ? '#00e5c0' : '#f87171', fontWeight: 600 }}>{side}</td>
                        <td style={{ padding: '12px 18px', color: '#94a3b8' }}>{Math.abs(parseFloat(pos.szi)).toFixed(4)}</td>
                        <td style={{ padding: '12px 18px', color: '#94a3b8' }}>{fmtUSD(pos.entryPx)}</td>
                        <td style={{ padding: '12px 18px', color: '#94a3b8' }}>—</td>
                        <td style={{ padding: '12px 18px', color: pnl >= 0 ? '#00e5c0' : '#f87171', fontWeight: 600 }}>{fmtUSD(pnl)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Recent Fills */}
          <BotSectionTitle>Recent Fills</BotSectionTitle>
          {(!fills || fills.length === 0) ? (
            <div style={{ padding: '16px 22px', fontSize: 15, color: '#475569' }}>No recent trades</div>
          ) : (
            <div style={{ overflowX: 'auto', padding: '0 0 8px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 15 }}>
                <thead>
                  <tr>
                    {['Time', 'Coin', 'Side', 'Size', 'Price', 'Fee'].map((h) => (
                      <th key={h} style={{
                        padding: '10px 18px',
                        textAlign: 'left',
                        color: '#64748b',
                        fontWeight: 600,
                        fontSize: 13,
                        letterSpacing: '0.06em',
                        textTransform: 'uppercase',
                        borderBottom: '1px solid #1e3a3a',
                      }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {fills.slice(0, 10).map((f, i) => (
                    <tr key={i} style={i % 2 === 0 ? { background: '#05100f' } : {}}>
                      <td style={{ padding: '12px 18px', color: '#94a3b8', fontSize: 14 }}>{fmtTime(f.time)}</td>
                      <td style={{ padding: '12px 18px', color: bot.color, fontWeight: 600, fontSize: 16 }}>{f.coin}</td>
                      <td style={{ padding: '12px 18px', color: f.side === 'B' ? '#00e5c0' : '#f87171', fontWeight: 600 }}>
                        {f.side === 'B' ? 'BUY' : 'SELL'}
                      </td>
                      <td style={{ padding: '12px 18px', color: '#94a3b8' }}>{parseFloat(f.sz).toFixed(4)}</td>
                      <td style={{ padding: '12px 18px', color: '#94a3b8' }}>{fmtUSD(f.px)}</td>
                      <td style={{ padding: '12px 18px', color: '#94a3b8' }}>{fmtUSD(f.fee)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function HLChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{
      background: '#0a1818',
      border: '1px solid #0f2a2a',
      borderRadius: 6,
      padding: '10px 14px',
      fontSize: 12,
    }}>
      <div style={{ color: '#475569', fontSize: 11, marginBottom: 4 }}>{label}</div>
      {payload.map((p) => (
        <div key={p.name} style={{ color: p.color, fontSize: 13, marginTop: 2 }}>
          {p.name}: <strong>{fmtUSD(p.value)}</strong>
        </div>
      ))}
    </div>
  );
}



export default function XRPETFTracker() {
  const [selectedView, setSelectedView] = useState('dashboard');
  const [modalView, setModalView] = useState(null); // 'privacy', 'terms', 'about'
  const [sortBy, setSortBy] = useState('aum');
  const [sortOrder, setSortOrder] = useState('desc');
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [pulseActive, setPulseActive] = useState(true);
  const [expandedETF, setExpandedETF] = useState(null);
  const [newsFilter, setNewsFilter] = useState('all');
  const [xAccounts, setXAccounts] = useState(defaultXAccounts);
  const [showAccountManager, setShowAccountManager] = useState(false);
  const [newAccountHandle, setNewAccountHandle] = useState('');
  
  const [xrpPrice, setXrpPrice] = useState(2.35);
  const [priceChange, setPriceChange] = useState(5.2);
  const [marketCap, setMarketCap] = useState(null);
  const [volume24h, setVolume24h] = useState(null);
  const [circulatingSupply, setCirculatingSupply] = useState(57560000000);
  const [escrowBalance, setEscrowBalance] = useState(37400000000); // ~37.4B XRP (Jan 2026)
  const [flareTVL, setFlareTVL] = useState(null);
  
  // Hyperliquid Bot Dashboard State
  const [hlBotData, setHlBotData] = useState({ bot1: null, bot2: null });
  const [hlBotFills, setHlBotFills] = useState({ bot1: [], bot2: [] });
  const [hlPortfolios, setHlPortfolios] = useState({ bot1: [], bot2: [] });
  const [hlLoading, setHlLoading] = useState({ bot1: true, bot2: true });
  const [hlErrors, setHlErrors] = useState({ bot1: null, bot2: null });
  const [hlLastUpdated, setHlLastUpdated] = useState(null);
  const [hlChartMerged, setHlChartMerged] = useState([]);
  const [hlMonthlyData, setHlMonthlyData] = useState([]);
  
  // Starting capital for accurate % returns (persisted)
  const [hlStartingCapital, setHlStartingCapital] = useState(() => {
    try {
      const saved = localStorage.getItem('xrpvault-hl-starting-capital');
      if (saved) return parseFloat(saved);
    } catch (e) {}
    return 1000; // Default $1000 per bot = $2000 total
  });
  
  // Save starting capital when changed
  useEffect(() => {
    try {
      localStorage.setItem('xrpvault-hl-starting-capital', hlStartingCapital.toString());
    } catch (e) {}
  }, [hlStartingCapital]);

  const fetchHLBot = useCallback(async (bot) => {
    try {
      const [state, fills, portfolio] = await Promise.all([
        fetchAccountState(bot.address),
        fetchFills(bot.address),
        fetchPortfolio(bot.address),
      ]);
      setHlBotData((prev) => ({ ...prev, [bot.id]: state }));
      setHlBotFills((prev) => ({ ...prev, [bot.id]: fills ?? [] }));
      setHlPortfolios((prev) => ({ ...prev, [bot.id]: portfolio ?? [] }));
      setHlErrors((prev) => ({ ...prev, [bot.id]: null }));
    } catch (e) {
      setHlErrors((prev) => ({ ...prev, [bot.id]: e.message }));
    } finally {
      setHlLoading((prev) => ({ ...prev, [bot.id]: false }));
    }
  }, []);

  const refreshHLBots = useCallback(() => {
    setHlLoading({ bot1: true, bot2: true });
    HYPERLIQUID_BOTS.forEach(fetchHLBot);
    setHlLastUpdated(new Date());
  }, [fetchHLBot]);

  // Auto-refresh Hyperliquid data when Trading tab is active
  useEffect(() => {
    if (selectedView === 'trading') {
      refreshHLBots();
      const interval = setInterval(refreshHLBots, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedView, refreshHLBots]);

  // Build merged chart data from fills (since portfolio API may not work)
  useEffect(() => {
    const fills1 = hlBotFills.bot1 || [];
    const fills2 = hlBotFills.bot2 || [];
    
    if (fills1.length === 0 && fills2.length === 0) return;

    // Compute cumulative PNL from fills by day
    const computeDailyPnl = (fills) => {
      if (!fills || fills.length === 0) return {};
      
      // Group fills by day and compute net PNL
      const dailyPnl = {};
      let cumulative = 0;
      
      // Sort fills oldest to newest
      const sorted = [...fills].sort((a, b) => a.time - b.time);
      
      sorted.forEach(fill => {
        const date = fmtDateShort(fill.time);
        // closedPnl represents realized PNL from closing positions
        const pnl = parseFloat(fill.closedPnl || 0);
        cumulative += pnl;
        dailyPnl[date] = cumulative;
      });
      
      return dailyPnl;
    };

    const d1 = computeDailyPnl(fills1);
    const d2 = computeDailyPnl(fills2);
    const allKeys = [...new Set([...Object.keys(d1), ...Object.keys(d2)])].sort((a, b) => {
      return new Date(a) - new Date(b);
    });

    // Build merged daily data with Combined
    const mergedData = allKeys.map((k) => {
      const v1 = d1[k] ?? 0;
      const v2 = d2[k] ?? 0;
      return {
        date: k,
        [HYPERLIQUID_BOTS[0].label]: d1[k] ?? null,
        [HYPERLIQUID_BOTS[1].label]: d2[k] ?? null,
        Combined: v1 + v2,
      };
    });
    setHlChartMerged(mergedData);

    // Calculate monthly returns from fills using configured starting capital
    const allFills = [
      ...fills1.map(f => ({ ...f, bot: 'bot1' })),
      ...fills2.map(f => ({ ...f, bot: 'bot2' }))
    ].sort((a, b) => a.time - b.time);

    // Group by month
    const monthlyPnl = {};
    allFills.forEach(fill => {
      const d = new Date(fill.time);
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (!monthlyPnl[monthKey]) monthlyPnl[monthKey] = 0;
      monthlyPnl[monthKey] += parseFloat(fill.closedPnl || 0);
    });

    // Use configured starting capital (total for both bots)
    const startingCapital = hlStartingCapital;

    // Calculate monthly % returns
    let cumulativePnl = 0;
    const monthlyArr = Object.entries(monthlyPnl)
      .sort(([a], [b]) => a.localeCompare(b))
      .filter(([month]) => {
        // Exclude January 2026 - no trading activity
        return month !== '2026-01';
      })
      .map(([month, gain]) => {
        const baseCapital = startingCapital + cumulativePnl;
        const pct = baseCapital > 0 ? (gain / baseCapital) * 100 : 0;
        cumulativePnl += gain;
        
        // Format month label
        const [year, mo] = month.split('-');
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        let label = `${monthNames[parseInt(mo) - 1]} '${year.slice(2)}`;
        
        // Add note for February (started on the 9th)
        const isPartialMonth = month === '2026-02';
        
        return {
          month: label,
          pct: parseFloat(pct.toFixed(1)),
          gain: parseFloat(gain.toFixed(2)),
          isPartial: isPartialMonth,
        };
      });

    setHlMonthlyData(monthlyArr);
  }, [hlBotFills, hlStartingCapital]);

  const hlTotalEquity =
    parseFloat(hlBotData.bot1?.marginSummary?.accountValue ?? 0) +
    parseFloat(hlBotData.bot2?.marginSummary?.accountValue ?? 0);
  
  // Theory tracking state - persisted to localStorage
  const [theoryStatus, setTheoryStatus] = useState(() => {
    try {
      const saved = localStorage.getItem('xrpvault-theory-status');
      if (saved) return JSON.parse(saved);
    } catch (e) {}
    // Default state from theory definitions
    const defaults = {};
    claverTheories.forEach(t => {
      defaults[t.id] = { 
        occurred: t.defaultOccurred || false, 
        date: t.defaultDate || '' 
      };
    });
    return defaults;
  });

  // Save theory status to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('xrpvault-theory-status', JSON.stringify(theoryStatus));
    } catch (e) {}
  }, [theoryStatus]);

  const toggleTheoryOccurred = (theoryId) => {
    setTheoryStatus(prev => ({
      ...prev,
      [theoryId]: {
        ...prev[theoryId],
        occurred: !prev[theoryId]?.occurred,
        date: !prev[theoryId]?.occurred ? new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : prev[theoryId]?.date
      }
    }));
  };

  const updateTheoryDate = (theoryId, date) => {
    setTheoryStatus(prev => ({
      ...prev,
      [theoryId]: {
        ...prev[theoryId],
        date: date
      }
    }));
  };
  
  // Never Sell Calculator State
  const [neverSellInputs, setNeverSellInputs] = useState({
    xrpHoldings: 1000,
    targetPrice: 350,
    percentToConvert: 50,
    dividendYield: 4.5,
    taxRate: 35,
    desiredIncome: 140000,
    loanToValue: 50,
    loanInterestRate: 8,
    // Portfolio Builder allocations (in XRP amounts)
    allocDividends: 0,
    allocStablecoin: 0,
    allocYield: 0,
    allocBorrow: 0,
    allocHold: 0,
    // Strategy-specific rates for portfolio
    lendingAPY: 8,
    stakingYield: 5,
    premiumYears: 10,
  });
  const [activeStrategy, setActiveStrategy] = useState('portfolio');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [dataSource, setDataSource] = useState('live');
  const [etfPrices, setEtfPrices] = useState({});
  const [whaleSearch, setWhaleSearch] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  const [userXrpHoldings, setUserXrpHoldings] = useState('');
  const [richListData, setRichListData] = useState(richListWallets.map(w => ({ ...w, balance: w.fallbackBalance, percentage: (w.fallbackBalance / 100000000000 * 100) })));
  const [richListLoading, setRichListLoading] = useState(true);
  
  // Detect mobile screen
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Fetch real ETF prices from Firebase Cloud Function (Polygon.io)
  const fetchETFPrices = async () => {
    const newPrices = {};
    
    try {
      const functionUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:5001/xrpvault-99404/us-central1/getETFQuotes'
        : 'https://us-central1-xrpvault-99404.cloudfunctions.net/getETFQuotes';
      
      const response = await fetch(functionUrl, { 
        method: 'GET',
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        
        if (data.quotes && Object.keys(data.quotes).length > 0) {
          for (const [ticker, quote] of Object.entries(data.quotes)) {
            newPrices[ticker] = {
              price: quote.price || 0,
              change: quote.change || 0,
              volume: quote.volume || 0,
              avgVolume: quote.avgVolume || 0,
              marketState: quote.marketState || 'CLOSED',
              lastUpdate: quote.lastUpdate ? new Date(quote.lastUpdate * 1000) : null,
              source: quote.source || data.source
            };
          }
          console.log(`ETF data from Polygon:`, Object.keys(newPrices).length, 'tickers');
        }
      }
    } catch (error) {
      console.log('Cloud Function error:', error.message);
    }
    
    if (Object.keys(newPrices).length > 0) {
      setEtfPrices(newPrices);
    }
  };

  // Fetch live balances for tracked exchange wallets from Cloud Function
  const fetchRichListBalances = async () => {
    setRichListLoading(true);
    
    try {
      const functionUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:5001/xrpvault-99404/us-central1/getExchangeBalances'
        : 'https://us-central1-xrpvault-99404.cloudfunctions.net/getExchangeBalances';
      
      const response = await fetch(functionUrl, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.wallets && data.wallets.length > 0) {
          const updatedList = data.wallets.map(wallet => ({
            name: wallet.name,
            address: wallet.address,
            balance: wallet.balance,
            percentage: (wallet.balance / 100000000000 * 100),
            live: true,
          }));
          // Sort by balance descending
          updatedList.sort((a, b) => b.balance - a.balance);
          setRichListData(updatedList);
        }
      }
    } catch (error) {
      console.log('Exchange balances fetch error, using defaults');
      // Keep existing data
    }
    
    setRichListLoading(false);
  };
  
  // Helper to format large XRP balances
  const formatXrpBalance = (balance) => {
    if (!balance || balance === 0) return '...';
    if (balance >= 1000000000) return `${(balance / 1000000000).toFixed(2)}B`;
    if (balance >= 1000000) return `${(balance / 1000000).toFixed(0)}M`;
    if (balance >= 1000) return `${(balance / 1000).toFixed(0)}K`;
    return balance.toFixed(0);
  };

  // Fetch Ripple escrow balance from known escrow wallets
  const fetchEscrowBalance = async () => {
    try {
      const functionUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:5001/xrpvault-99404/us-central1/getEscrowBalance'
        : 'https://us-central1-xrpvault-99404.cloudfunctions.net/getEscrowBalance';
      
      const response = await fetch(functionUrl, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.escrowBalance && data.escrowBalance > 0) {
          setEscrowBalance(data.escrowBalance);
          console.log('Escrow balance:', data.escrowBalance, 'source:', data.source);
        }
      }
    } catch (error) {
      console.log('Escrow fetch error, using default');
      // Keep default value
    }
  };

  // Fetch Flare Network TVL from DefiLlama
  const fetchFlareTVL = async () => {
    try {
      const response = await fetch('https://api.llama.fi/tvl/flare');
      if (response.ok) {
        const tvl = await response.json();
        if (tvl && typeof tvl === 'number') {
          setFlareTVL(tvl);
        }
      }
    } catch (error) {
      console.log('Could not fetch Flare TVL');
    }
  };

  // Calculate dynamic ETF data based on live XRP price and real ETF prices
  const etfData = baseETFData.map(etf => {
    // AUM = XRP holdings × XRP price (in millions)
    const aum = etf.xrpLocked * xrpPrice;
    // Use real ETF price if available, otherwise calculate estimate
    const realData = etfPrices[etf.ticker];
    const price = realData?.price || (etf.sharesOutstanding ? (etf.xrpLocked * xrpPrice) / etf.sharesOutstanding : null);
    const change = realData?.change ?? priceChange;
    // Share volume (from Polygon) - use today's or previous day's
    const shareVolume = realData?.volume || 0;
    const prevVolume = realData?.prevVolume || 0;
    // Dollar volume = shares × price (in millions)
    const dollarVolume = shareVolume && realData?.price ? (shareVolume * realData.price / 1000000) : null;
    // Previous day dollar volume for display when market closed
    const prevDollarVolume = prevVolume && realData?.price ? (prevVolume * realData.price / 1000000) : null;
    // Average volume for comparison
    const avgVolume = realData?.avgVolume || 0;
    // Market state (PRE, REGULAR, POST, CLOSED)
    const marketState = realData?.marketState || 'CLOSED';
    
    return {
      ...etf,
      price: price ? parseFloat(price.toFixed(2)) : null,
      change: parseFloat(change.toFixed(2)),
      aum: parseFloat(aum.toFixed(1)),
      volume: dollarVolume ? parseFloat(dollarVolume.toFixed(2)) : null,
      prevVolume: prevDollarVolume ? parseFloat(prevDollarVolume.toFixed(2)) : null,
      shareVolume: shareVolume,
      prevShareVolume: prevVolume,
      avgVolume: avgVolume,
      marketState: marketState,
      isRealPrice: !!realData,
    };
  });

  useEffect(() => {
    const fetchPrice = async () => {
      try {
        // Use coins endpoint for more complete data
        const response = await fetch(
          'https://api.coingecko.com/api/v3/coins/ripple?localization=false&tickers=false&community_data=false&developer_data=false'
        );
        if (response.ok) {
          const data = await response.json();
          if (data.market_data) {
            setXrpPrice(data.market_data.current_price?.usd || 2.35);
            setPriceChange(data.market_data.price_change_percentage_24h || 0);
            setMarketCap(data.market_data.market_cap?.usd || null);
            setVolume24h(data.market_data.total_volume?.usd || null);
            setCirculatingSupply(data.market_data.circulating_supply || 57560000000);
            setDataSource('live');
          }
        }
        setIsLoading(false);
      } catch (error) {
        // Fallback to simple endpoint
        try {
          const fallbackResponse = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=ripple&vs_currencies=usd&include_24hr_change=true&include_market_cap=true&include_24hr_vol=true'
          );
          if (fallbackResponse.ok) {
            const data = await fallbackResponse.json();
            if (data.ripple) {
              setXrpPrice(data.ripple.usd);
              setPriceChange(data.ripple.usd_24h_change || 0);
              setMarketCap(data.ripple.usd_market_cap || null);
              setVolume24h(data.ripple.usd_24h_vol || null);
              setDataSource('live');
            }
          }
        } catch (e) {
          console.log('Using fallback price');
          setDataSource('cached');
        }
        setIsLoading(false);
      }
    };
    
    fetchPrice();
    fetchETFPrices();
    fetchRichListBalances();
    fetchEscrowBalance();
    fetchFlareTVL();
    const interval = setInterval(() => {
      fetchPrice();
      fetchETFPrices();
    }, 60000); // Update every 60 seconds
    
    // Refresh exchange balances and escrow every 5 minutes
    const walletInterval = setInterval(() => {
      fetchRichListBalances();
      fetchEscrowBalance();
    }, 300000);
    
    // Refresh Flare TVL every 10 minutes
    const tvlInterval = setInterval(fetchFlareTVL, 600000);
    
    return () => {
      clearInterval(interval);
      clearInterval(walletInterval);
      clearInterval(tvlInterval);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setLastUpdate(new Date());
      setPulseActive(true);
      setTimeout(() => setPulseActive(false), 1000);
    }, 60000);
    return () => clearInterval(interval);
  }, []);

  const totalAUM = etfData.reduce((sum, etf) => sum + etf.aum, 0);
  const totalXRPLocked = etfData.reduce((sum, etf) => sum + etf.xrpLocked, 0);
  const percentOfSupply = ((totalXRPLocked / totalSupply) * 100).toFixed(4);
  const percentOfTotalSupply = ((totalXRPLocked / 100000) * 100).toFixed(4); // of 100B total
  const totalVolume = etfData.filter(e => !e.isIndex).reduce((sum, etf) => sum + (etf.volume || 0), 0);
  const totalShareVolume = etfData.filter(e => !e.isIndex).reduce((sum, etf) => sum + (etf.shareVolume || 0), 0);
  const totalPrevVolume = etfData.filter(e => !e.isIndex).reduce((sum, etf) => sum + (etf.prevVolume || 0), 0);
  const totalPrevShareVolume = etfData.filter(e => !e.isIndex).reduce((sum, etf) => sum + (etf.prevShareVolume || 0), 0);
  const marketOpen = etfData.some(e => e.marketState === 'REGULAR');
  const isPreMarket = etfData.some(e => e.marketState === 'PRE');
  const isPostMarket = etfData.some(e => e.marketState === 'POST');

  const sortedETFs = [...etfData].sort((a, b) => {
    const aVal = a[sortBy] || 0;
    const bVal = b[sortBy] || 0;
    return sortOrder === 'desc' ? bVal - aVal : aVal - bVal;
  });

  const spotETFs = etfData.filter(e => !e.isIndex);
  const lowestFeeETF = spotETFs.reduce((min, etf) => etf.fee < min.fee ? etf : min, spotETFs[0]);
  const highestVolumeETF = spotETFs.reduce((max, etf) => (etf.volume || 0) > (max.volume || 0) ? etf : max, spotETFs[0]);

  const filteredNews = newsFilter === 'all' 
    ? mockNewsItems 
    : mockNewsItems.filter(item => item.category === newsFilter);

  const addXAccount = () => {
    if (newAccountHandle && !xAccounts.find(a => a.handle.toLowerCase() === newAccountHandle.toLowerCase())) {
      setXAccounts([...xAccounts, { handle: newAccountHandle, displayName: newAccountHandle, verified: false, category: 'custom' }]);
      setNewAccountHandle('');
    }
  };

  const removeXAccount = (handle) => {
    setXAccounts(xAccounts.filter(a => a.handle !== handle));
  };

  return (
    <div style={styles.container}>
      <style>{`
        html, body { 
          background: #0a0f14; 
          margin: 0; 
          padding: 0;
        }
        ::-webkit-scrollbar { 
          width: 8px; 
        }
        ::-webkit-scrollbar-track { 
          background: #0a0f14; 
        }
        ::-webkit-scrollbar-thumb { 
          background: #434C54; 
          border-radius: 4px; 
        }
        ::-webkit-scrollbar-thumb:hover { 
          background: #00A5DF; 
        }
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @keyframes twinkle {
          0%, 100% { opacity: 0.2; }
          50% { opacity: 0.8; }
        }
        @keyframes rocketShake {
          0%, 100% { transform: translateY(-50%) rotate(-3deg); }
          50% { transform: translateY(-50%) rotate(3deg); }
        }
        @media (max-width: 768px) {
          .nav-container { 
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            padding-bottom: 5px;
          }
          .nav-container::-webkit-scrollbar { display: none; }
          .stat-grid { grid-template-columns: 1fr 1fr !important; }
          .etf-table { font-size: 12px !important; }
          .etf-table th, .etf-table td { padding: 8px 6px !important; }
          .theory-grid { grid-template-columns: 1fr !important; }
          .article-grid { grid-template-columns: 1fr !important; }
          .preset-buttons { flex-wrap: wrap !important; }
          .strategy-selector { flex-wrap: wrap !important; }
          .calc-grid { grid-template-columns: 1fr !important; }
          .tier-grid { grid-template-columns: repeat(2, 1fr) !important; }
          .exchange-grid { grid-template-columns: repeat(2, 1fr) !important; }
        }
        @media (max-width: 480px) {
          .stat-grid { grid-template-columns: 1fr !important; }
          .tier-grid { grid-template-columns: 1fr !important; }
          .exchange-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
      <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap" rel="stylesheet" />
      
      {/* Header */}
      <header style={styles.header}>
        <div style={styles.logo}>
          <RippleLogo size={44} />
          <span style={styles.logoText}>XRP<span style={styles.logoAccent}>VAULT</span></span>
        </div>
        <nav style={styles.nav} className="nav-container">
          {['dashboard', 'whales', 'trading', 'wealth', 'learn'].map(view => (
            <button
              key={view}
              onClick={() => setSelectedView(view)}
              style={{
                ...styles.navButton,
                ...(selectedView === view ? styles.navButtonActive : {})
              }}
            >
              {view === 'wealth' ? '💰 Wealth' : 
               view === 'trading' ? '🤖 Trading' :
               view.charAt(0).toUpperCase() + view.slice(1)}
            </button>
          ))}
        </nav>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <ShareButtons compact />
          <div style={styles.liveIndicator}>
            <span style={{...styles.liveDot, ...(pulseActive ? styles.liveDotPulse : {}), backgroundColor: dataSource === 'live' ? '#00ff88' : '#ffaa00'}} />
            <span style={styles.liveText}>{isLoading ? 'Loading...' : dataSource === 'live' ? 'Live' : 'Cached'}</span>
          </div>
        </div>
      </header>

      {/* Stats Row - Only on Dashboard */}
      {selectedView === 'dashboard' && (
        <section style={styles.statsRow} className="stat-grid">
          <StatCard label="XRP Price" value={`$${xrpPrice?.toFixed(4) || '2.3500'}`} subValue={`${priceChange >= 0 ? '+' : ''}${priceChange?.toFixed(2) || '0.00'}% (24h)`} positive={priceChange >= 0} isLive />
          <StatCard label="Market Cap" value={marketCap ? `$${(marketCap / 1e9).toFixed(1)}B` : '--'} subValue={volume24h ? `Vol: $${(volume24h / 1e9).toFixed(2)}B` : 'Loading...'} isLive={!!marketCap} />
          <StatCard label="Total AUM" value={`$${(totalAUM / 1000).toFixed(2)}B`} subValue={`${etfData.length} ETFs Tracking`} highlight />
          <StatCard label="XRP Locked" value={`${totalXRPLocked.toFixed(1)}M`} subValue="In ETF Vaults" />
          {/* Custom % of Supply card with both figures */}
          <div style={{...styles.statCard}}>
            <div style={styles.statIconWrap}><RippleIcon size={22} /></div>
            <div style={styles.statLabelRow}>
              <span style={styles.statLabel}>% of Supply</span>
            </div>
            <div style={styles.statValue}>{percentOfSupply}%</div>
            <div style={styles.statSubValue}>of {(circulatingSupply / 1e9).toFixed(1)}B Circ.</div>
            <div style={{fontSize: 11, color: '#6b7a8a', marginTop: 4}}>{percentOfTotalSupply}% of 100B Total</div>
          </div>
          {/* Enhanced ETF Volume Card */}
          <div style={{...styles.statCard}}>
            <div style={styles.statIconWrap}>📊</div>
            <div style={styles.statLabelRow}>
              <span style={styles.statLabel}>ETF Volume</span>
              <span style={{
                fontSize: 9,
                padding: '2px 6px',
                borderRadius: 4,
                background: marketOpen ? 'rgba(0, 255, 136, 0.2)' : 
                           isPreMarket ? 'rgba(245, 158, 11, 0.2)' :
                           isPostMarket ? 'rgba(245, 158, 11, 0.2)' :
                           'rgba(107, 122, 138, 0.2)',
                color: marketOpen ? '#00ff88' : 
                       isPreMarket || isPostMarket ? '#f59e0b' : 
                       '#6b7a8a',
                marginLeft: 6,
              }}>
                {marketOpen ? 'LIVE' : 
                 isPreMarket ? 'PRE' :
                 isPostMarket ? 'POST' : 'CLOSED'}
              </span>
            </div>
            <div style={styles.statValue}>
              {totalVolume > 0 ? `$${totalVolume.toFixed(2)}M` : 
               totalPrevVolume > 0 ? `$${totalPrevVolume.toFixed(2)}M` : '--'}
            </div>
            <div style={styles.statSubValue}>
              {totalShareVolume > 0 ? `${(totalShareVolume / 1000).toFixed(0)}K shares` :
               totalPrevShareVolume > 0 ? `${(totalPrevShareVolume / 1000).toFixed(0)}K shares (prev)` :
               isPreMarket ? 'Pre-Market' :
               isPostMarket ? 'After Hours' : 'Market Closed'}
            </div>
            <div style={{fontSize: 10, color: '#6b7a8a', marginTop: 4}}>
              {totalShareVolume > 0 ? 'Today' : totalPrevShareVolume > 0 ? 'Previous Close' : '24h Combined'}
            </div>
          </div>
        </section>
      )}

      {/* Dashboard */}
      {selectedView === 'dashboard' && (
        <>
          <section style={styles.controls}>
            <div style={styles.controlGroup}>
              <span style={styles.controlLabel}>Sort by</span>
              {['aum', 'xrpLocked', 'volume', 'fee'].map(field => (
                <button
                  key={field}
                  onClick={() => {
                    if (sortBy === field) setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                    else { setSortBy(field); setSortOrder('desc'); }
                  }}
                  style={{...styles.sortButton, ...(sortBy === field ? styles.sortButtonActive : {})}}
                >
                  {field === 'xrpLocked' ? 'XRP' : field.charAt(0).toUpperCase() + field.slice(1)}
                  {sortBy === field && <span>{sortOrder === 'desc' ? ' ↓' : ' ↑'}</span>}
                </button>
              ))}
            </div>
            <div style={styles.updateTime}>
              <span style={{color: dataSource === 'live' ? '#00ff88' : '#ffaa00', marginRight: 6}}>●</span>
              Updated {lastUpdate.toLocaleTimeString()} • Refreshes every 60s
            </div>
          </section>

          {/* ETF Leaderboard Graphic */}
          <section style={{
            background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
            borderRadius: 16,
            padding: 24,
            marginBottom: 20,
            border: '1px solid rgba(0, 207, 255, 0.2)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>
                🏆 ETF Leaderboard
              </h3>
              <span style={{ fontSize: 12, color: '#6b7a8a' }}>Ranked by AUM</span>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {sortedETFs.slice(0, 6).map((etf, index) => {
                const maxAUM = sortedETFs[0]?.aum || 1;
                const barWidth = (etf.aum / maxAUM) * 100;
                const medals = ['🥇', '🥈', '🥉', '4️⃣', '5️⃣', '6️⃣'];
                const barColors = [
                  'linear-gradient(90deg, #FFD700, #FFA500)',
                  'linear-gradient(90deg, #C0C0C0, #A0A0A0)',
                  'linear-gradient(90deg, #CD7F32, #8B4513)',
                  'linear-gradient(90deg, #00CFFF, #0090CC)',
                  'linear-gradient(90deg, #a78bfa, #8b5cf6)',
                  'linear-gradient(90deg, #00ff88, #00cc6a)',
                ];
                
                return (
                  <div key={etf.ticker} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <span style={{ fontSize: 20, width: 30, textAlign: 'center' }}>{medals[index]}</span>
                    <div style={{ width: 60, fontWeight: 700, color: '#fff', fontSize: 14 }}>{etf.ticker}</div>
                    <div style={{ flex: 1, position: 'relative', height: 28, background: 'rgba(0,0,0,0.3)', borderRadius: 6, overflow: 'hidden' }}>
                      <div style={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        height: '100%',
                        width: `${barWidth}%`,
                        background: barColors[index],
                        borderRadius: 6,
                        transition: 'width 0.5s ease',
                        display: 'flex',
                        alignItems: 'center',
                        paddingLeft: 10,
                      }}>
                        {barWidth > 30 && (
                          <span style={{ fontSize: 12, fontWeight: 700, color: index < 3 ? '#000' : '#fff' }}>
                            ${(etf.aum / 1000).toFixed(2)}B
                          </span>
                        )}
                      </div>
                      {barWidth <= 30 && (
                        <span style={{ position: 'absolute', left: `${barWidth + 2}%`, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
                          ${(etf.aum / 1000).toFixed(2)}B
                        </span>
                      )}
                    </div>
                    <div style={{ width: 70, textAlign: 'right', fontSize: 12, color: '#6b7a8a' }}>
                      {etf.xrpLocked}M XRP
                    </div>
                  </div>
                );
              })}
            </div>
            
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              marginTop: 16, 
              paddingTop: 16, 
              borderTop: '1px solid rgba(255,255,255,0.1)',
              flexWrap: 'wrap',
              gap: 12,
            }}>
              <div style={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#00CFFF' }}>${(totalAUM / 1000).toFixed(2)}B</div>
                <div style={{ fontSize: 11, color: '#6b7a8a' }}>Total AUM</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#00ff88' }}>{totalXRPLocked.toFixed(0)}M</div>
                <div style={{ fontSize: 11, color: '#6b7a8a' }}>XRP Locked</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#a78bfa' }}>{sortedETFs.length}</div>
                <div style={{ fontSize: 11, color: '#6b7a8a' }}>Active ETFs</div>
              </div>
              <div style={{ textAlign: 'center', flex: 1, minWidth: 100 }}>
                <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>{lowestFeeETF.fee}%</div>
                <div style={{ fontSize: 11, color: '#6b7a8a' }}>Lowest Fee</div>
              </div>
            </div>
          </section>

          <section style={styles.etfGrid}>
            {sortedETFs.map((etf, index) => (
              <ETFCard key={etf.ticker} etf={etf} rank={index + 1} isExpanded={expandedETF === etf.ticker} onToggle={() => setExpandedETF(expandedETF === etf.ticker ? null : etf.ticker)} totalXRP={totalXRPLocked} />
            ))}
            {/* Future ETF Placeholder */}
            <div style={styles.futureETFCard}>
              <div style={styles.futureETFIcon}>+</div>
              <div style={styles.futureETFTitle}>More Coming Soon</div>
              <div style={styles.futureETFText}>New XRP ETF applications are pending SEC approval. This space is reserved for future listings.</div>
              <div style={styles.futureETFPending}>
                <span style={styles.pendingDot} />
                <span>Pending Approvals</span>
              </div>
            </div>
          </section>

          <section style={styles.quickStats}>
            <div style={styles.quickStat}>
              <span style={styles.quickStatLabel}>Lowest Fee</span>
              <span style={styles.quickStatValue}>{lowestFeeETF.ticker} ({lowestFeeETF.fee}%)</span>
            </div>
            <div style={styles.quickStat}>
              <span style={styles.quickStatLabel}>Highest Volume</span>
              <span style={styles.quickStatValue}>{highestVolumeETF.ticker} (${highestVolumeETF.volume?.toFixed(2)}M)</span>
            </div>
            <div style={styles.quickStat}>
              <span style={styles.quickStatLabel}>Most XRP</span>
              <span style={styles.quickStatValue}>XRPC ({etfData[0].xrpLocked}M)</span>
            </div>
            <div style={styles.quickStat}>
              <span style={styles.quickStatLabel}>First ETF</span>
              <span style={styles.quickStatValue}>XRPR (Oct 29)</span>
            </div>
          </section>

          {/* Exchange Reserves Section */}
          <section style={styles.exchangeReservesSection}>
            <div style={styles.reservesHeader}>
              <div>
                <h3 style={styles.reservesTitle}>📊 Exchange Reserves</h3>
                <p style={styles.reservesSubtitle}>XRP held on centralized exchanges — lower reserves = tighter supply</p>
              </div>
              <a href="https://cryptoquant.com/asset/xrp/chart/exchange-flows/exchange-reserve" target="_blank" rel="noopener noreferrer" style={styles.reservesLink}>
                CryptoQuant ↗
              </a>
            </div>

            {/* Main Reserve Stats */}
            <div style={styles.reserveStatsGrid}>
              <div style={styles.reserveMainCard}>
                <span style={styles.reserveMainLabel}>Total Exchange Reserves</span>
                <span style={styles.reserveMainValue}>1.65B</span>
                <span style={styles.reserveMainSub}>XRP across all exchanges</span>
                <div style={styles.reserveChange}>
                  <span style={styles.reserveChangeNegative}>▼ -70M</span>
                  <span style={styles.reserveChangePeriod}>Last 30 Days</span>
                </div>
              </div>
              <div style={styles.reserveMainCard}>
                <span style={styles.reserveMainLabel}>8-Year Low</span>
                <span style={styles.reserveMainValue}>🔥</span>
                <span style={styles.reserveMainSub}>Lowest since 2018</span>
                <div style={styles.reserveChange}>
                  <span style={styles.reserveChangeNegative}>▼ 56%</span>
                  <span style={styles.reserveChangePeriod}>From Oct '24 Peak</span>
                </div>
              </div>
              <div style={styles.reserveMainCard}>
                <span style={styles.reserveMainLabel}>Supply Squeeze</span>
                <span style={styles.reserveMainValue}>ACTIVE</span>
                <span style={styles.reserveMainSub}>ETFs absorbing OTC supply</span>
                <div style={styles.reserveChange}>
                  <span style={styles.reserveChangePositive}>+850M</span>
                  <span style={styles.reserveChangePeriod}>XRP to ETFs (60d)</span>
                </div>
              </div>
            </div>

            {/* Individual Exchange Reserves */}
            <div style={styles.exchangeGrid} className="exchange-grid">
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>🟡</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Binance</span>
                  <span style={styles.exchangeReserve}>1.85B XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendDown}>▼ -30%</span>
                </div>
              </div>
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>🔵</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Upbit</span>
                  <span style={styles.exchangeReserve}>1.42B XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendDown}>▼ -40M</span>
                </div>
              </div>
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>🟠</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Bithumb</span>
                  <span style={styles.exchangeReserve}>1.77B XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendDown}>▼ -20M</span>
                </div>
              </div>
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>🟢</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Uphold</span>
                  <span style={styles.exchangeReserve}>1.65B XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendNeutral}>—</span>
                </div>
              </div>
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>⚪</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Kraken</span>
                  <span style={styles.exchangeReserve}>892M XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendDown}>▼ -12%</span>
                </div>
              </div>
              <div style={styles.exchangeCard}>
                <div style={styles.exchangeIcon}>🔴</div>
                <div style={styles.exchangeInfo}>
                  <span style={styles.exchangeName}>Bitstamp</span>
                  <span style={styles.exchangeReserve}>567M XRP</span>
                </div>
                <div style={styles.exchangeTrend}>
                  <span style={styles.trendDown}>▼ -8%</span>
                </div>
              </div>
            </div>

            {/* Reserve History Bar */}
            <div style={styles.reserveHistorySection}>
              <h4 style={styles.reserveHistoryTitle}>📉 Reserve Drawdown Timeline</h4>
              <div style={styles.reserveTimeline}>
                {(() => {
                  // Dynamic reserve data - add new months here as data becomes available
                  const reserveHistory = [
                    { date: new Date('2024-10-01'), value: 3.76, label: 'Oct 2024' },
                    { date: new Date('2024-12-01'), value: 3.0, label: 'Dec 2024' },
                    { date: new Date('2025-12-01'), value: 2.65, label: 'Dec 2025' },
                    { date: new Date('2026-01-01'), value: 1.85, label: 'Jan 2026' },
                    { date: new Date('2026-02-01'), value: 1.72, label: 'Feb 2026' },
                    { date: new Date('2026-03-01'), value: 1.65, label: 'Mar 2026' },
                  ];
                  
                  // Filter to only show months up to current month
                  const now = new Date();
                  const currentData = reserveHistory.filter(item => item.date <= now);
                  
                  // Get max value for scaling bars
                  const maxValue = Math.max(...currentData.map(d => d.value));
                  const minValue = Math.min(...currentData.map(d => d.value));
                  
                  return currentData.map((item, idx) => {
                    const isLatest = idx === currentData.length - 1;
                    const barWidth = (item.value / maxValue) * 100;
                    
                    return (
                      <div key={item.label} style={styles.timelineItem}>
                        <span style={styles.timelineDate}>{item.label}</span>
                        <div style={styles.timelineBar}>
                          <div style={{
                            ...styles.timelineBarFill, 
                            width: `${barWidth}%`,
                            ...(isLatest ? {background: 'linear-gradient(90deg, #00ff88, #00CFFF)'} : {})
                          }}></div>
                        </div>
                        <span style={{
                          ...styles.timelineValue, 
                          ...(isLatest ? {color: '#00ff88'} : {})
                        }}>
                          {item.value}B {isLatest && '🔥'}
                        </span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>

            <div style={styles.reservesFooter}>
              <span>💡 Lower exchange reserves = less sell pressure. When ETFs need XRP and exchanges run dry, price discovery happens on open markets.</span>
            </div>

            {/* ETF Approval Timeline */}
            <div style={styles.etfTimelineSection}>
              <h4 style={styles.reserveHistoryTitle}>📅 XRP ETF Approval Timeline</h4>
              <div style={styles.etfTimelineGrid}>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Oct 29, 2025</span>
                    <span style={styles.etfTimelineName}>REX-Osprey XRPR</span>
                  </div>
                </div>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Nov 13, 2025</span>
                    <span style={styles.etfTimelineName}>Canary XRPC</span>
                  </div>
                </div>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Nov 20, 2025</span>
                    <span style={styles.etfTimelineName}>Bitwise XRP</span>
                  </div>
                </div>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Nov 24, 2025</span>
                    <span style={styles.etfTimelineName}>Franklin XRPZ</span>
                  </div>
                </div>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Nov 24, 2025</span>
                    <span style={styles.etfTimelineName}>Grayscale GXRP</span>
                  </div>
                </div>
                <div style={styles.etfTimelineItem}>
                  <div style={styles.etfTimelineDot}></div>
                  <div style={styles.etfTimelineContent}>
                    <span style={styles.etfTimelineDate}>Dec 11, 2025</span>
                    <span style={styles.etfTimelineName}>21Shares TOXR</span>
                  </div>
                </div>
              </div>
            </div>

            {/* XRP Allocation Radar */}
            <div style={{
              marginTop: 20,
              padding: 24,
              background: 'linear-gradient(135deg, rgba(0, 207, 255, 0.08) 0%, rgba(138, 43, 226, 0.08) 100%)',
              borderRadius: 16,
              border: '1px solid rgba(0, 207, 255, 0.2)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                <div>
                  <h3 style={{ fontSize: 20, fontWeight: 700, color: '#fff', margin: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                    🗺️ XRP Supply Map
                  </h3>
                  <p style={{ fontSize: 13, color: '#6b7a8a', margin: '4px 0 0 0' }}>Track where all 100 Billion XRP is allocated</p>
                </div>
                <div style={{ fontSize: 12, color: '#00CFFF', background: 'rgba(0, 207, 255, 0.1)', padding: '6px 12px', borderRadius: 20 }}>
                  Total Supply: 100B XRP
                </div>
              </div>

              {/* Allocation Visual Bar */}
              <div style={{ marginBottom: 20 }}>
                <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', height: 32 }}>
                  <div style={{ width: `${(escrowBalance / 1e11 * 100).toFixed(0)}%`, background: 'linear-gradient(90deg, #a78bfa, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>Escrow {(escrowBalance / 1e9).toFixed(0)}B</span>
                  </div>
                  <div style={{ width: `${(richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e11 * 100).toFixed(0)}%`, background: 'linear-gradient(90deg, #00CFFF, #0090CC)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>CEX {(richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e9).toFixed(0)}B</span>
                  </div>
                  <div style={{ width: '8%', background: 'linear-gradient(90deg, #f59e0b, #d97706)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 10, color: '#fff', fontWeight: 600 }}>Team</span>
                  </div>
                  <div style={{ width: '1%', background: 'linear-gradient(90deg, #ec4899, #db2777)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  </div>
                  <div style={{ width: '0.8%', background: 'linear-gradient(90deg, #6366f1, #4f46e5)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  </div>
                  <div style={{ flex: 1, background: 'linear-gradient(90deg, #00ff88, #00cc6a)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span style={{ fontSize: 11, color: '#000', fontWeight: 600 }}>Public Circulation</span>
                  </div>
                </div>
              </div>

              {/* Allocation Grid */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 }}>
                {/* Escrow */}
                <div style={{ background: 'rgba(167, 139, 250, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>🔒</span>
                    <span style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600 }}>Ripple Escrow</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{(escrowBalance / 1e9).toFixed(1)}B</div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>{(escrowBalance / 1e11 * 100).toFixed(0)}% of supply</div>
                  <div style={{ fontSize: 11, color: '#00ff88', marginTop: 4 }}>🟢 Live from XRPScan</div>
                </div>

                {/* Exchanges */}
                <div style={{ background: 'rgba(0, 207, 255, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(0, 207, 255, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>🏦</span>
                    <span style={{ fontSize: 13, color: '#00CFFF', fontWeight: 600 }}>Exchanges</span>
                    {richListLoading && <span style={{ fontSize: 10, color: '#6b7a8a' }}>⏳</span>}
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>
                    {(() => {
                      const total = richListData.reduce((sum, w) => sum + (w.balance || 0), 0);
                      return total > 0 ? `${(total / 1000000000).toFixed(1)}B` : '~10B';
                    })()}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>Top 10 exchanges</div>
                  <div style={{ fontSize: 11, color: richListData.some(w => w.live) ? '#00ff88' : '#f59e0b', marginTop: 4 }}>
                    {richListData.some(w => w.live) ? '🟢 Live from XRPScan' : '⚠️ Using estimates'}
                  </div>
                </div>

                {/* Founders/Team */}
                <div style={{ background: 'rgba(245, 158, 11, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>👥</span>
                    <span style={{ fontSize: 13, color: '#f59e0b', fontWeight: 600 }}>Founders/Team</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>~8B</div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>8% of supply</div>
                  <div style={{ fontSize: 11, color: '#f59e0b', marginTop: 4 }}>Larsen, McCaleb, etc.</div>
                </div>

                {/* ETFs */}
                <div style={{ background: 'rgba(236, 72, 153, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(236, 72, 153, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>📈</span>
                    <span style={{ fontSize: 13, color: '#ec4899', fontWeight: 600 }}>ETF Holdings</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{totalXRPLocked.toFixed(0)}M</div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>{percentOfSupply}% of circ.</div>
                  <div style={{ fontSize: 11, color: '#ec4899', marginTop: 4 }}>{etfData.length} spot ETFs</div>
                </div>

                {/* Corporate Treasury */}
                <div style={{ background: 'rgba(99, 102, 241, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(99, 102, 241, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>🏢</span>
                    <span style={{ fontSize: 13, color: '#6366f1', fontWeight: 600 }}>Corp Treasury</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>~500M</div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>$471M+ allocated</div>
                  <div style={{ fontSize: 11, color: '#6366f1', marginTop: 4 }}>VivoPower, Wellgistics</div>
                </div>

                {/* Public */}
                <div style={{ background: 'rgba(0, 255, 136, 0.15)', borderRadius: 10, padding: 14, border: '1px solid rgba(0, 255, 136, 0.3)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontSize: 20 }}>🌍</span>
                    <span style={{ fontSize: 13, color: '#00ff88', fontWeight: 600 }}>Public Circulation</span>
                  </div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>~38B</div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>38% of supply</div>
                  <div style={{ fontSize: 11, color: '#00ff88', marginTop: 4 }}>6.65M+ wallets</div>
                </div>
              </div>

              {/* Top Exchange Holdings */}
              <div style={{ marginTop: 20, padding: 16, background: 'rgba(0, 0, 0, 0.2)', borderRadius: 10 }}>
                <h4 style={{ fontSize: 14, color: '#9ca3af', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                  🏦 Exchange Reserves ({richListData.length} Exchanges) {richListLoading && <span style={{ fontSize: 11 }}>⏳ Loading...</span>}
                </h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                  {richListData.map((item, idx) => (
                    <div key={idx} style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      padding: '6px 10px', 
                      background: 'rgba(0, 207, 255, 0.1)', 
                      borderRadius: 6 
                    }}>
                      <span style={{ fontSize: 11, color: '#00CFFF' }}>
                        {idx + 1}. {item.name}
                      </span>
                      <span style={{ fontSize: 11, color: '#fff', fontWeight: 600 }}>
                        {formatXrpBalance(item.balance)}
                        {item.live && <span style={{ color: '#00ff88', marginLeft: 4, fontSize: 8 }}>●</span>}
                      </span>
                    </div>
                  ))}
                </div>
                <p style={{ fontSize: 11, color: '#4a5568', marginTop: 10 }}>
                  {richListData.filter(w => w.live).length > 0 
                    ? <span style={{ color: '#00ff88' }}>🟢 Live from XRPScan • {richListData.filter(w => w.live).length}/{richListData.length} wallets • Updates every 5 min</span>
                    : <span style={{ color: '#f59e0b' }}>* Using estimates (API unavailable)</span>
                  }
                </p>
              </div>
            </div>
          </section>

          {/* Futures & Leveraged ETFs Section */}
          <section style={{
            background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 165, 0, 0.05) 100%)',
            borderRadius: 16,
            padding: 24,
            marginTop: 24,
            border: '1px solid rgba(255, 107, 0, 0.3)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
              <div>
                <h3 style={{
                  fontSize: 20,
                  fontWeight: 700,
                  marginBottom: 4,
                  background: 'linear-gradient(90deg, #ff6b00, #f59e0b)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  📊 Futures & Leveraged ETFs
                </h3>
                <p style={{ fontSize: 13, color: '#9ca3af', margin: 0 }}>
                  Derivatives-based products — no spot XRP held • Monthly dividend income potential
                </p>
              </div>
              <div style={{
                background: 'rgba(255, 107, 0, 0.1)',
                border: '1px solid rgba(255, 107, 0, 0.3)',
                borderRadius: 8,
                padding: '8px 12px',
                fontSize: 12,
                color: '#ff6b00',
              }}>
                ⚠️ Leveraged = Higher Risk
              </div>
            </div>

            {/* Futures ETF Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 20 }}>
              {futuresETFData.map((etf, index) => (
                <div key={etf.ticker} style={{
                  background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: `1px solid ${etf.leverage === '2x' ? 'rgba(255, 107, 0, 0.3)' : 'rgba(0, 207, 255, 0.3)'}`,
                  position: 'relative',
                  overflow: 'hidden',
                }}>
                  {/* Leverage Badge */}
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: etf.leverage === '2x' ? 'rgba(255, 107, 0, 0.2)' : 'rgba(0, 207, 255, 0.2)',
                    border: `1px solid ${etf.leverage === '2x' ? '#ff6b00' : '#00CFFF'}`,
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 11,
                    fontWeight: 700,
                    color: etf.leverage === '2x' ? '#ff6b00' : '#00CFFF',
                  }}>
                    {etf.leverage}
                  </div>

                  {/* Header */}
                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#fff', marginBottom: 2 }}>{etf.ticker}</div>
                    <div style={{ fontSize: 13, color: '#6b7a8a' }}>{etf.name}</div>
                    <div style={{ fontSize: 11, color: '#4a5568' }}>{etf.description}</div>
                  </div>

                  {/* Stats Grid */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Dividend Yield</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: etf.divYield > 5 ? '#00ff88' : etf.divYield > 1 ? '#f59e0b' : '#6b7a8a' }}>
                        {etf.divYield}%
                      </div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Expense Ratio</div>
                      <div style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>{etf.fee}%</div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Exchange</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#00CFFF' }}>{etf.exchange}</div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Launched</div>
                      <div style={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>{etf.launchDate}</div>
                    </div>
                  </div>

                  {/* Type Tag */}
                  <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      background: 'rgba(167, 139, 250, 0.2)',
                      border: '1px solid rgba(167, 139, 250, 0.3)',
                      borderRadius: 4,
                      padding: '3px 8px',
                      fontSize: 10,
                      color: '#a78bfa',
                    }}>
                      {etf.type}
                    </span>
                    <span style={{
                      background: 'rgba(0, 255, 136, 0.1)',
                      border: '1px solid rgba(0, 255, 136, 0.2)',
                      borderRadius: 4,
                      padding: '3px 8px',
                      fontSize: 10,
                      color: '#00ff88',
                    }}>
                      Monthly Dividends
                    </span>
                  </div>
                </div>
              ))}

              {/* Coming Soon Card - KURV */}
              {upcomingFuturesETFs.map((etf) => (
                <div key={etf.ticker} style={{
                  background: 'linear-gradient(145deg, #1a1a2e 0%, #16213e 100%)',
                  borderRadius: 12,
                  padding: 16,
                  border: '1px dashed rgba(138, 43, 226, 0.4)',
                  position: 'relative',
                  opacity: 0.9,
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 12,
                    right: 12,
                    background: 'rgba(138, 43, 226, 0.3)',
                    border: '1px solid #8b5cf6',
                    borderRadius: 6,
                    padding: '4px 8px',
                    fontSize: 10,
                    fontWeight: 700,
                    color: '#a78bfa',
                  }}>
                    🚀 COMING SOON
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#a78bfa', marginBottom: 2 }}>{etf.ticker}</div>
                    <div style={{ fontSize: 13, color: '#6b7a8a' }}>{etf.name}</div>
                    <div style={{ fontSize: 11, color: '#4a5568' }}>{etf.description}</div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Expected Launch</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#a78bfa' }}>{etf.expectedDate}</div>
                    </div>
                    <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: 8, padding: 10 }}>
                      <div style={{ fontSize: 10, color: '#6b7a8a', marginBottom: 2 }}>Est. Fee</div>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#fff' }}>{etf.fee}%</div>
                    </div>
                  </div>

                  <div style={{ marginTop: 12 }}>
                    <span style={{
                      background: 'rgba(0, 255, 136, 0.1)',
                      border: '1px solid rgba(0, 255, 136, 0.2)',
                      borderRadius: 4,
                      padding: '3px 8px',
                      fontSize: 10,
                      color: '#00ff88',
                    }}>
                      💰 Monthly Income Focus
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Info Box */}
            <div style={{
              background: 'rgba(255, 107, 0, 0.05)',
              border: '1px solid rgba(255, 107, 0, 0.2)',
              borderRadius: 10,
              padding: 14,
            }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#ff6b00', marginBottom: 8 }}>
                ⚠️ Important: Futures vs Spot ETFs
              </div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: '#9ca3af', lineHeight: 1.6 }}>
                <li><strong>No XRP custody</strong> — These ETFs use derivatives (futures/swaps), not actual XRP tokens</li>
                <li><strong>Leveraged products reset daily</strong> — 2x funds compound gains AND losses; not for long-term holding</li>
                <li><strong>Dividend yields vary</strong> — Income comes from futures roll yields & interest, not staking</li>
                <li><strong>Higher fees</strong> — Derivatives management costs more than spot custody</li>
              </ul>
            </div>
          </section>

          {/* XRPFi DeFi Section */}
          <section style={{ 
            background: 'linear-gradient(135deg, rgba(138, 43, 226, 0.1) 0%, rgba(0, 207, 255, 0.1) 100%)',
            borderRadius: 16,
            padding: 24,
            marginTop: 24,
            border: '1px solid rgba(138, 43, 226, 0.3)',
          }}>
            <h3 style={{ 
              fontSize: 20, 
              fontWeight: 700, 
              marginBottom: 8,
              background: 'linear-gradient(90deg, #a78bfa, #00CFFF)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              🔥 XRPFi — Earn Yield on Your XRP
            </h3>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
              Only 0.1% of XRP is in DeFi. These protocols let you put your XRP to work.
            </p>

            {/* DeFi Leaderboard */}
            <div style={{
              background: 'rgba(0, 0, 0, 0.3)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(167, 139, 250, 0.2)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <h4 style={{ fontSize: 16, fontWeight: 700, color: '#fff', margin: 0 }}>
                  🏆 DeFi Leaderboard
                </h4>
                <span style={{ fontSize: 11, color: '#6b7a8a' }}>Ranked by TVL</span>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {/* Flare Network */}
                <a href="https://flare.network" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                  <span style={{ fontSize: 18, width: 28 }}>🥇</span>
                  <span style={{ width: 50, fontSize: 20 }}>☀️</span>
                  <div style={{ flex: 1, position: 'relative', height: 28, background: 'rgba(0,0,0,0.3)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, height: '100%',
                      width: '100%',
                      background: 'linear-gradient(90deg, #ff6b00, #ff8c00)',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', paddingLeft: 12,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>Flare Network ↗</span>
                    </div>
                  </div>
                  <div style={{ width: 90, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: flareTVL ? '#00ff88' : '#ff6b00' }}>
                      {flareTVL ? `$${(flareTVL / 1e6).toFixed(1)}M` : '~$86M'}
                    </div>
                    <div style={{ fontSize: 10, color: flareTVL ? '#00ff88' : '#6b7a8a' }}>{flareTVL ? '🟢 Live' : 'TVL'}</div>
                  </div>
                </a>

                {/* mXRP */}
                <a href="https://midas.app" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                  <span style={{ fontSize: 18, width: 28 }}>🥈</span>
                  <span style={{ width: 50, fontSize: 20 }}>🪙</span>
                  <div style={{ flex: 1, position: 'relative', height: 28, background: 'rgba(0,0,0,0.3)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, height: '100%',
                      width: flareTVL ? `${(20 / (flareTVL / 1e6)) * 100}%` : '23%',
                      background: 'linear-gradient(90deg, #3b82f6, #60a5fa)',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', paddingLeft: 12,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>mXRP (Midas) ↗</span>
                    </div>
                  </div>
                  <div style={{ width: 90, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#3b82f6' }}>~$20M</div>
                    <div style={{ fontSize: 10, color: '#6b7a8a' }}>Est.</div>
                  </div>
                </a>

                {/* earnXRP */}
                <a href="https://earnxrp.io" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                  <span style={{ fontSize: 18, width: 28 }}>🥉</span>
                  <span style={{ width: 50, fontSize: 20 }}>💰</span>
                  <div style={{ flex: 1, position: 'relative', height: 28, background: 'rgba(0,0,0,0.3)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, height: '100%',
                      width: flareTVL ? `${(15 / (flareTVL / 1e6)) * 100}%` : '17%',
                      background: 'linear-gradient(90deg, #22c55e, #4ade80)',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', paddingLeft: 12,
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>earnXRP ↗</span>
                    </div>
                  </div>
                  <div style={{ width: 90, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#22c55e' }}>~$15M</div>
                    <div style={{ fontSize: 10, color: '#6b7a8a' }}>Est.</div>
                  </div>
                </a>

                {/* Firelight */}
                <a href="https://firelightdex.com" target="_blank" rel="noopener noreferrer" style={{ display: 'flex', alignItems: 'center', gap: 10, textDecoration: 'none' }}>
                  <span style={{ fontSize: 18, width: 28 }}>4️⃣</span>
                  <span style={{ width: 50, fontSize: 20 }}>🔥</span>
                  <div style={{ flex: 1, position: 'relative', height: 28, background: 'rgba(0,0,0,0.3)', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{
                      position: 'absolute', left: 0, top: 0, height: '100%',
                      width: flareTVL ? `${(5 / (flareTVL / 1e6)) * 100}%` : '6%',
                      background: 'linear-gradient(90deg, #ef4444, #f87171)',
                      borderRadius: 6,
                      display: 'flex', alignItems: 'center', paddingLeft: 12,
                    }}>
                      <span style={{ fontSize: 11, fontWeight: 700, color: '#fff' }}>Firelight ↗</span>
                    </div>
                  </div>
                  <div style={{ width: 90, textAlign: 'right' }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: '#ef4444' }}>~$5M</div>
                    <div style={{ fontSize: 10, color: '#6b7a8a' }}>Est.</div>
                  </div>
                </a>
              </div>

              {/* Summary stats */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-around', 
                marginTop: 16, 
                paddingTop: 16, 
                borderTop: '1px solid rgba(255,255,255,0.1)',
                flexWrap: 'wrap',
                gap: 12,
              }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#a78bfa' }}>
                    ${flareTVL ? ((flareTVL / 1e6) + 40).toFixed(0) : '~126'}M
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Total XRPFi TVL</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#00ff88' }}>4-10%</div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Yield Range</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#00CFFF' }}>4</div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Protocols</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: '#f59e0b' }}>0.1%</div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>XRP in DeFi</div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
              {/* Flare Network */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.3)', 
                borderRadius: 12, 
                padding: 18,
                border: '1px solid rgba(255, 107, 0, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>☀️</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#ff6b00' }}>Flare Network</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Layer 1 • XRPFi Hub</div>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: '#d1d5db', marginBottom: 12, lineHeight: 1.5 }}>
                  Bridge XRP → FXRP (1:1 wrapped token) to access DeFi. FXRP is overcollateralized and non-custodial via FAssets.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(255, 107, 0, 0.2)', borderRadius: 4, color: '#ff6b00' }}>FXRP Bridge</span>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: flareTVL ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 107, 0, 0.2)', borderRadius: 4, color: flareTVL ? '#00ff88' : '#ff6b00' }}>
                    {flareTVL ? `$${(flareTVL / 1e6).toFixed(1)}M TVL 🟢` : '~$86M TVL'}
                  </span>
                </div>
              </div>

              {/* Firelight Finance */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.3)', 
                borderRadius: 12, 
                padding: 18,
                border: '1px solid rgba(239, 68, 68, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>🔥</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#ef4444' }}>Firelight Finance</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Liquid Staking • On Flare</div>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: '#d1d5db', marginBottom: 12, lineHeight: 1.5 }}>
                  Stake FXRP → receive stXRP (liquid staking token). Use stXRP in DeFi while earning staking rewards. DeFi insurance model.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: 4, color: '#ef4444' }}>stXRP</span>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(239, 68, 68, 0.2)', borderRadius: 4, color: '#ef4444' }}>Phase 2: Q1 2026</span>
                </div>
              </div>

              {/* earnXRP */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.3)', 
                borderRadius: 12, 
                padding: 18,
                border: '1px solid rgba(34, 197, 94, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>💰</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#22c55e' }}>earnXRP</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Yield Vault • Upshift + Clearstar</div>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: '#d1d5db', marginBottom: 12, lineHeight: 1.5 }}>
                  Deposit FXRP into automated vault. Deploys across carry trades, staking, LP strategies. Returns compounded in XRP.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: 4, color: '#22c55e' }}>4-10% APY</span>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(34, 197, 94, 0.2)', borderRadius: 4, color: '#22c55e' }}>Non-Custodial</span>
                </div>
              </div>

              {/* mXRP */}
              <div style={{ 
                background: 'rgba(0, 0, 0, 0.3)', 
                borderRadius: 12, 
                padding: 18,
                border: '1px solid rgba(59, 130, 246, 0.3)',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                  <span style={{ fontSize: 24 }}>🪙</span>
                  <div>
                    <div style={{ fontSize: 16, fontWeight: 700, color: '#3b82f6' }}>mXRP (Midas)</div>
                    <div style={{ fontSize: 11, color: '#9ca3af' }}>Tokenized XRP • Axelar</div>
                  </div>
                </div>
                <p style={{ fontSize: 12, color: '#d1d5db', marginBottom: 12, lineHeight: 1.5 }}>
                  Tokenized XRP product with ~$20M TVL. Base yield from institutional strategies + additional DeFi yield opportunities.
                </p>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: 4, color: '#3b82f6' }}>6-8% APY</span>
                  <span style={{ fontSize: 11, padding: '4px 8px', background: 'rgba(59, 130, 246, 0.2)', borderRadius: 4, color: '#3b82f6' }}>~$20M TVL</span>
                </div>
              </div>
            </div>

            <div style={{ 
              marginTop: 16, 
              padding: 12, 
              background: 'rgba(245, 158, 11, 0.1)', 
              borderRadius: 8,
              border: '1px solid rgba(245, 158, 11, 0.2)',
            }}>
              <p style={{ fontSize: 12, color: '#f59e0b', margin: 0 }}>
                ⚠️ <strong>DYOR:</strong> DeFi carries smart contract risk. Only use funds you can afford to lose. These are not endorsements.
              </p>
            </div>
          </section>
        </>
      )}

      {/* Whales View */}
      {selectedView === 'whales' && (
        <section style={styles.whalesSection}>
          <div style={styles.whalesHeader}>
            <h2 style={styles.sectionTitle}>XRP Rich List</h2>
            <p style={styles.sectionSubtitle}>Top wallet holders and distribution tiers on the XRP Ledger</p>
          </div>

          {/* Whale Stats Row */}
          <div style={styles.whaleStatsRow}>
            <div style={styles.whaleStat}>
              <span style={styles.whaleStatValue}>{(whaleStats.totalAccounts / 1000000).toFixed(2)}M</span>
              <span style={styles.whaleStatLabel}>Total Accounts</span>
            </div>
            <div style={styles.whaleStat}>
              <span style={styles.whaleStatValue}>{whaleStats.top10Percent}%</span>
              <span style={styles.whaleStatLabel}>Held by Top 10</span>
            </div>
            <div style={styles.whaleStat}>
              <span style={styles.whaleStatValue}>{whaleStats.top50Percent}%</span>
              <span style={styles.whaleStatLabel}>Held by Top 50</span>
            </div>
            <div style={styles.whaleStat}>
              <span style={styles.whaleStatValue}>{(escrowBalance / 1000000000).toFixed(1)}B</span>
              <span style={styles.whaleStatLabel}>Ripple Escrow</span>
            </div>
          </div>

          {/* Distribution Tiers */}
          <div style={styles.tiersSection}>
            <h3 style={styles.tiersTitle}>🎯 Where Do You Rank?</h3>
            <p style={styles.tiersSubtitle}>Enter your XRP holdings to see your tier</p>
            
            {/* User Input Calculator */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(0, 207, 255, 0.1) 0%, rgba(138, 43, 226, 0.1) 100%)',
              borderRadius: 12,
              padding: 20,
              marginBottom: 20,
              border: '1px solid rgba(0, 207, 255, 0.2)',
            }}>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  type="number"
                  placeholder="Enter your XRP amount..."
                  value={userXrpHoldings}
                  onChange={(e) => setUserXrpHoldings(e.target.value)}
                  style={{
                    flex: 1,
                    minWidth: 200,
                    padding: '14px 18px',
                    background: 'rgba(0,0,0,0.3)',
                    border: '2px solid rgba(0, 207, 255, 0.3)',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 18,
                    fontWeight: 600,
                    outline: 'none',
                  }}
                />
                {userXrpHoldings && (
                  <button 
                    onClick={() => setUserXrpHoldings('')}
                    style={{
                      padding: '14px 20px',
                      background: 'rgba(255,255,255,0.1)',
                      border: 'none',
                      borderRadius: 8,
                      color: '#fff',
                      cursor: 'pointer',
                    }}
                  >Clear</button>
                )}
              </div>
              
              {/* Results */}
              {userXrpHoldings && parseFloat(userXrpHoldings) > 0 && (() => {
                const holdings = parseFloat(userXrpHoldings);
                const usdValue = holdings * xrpPrice;
                
                // Find user's tier
                let userTier = { tier: 'Top 100%', label: 'Plankton', emoji: '🦠', color: '#4a5568', minBalance: 0 };
                let nextTier = null;
                
                // Sort tiers by minBalance descending to find the right tier
                const sortedTiers = [...distributionTiers].sort((a, b) => b.minBalance - a.minBalance);
                
                for (let i = 0; i < sortedTiers.length; i++) {
                  if (holdings >= sortedTiers[i].minBalance) {
                    userTier = sortedTiers[i];
                    nextTier = i > 0 ? sortedTiers[i - 1] : null;
                    break;
                  }
                  // If we're at the last tier and still haven't matched, set next tier
                  if (i === sortedTiers.length - 1) {
                    nextTier = sortedTiers[i];
                  }
                }
                
                // Find rank position (1-9)
                const tierIndex = sortedTiers.findIndex(t => t.minBalance === userTier.minBalance);
                const rankPosition = tierIndex >= 0 ? tierIndex + 1 : 9;
                
                const toNextTier = nextTier ? nextTier.minBalance - holdings : 0;
                
                return (
                  <div style={{ marginTop: 20 }}>
                    {/* Main Result Card */}
                    <div style={{
                      background: `linear-gradient(135deg, ${userTier.color}20 0%, ${userTier.color}10 100%)`,
                      borderRadius: 12,
                      padding: 24,
                      border: `2px solid ${userTier.color}50`,
                      textAlign: 'center',
                      marginBottom: 16,
                    }}>
                      <div style={{ fontSize: 48, marginBottom: 8 }}>{userTier.emoji}</div>
                      <div style={{ fontSize: 28, fontWeight: 800, color: userTier.color, marginBottom: 4 }}>
                        {userTier.tier}
                      </div>
                      <div style={{ fontSize: 20, color: '#00ff88', fontWeight: 700, marginBottom: 8 }}>
                        🏆 Top {userTier.percentile}% of Holders
                      </div>
                      <div style={{ fontSize: 14, color: '#9ca3af', marginBottom: 16 }}>
                        You are a <span style={{ color: userTier.color, fontWeight: 700 }}>{userTier.tier}</span>!
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'center', gap: 30, flexWrap: 'wrap' }}>
                        <div>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#fff' }}>
                            {holdings.toLocaleString()}
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7a8a' }}>XRP Holdings</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#00ff88' }}>
                            ${usdValue >= 1000000 
                              ? (usdValue / 1000000).toFixed(2) + 'M' 
                              : usdValue >= 1000 
                                ? (usdValue / 1000).toFixed(1) + 'K'
                                : usdValue.toFixed(2)
                            }
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7a8a' }}>USD Value</div>
                        </div>
                        <div>
                          <div style={{ fontSize: 24, fontWeight: 700, color: '#00CFFF' }}>
                            #{rankPosition} of 9
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7a8a' }}>Tier Rank</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Next Tier Info */}
                    {nextTier && toNextTier > 0 && (
                      <div style={{
                        background: 'rgba(255,255,255,0.05)',
                        borderRadius: 8,
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 12,
                      }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <span style={{ fontSize: 24 }}>{nextTier.emoji}</span>
                          <div>
                            <div style={{ fontSize: 14, color: '#fff', fontWeight: 600 }}>
                              Next: {nextTier.label}
                            </div>
                            <div style={{ fontSize: 12, color: '#6b7a8a' }}>
                              {nextTier.tier} of holders
                            </div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontSize: 16, color: '#f59e0b', fontWeight: 700 }}>
                            +{toNextTier.toLocaleString()} XRP
                          </div>
                          <div style={{ fontSize: 12, color: '#6b7a8a' }}>
                            (${(toNextTier * xrpPrice).toLocaleString(undefined, {maximumFractionDigits: 0})})
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
            
            {/* Tier Reference Grid */}
            <h4 style={{ fontSize: 14, color: '#9ca3af', marginBottom: 12, marginTop: 24 }}>📊 XRP Ranking Ladder</h4>
            <p style={{ fontSize: 12, color: '#6b7a8a', marginBottom: 12 }}>Community standard rankings by Edo Farina & EGRAG</p>
            <div style={styles.tiersGrid} className="tier-grid">
              {distributionTiers.map((tier, index) => (
                <div key={index} style={{...styles.tierCard, borderColor: `${tier.color}50`}}>
                  <div style={styles.tierEmoji}>{tier.emoji}</div>
                  <div style={{...styles.tierLabel, color: tier.color, fontSize: 16, fontWeight: 700}}>{tier.tier}</div>
                  <div style={styles.tierBalance}>
                    {tier.minBalance >= 100000 
                      ? `${(tier.minBalance / 1000).toFixed(0)}K+ XRP`
                      : tier.minBalance >= 1000
                        ? `${(tier.minBalance / 1000).toFixed(0)}K+ XRP`
                        : `${tier.minBalance}+ XRP`
                    }
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>
                    ~${(tier.minBalance * xrpPrice).toLocaleString(undefined, {maximumFractionDigits: 0})} USD
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Rich List Table - Top 50 Wallets */}
          <div style={styles.richListSection}>
            <h3 style={styles.richListTitle}>
              🏆 Top 50 Wallet Holdings
            </h3>
            <p style={{ fontSize: 12, color: '#6b7a8a', marginBottom: 15 }}>
              Data from XRPScan/CoinCarp rich list • Includes escrow, exchanges, founders & whales
            </p>
            <div style={styles.richListTable}>
              <div style={styles.richListHeader}>
                <span style={styles.richListColRank}>#</span>
                <span style={styles.richListColName}>Wallet</span>
                <span style={styles.richListColBalance}>Balance</span>
                <span style={styles.richListColPercent}>% Supply</span>
                <span style={styles.richListColType}>Type</span>
              </div>
              {top50Wallets.map((item, idx) => (
                <div key={idx} style={styles.richListRow}>
                  <span style={styles.richListColRank}>{item.rank}</span>
                  <span style={styles.richListColName}>
                    <span style={styles.walletName}>{item.name}</span>
                    <a 
                      href={`https://xrpscan.com/account/${item.address}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ fontSize: 10, color: '#00CFFF', marginLeft: 8, textDecoration: 'none' }}
                    >
                      {item.address.slice(0, 8)}...
                    </a>
                  </span>
                  <span style={styles.richListColBalance}>
                    {formatXrpBalance(item.balance)}
                  </span>
                  <span style={styles.richListColPercent}>{((item.balance / 100000000000) * 100).toFixed(2)}%</span>
                  <span style={styles.richListColType}>
                    <span style={{
                      ...styles.typeTag,
                      background: item.type === 'escrow' ? 'rgba(167, 139, 250, 0.2)' :
                                 item.type === 'exchange' ? 'rgba(0, 207, 255, 0.2)' :
                                 item.type === 'founder' ? 'rgba(245, 158, 11, 0.2)' :
                                 item.type === 'ripple' ? 'rgba(138, 43, 226, 0.2)' :
                                 'rgba(0, 255, 136, 0.2)',
                      color: item.type === 'escrow' ? '#a78bfa' :
                             item.type === 'exchange' ? '#00CFFF' :
                             item.type === 'founder' ? '#f59e0b' :
                             item.type === 'ripple' ? '#8b5cf6' :
                             '#00ff88',
                    }}>
                      {item.type === 'escrow' ? '🔒 Escrow' :
                       item.type === 'exchange' ? '🏦 Exchange' :
                       item.type === 'founder' ? '👤 Founder' :
                       item.type === 'ripple' ? '🏢 Ripple' :
                       '🐋 Whale'}
                    </span>
                  </span>
                </div>
              ))}
            </div>
            <div style={{ marginTop: 16, padding: 12, background: 'rgba(0, 207, 255, 0.1)', borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: '#00CFFF', fontWeight: 600, marginBottom: 8 }}>Top 50 Summary</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                <span style={{ fontSize: 11, color: '#a78bfa' }}>🔒 Escrow: {top50Wallets.filter(w => w.type === 'escrow').length} wallets</span>
                <span style={{ fontSize: 11, color: '#00CFFF' }}>🏦 Exchanges: {top50Wallets.filter(w => w.type === 'exchange').length} wallets</span>
                <span style={{ fontSize: 11, color: '#8b5cf6' }}>🏢 Ripple: {top50Wallets.filter(w => w.type === 'ripple').length} wallets</span>
                <span style={{ fontSize: 11, color: '#f59e0b' }}>👤 Founders: {top50Wallets.filter(w => w.type === 'founder').length} wallets</span>
                <span style={{ fontSize: 11, color: '#00ff88' }}>🐋 Whales: {top50Wallets.filter(w => w.type === 'whale').length} wallets</span>
              </div>
            </div>
          </div>

          {/* Distribution Bar */}
          <div style={styles.distributionSection}>
            <h3 style={styles.distributionTitle}>Supply Distribution</h3>
            <div style={styles.distributionBar}>
              <div style={{...styles.distributionSegment, width: `${(escrowBalance / 1e11 * 100).toFixed(0)}%`, background: 'linear-gradient(90deg, #a78bfa, #8b5cf6)'}}>
                <span style={styles.segmentLabel}>Ripple Escrow</span>
                <span style={styles.segmentValue}>{(escrowBalance / 1e11 * 100).toFixed(0)}%</span>
              </div>
              <div style={{...styles.distributionSegment, width: `${(richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e11 * 100).toFixed(0)}%`, background: 'linear-gradient(90deg, #00CFFF, #0090CC)'}}>
                <span style={styles.segmentLabel}>Exchanges</span>
                <span style={styles.segmentValue}>{(richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e11 * 100).toFixed(0)}%</span>
              </div>
              <div style={{...styles.distributionSegment, width: '8%', background: 'linear-gradient(90deg, #f59e0b, #d97706)'}}>
                <span style={styles.segmentLabel}>Founders</span>
                <span style={styles.segmentValue}>8%</span>
              </div>
              <div style={{...styles.distributionSegment, width: `${100 - (escrowBalance / 1e11 * 100) - (richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e11 * 100) - 8}%`, background: 'linear-gradient(90deg, #00ff88, #00cc6a)'}}>
                <span style={styles.segmentLabel}>Public</span>
                <span style={styles.segmentValue}>{(100 - (escrowBalance / 1e11 * 100) - (richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e11 * 100) - 8).toFixed(0)}%</span>
              </div>
            </div>
            <div style={styles.distributionLegend}>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#a78bfa'}} /> Ripple Escrow ({(escrowBalance / 1e9).toFixed(0)}B)</span>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#00CFFF'}} /> Exchanges ({(richListData.reduce((sum, w) => sum + (w.balance || 0), 0) / 1e9).toFixed(0)}B)</span>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#f59e0b'}} /> Founders (8B)</span>
              <span style={styles.legendItem}><span style={{...styles.legendDot, background: '#00ff88'}} /> Public Circulation</span>
            </div>
          </div>

          <div style={styles.dataSource}>
            <span>Data sourced from XRPScan API • Live updates</span>
          </div>
        </section>
      )}

      {/* Never Sell */}
      {selectedView === 'wealth' && (
        <section style={styles.neverSellSection}>
          <div style={styles.neverSellHeader}>
            <h2 style={styles.sectionTitle}>💰 Wealth Strategies</h2>
            <p style={styles.sectionSubtitle}>Every option to monetize your crypto — staking, lending, borrowing, dividends, and insurance</p>
          </div>

          {/* Strategy Selector */}
          <div style={styles.strategySelector} className="strategy-selector">
            <button 
              onClick={() => setActiveStrategy('portfolio')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'portfolio' ? styles.strategyButtonActive : {}), ...(activeStrategy === 'portfolio' ? {background: 'linear-gradient(135deg, #f59e0b, #d97706)'} : {})}}
            >
              🎯 Portfolio Builder
            </button>
            <button 
              onClick={() => setActiveStrategy('dividend')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'dividend' ? styles.strategyButtonActive : {})}}
            >
              📈 Sell ETF Shares → Dividends
            </button>
            <button 
              onClick={() => setActiveStrategy('stablecoin')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'stablecoin' ? styles.strategyButtonActive : {})}}
            >
              💵 Sell → Lend Stables
            </button>
            <button 
              onClick={() => setActiveStrategy('yield')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'yield' ? styles.strategyButtonActive : {})}}
            >
              🌾 Stake/LP for Yield
            </button>
            <button 
              onClick={() => setActiveStrategy('borrow')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'borrow' ? styles.strategyButtonActive : {})}}
            >
              🏦 Borrow Against
            </button>
            <button 
              onClick={() => setActiveStrategy('insurance')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'insurance' ? styles.strategyButtonActive : {})}}
            >
              🛡️ Life Insurance
            </button>
            <button 
              onClick={() => setActiveStrategy('hybrid')} 
              style={{...styles.strategyButton, ...(activeStrategy === 'hybrid' ? styles.strategyButtonActive : {})}}
            >
              ⚡ Hybrid Strategy
            </button>
          </div>

          {/* Calculator */}
          <div style={styles.calculatorGrid} className="calc-grid">
            {/* Inputs */}
            <div style={styles.calcInputs}>
              <h3 style={styles.calcTitle}>📊 Your Scenario</h3>
              
              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Total XRP Holdings</label>
                <input 
                  type="number" 
                  value={neverSellInputs.xrpHoldings}
                  onChange={(e) => setNeverSellInputs({...neverSellInputs, xrpHoldings: Number(e.target.value)})}
                  style={styles.calcInput}
                />
              </div>

              <div style={styles.inputGroup}>
                <label style={styles.inputLabel}>Target XRP Price ($)</label>
                <input 
                  type="number" 
                  value={neverSellInputs.targetPrice}
                  onChange={(e) => setNeverSellInputs({...neverSellInputs, targetPrice: Number(e.target.value)})}
                  style={styles.calcInput}
                />
              </div>

              {/* Portfolio Builder - Allocation Inputs */}
              {activeStrategy === 'portfolio' && (
                <>
                  <div style={{ background: 'rgba(245, 158, 11, 0.1)', borderRadius: 8, padding: 12, marginBottom: 12, border: '1px solid rgba(245, 158, 11, 0.3)' }}>
                    <div style={{ fontSize: 12, color: '#f59e0b', marginBottom: 8, fontWeight: 600 }}>
                      Allocate your {neverSellInputs.xrpHoldings.toLocaleString()} XRP across strategies:
                    </div>
                    <div style={{ fontSize: 11, color: '#6b7a8a' }}>
                      Remaining: {(neverSellInputs.xrpHoldings - (neverSellInputs.allocDividends || 0) - (neverSellInputs.allocStablecoin || 0) - (neverSellInputs.allocYield || 0) - (neverSellInputs.allocBorrow || 0) - (neverSellInputs.allocHold || 0)).toLocaleString()} XRP
                    </div>
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>📈 Sell ETF Shares → Dividends (XRP)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.allocDividends || 0}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, allocDividends: Number(e.target.value)})}
                      style={styles.calcInput}
                      placeholder="0"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>💵 Sell → Lend Stables (XRP)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.allocStablecoin || 0}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, allocStablecoin: Number(e.target.value)})}
                      style={styles.calcInput}
                      placeholder="0"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>🌾 Stake/LP for Yield (XRP)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.allocYield || 0}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, allocYield: Number(e.target.value)})}
                      style={styles.calcInput}
                      placeholder="0"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>🏦 Borrow Against (XRP)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.allocBorrow || 0}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, allocBorrow: Number(e.target.value)})}
                      style={styles.calcInput}
                      placeholder="0"
                    />
                  </div>

                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>💎 HODL (XRP)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.allocHold || 0}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, allocHold: Number(e.target.value)})}
                      style={styles.calcInput}
                      placeholder="0"
                    />
                  </div>

                  <div style={{ borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: 12, marginTop: 8 }}>
                    <div style={{ fontSize: 11, color: '#6b7a8a', marginBottom: 8 }}>Strategy Rates:</div>
                    
                    <div style={{...styles.inputGroup, marginBottom: 8}}>
                      <label style={{...styles.inputLabel, fontSize: 11}}>Dividend Yield (%)</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={neverSellInputs.dividendYield}
                        onChange={(e) => setNeverSellInputs({...neverSellInputs, dividendYield: Number(e.target.value)})}
                        style={{...styles.calcInput, padding: '8px 12px'}}
                      />
                    </div>

                    <div style={{...styles.inputGroup, marginBottom: 8}}>
                      <label style={{...styles.inputLabel, fontSize: 11}}>Stablecoin Lending APY (%)</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={neverSellInputs.lendingAPY || 8}
                        onChange={(e) => setNeverSellInputs({...neverSellInputs, lendingAPY: Number(e.target.value)})}
                        style={{...styles.calcInput, padding: '8px 12px'}}
                      />
                    </div>

                    <div style={{...styles.inputGroup, marginBottom: 8}}>
                      <label style={{...styles.inputLabel, fontSize: 11}}>Staking/LP Yield (%)</label>
                      <input 
                        type="number" 
                        step="0.1"
                        value={neverSellInputs.stakingYield || 5}
                        onChange={(e) => setNeverSellInputs({...neverSellInputs, stakingYield: Number(e.target.value)})}
                        style={{...styles.calcInput, padding: '8px 12px'}}
                      />
                    </div>

                    <div style={{...styles.inputGroup, marginBottom: 8}}>
                      <label style={{...styles.inputLabel, fontSize: 11}}>Loan-to-Value (%)</label>
                      <input 
                        type="number" 
                        value={neverSellInputs.loanToValue}
                        onChange={(e) => setNeverSellInputs({...neverSellInputs, loanToValue: Number(e.target.value)})}
                        style={{...styles.calcInput, padding: '8px 12px'}}
                      />
                    </div>

                    <div style={{...styles.inputGroup, marginBottom: 8}}>
                      <label style={{...styles.inputLabel, fontSize: 11}}>Tax Rate (%)</label>
                      <input 
                        type="number" 
                        value={neverSellInputs.taxRate}
                        onChange={(e) => setNeverSellInputs({...neverSellInputs, taxRate: Number(e.target.value)})}
                        style={{...styles.calcInput, padding: '8px 12px'}}
                      />
                    </div>
                  </div>
                </>
              )}

              {/* Dynamic % slider - only show for strategies that use it */}
              {(activeStrategy === 'dividend' || activeStrategy === 'hybrid') && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>% to Convert to Dividends</label>
                  <input 
                    type="range" 
                    min="0" 
                    max="100" 
                    value={neverSellInputs.percentToConvert}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, percentToConvert: Number(e.target.value)})}
                    style={styles.rangeInput}
                  />
                  <span style={styles.rangeValue}>{neverSellInputs.percentToConvert}%</span>
                </div>
              )}

              {/* Dividend Yield - only for dividend strategy */}
              {activeStrategy === 'dividend' && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Dividend Yield (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={neverSellInputs.dividendYield}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, dividendYield: Number(e.target.value)})}
                    style={styles.calcInput}
                  />
                </div>
              )}

              {/* Stablecoin Lending APY - for stablecoin strategy */}
              {activeStrategy === 'stablecoin' && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Lending APY (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={neverSellInputs.lendingAPY || 8}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, lendingAPY: Number(e.target.value)})}
                    style={styles.calcInput}
                  />
                </div>
              )}

              {/* Staking/LP Yield - for yield strategy */}
              {activeStrategy === 'yield' && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Expected Yield (%)</label>
                  <input 
                    type="number" 
                    step="0.1"
                    value={neverSellInputs.stakingYield || 5}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, stakingYield: Number(e.target.value)})}
                    style={styles.calcInput}
                  />
                </div>
              )}

              {/* Tax Rate - show for dividend, stablecoin, yield, hybrid only */}
              {activeStrategy !== 'borrow' && activeStrategy !== 'portfolio' && activeStrategy !== 'insurance' && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>
                    {activeStrategy === 'stablecoin' ? 'Capital Gains Tax Rate (%)' : 'Tax Rate (%)'}
                  </label>
                  <input 
                    type="number" 
                    value={neverSellInputs.taxRate}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, taxRate: Number(e.target.value)})}
                    style={styles.calcInput}
                  />
                </div>
              )}

              {(activeStrategy === 'borrow' || activeStrategy === 'hybrid') && (
                <>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Loan-to-Value (%)</label>
                    <input 
                      type="number" 
                      value={neverSellInputs.loanToValue}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, loanToValue: Number(e.target.value)})}
                      style={styles.calcInput}
                    />
                  </div>
                  <div style={styles.inputGroup}>
                    <label style={styles.inputLabel}>Loan Interest Rate (%)</label>
                    <input 
                      type="number" 
                      step="0.1"
                      value={neverSellInputs.loanInterestRate}
                      onChange={(e) => setNeverSellInputs({...neverSellInputs, loanInterestRate: Number(e.target.value)})}
                      style={styles.calcInput}
                    />
                  </div>
                </>
              )}

              {/* Insurance specific inputs */}
              {activeStrategy === 'insurance' && (
                <div style={styles.inputGroup}>
                  <label style={styles.inputLabel}>Premium Period (Years)</label>
                  <input 
                    type="number" 
                    value={neverSellInputs.premiumYears || 10}
                    onChange={(e) => setNeverSellInputs({...neverSellInputs, premiumYears: Number(e.target.value)})}
                    style={styles.calcInput}
                  />
                </div>
              )}
            </div>

            {/* Results */}
            <div style={styles.calcResults}>
              <h3 style={styles.calcTitle}>💰 Results</h3>
              
              {(() => {
                const totalValue = neverSellInputs.xrpHoldings * neverSellInputs.targetPrice;
                const dividendPortion = totalValue * (neverSellInputs.percentToConvert / 100);
                const holdPortion = totalValue - dividendPortion;
                const annualDividends = dividendPortion * (neverSellInputs.dividendYield / 100);
                const afterTaxDividends = annualDividends * (1 - neverSellInputs.taxRate / 100);
                const loanAmount = holdPortion * (neverSellInputs.loanToValue / 100);
                const annualInterest = loanAmount * (neverSellInputs.loanInterestRate / 100);
                const netFromLoan = loanAmount - annualInterest;
                
                return (
                  <>
                    <div style={styles.resultCard}>
                      <span style={styles.resultLabel}>Total Portfolio Value</span>
                      <span style={styles.resultValue}>${totalValue.toLocaleString()}</span>
                    </div>

                    {activeStrategy === 'dividend' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Dividend Portfolio</span>
                          <span style={styles.resultValue}>${dividendPortion.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Annual Dividends (Pre-Tax)</span>
                          <span style={styles.resultValue}>${annualDividends.toLocaleString()}</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>Annual Income (After Tax)</span>
                          <span style={styles.resultValueBig}>${afterTaxDividends.toLocaleString()}</span>
                          <span style={styles.resultSub}>${(afterTaxDividends / 12).toLocaleString()}/month</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>XRP Still Held</span>
                          <span style={styles.resultValue}>{(neverSellInputs.xrpHoldings * (1 - neverSellInputs.percentToConvert / 100)).toLocaleString()} XRP</span>
                          <span style={styles.resultSub}>${holdPortion.toLocaleString()} value</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'borrow' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Collateral (Never Sold)</span>
                          <span style={styles.resultValue}>${totalValue.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Loan Amount ({neverSellInputs.loanToValue}% LTV)</span>
                          <span style={styles.resultValue}>${(totalValue * neverSellInputs.loanToValue / 100).toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Annual Interest Cost</span>
                          <span style={{...styles.resultValue, color: '#ff6b9d'}}>-${(totalValue * neverSellInputs.loanToValue / 100 * neverSellInputs.loanInterestRate / 100).toLocaleString()}</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>Tax-Free Cash Access</span>
                          <span style={styles.resultValueBig}>${(totalValue * neverSellInputs.loanToValue / 100).toLocaleString()}</span>
                          <span style={styles.resultSub}>No capital gains tax!</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'hybrid' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Dividend Portfolio ({neverSellInputs.percentToConvert}%)</span>
                          <span style={styles.resultValue}>${dividendPortion.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Dividend Income (After Tax)</span>
                          <span style={styles.resultValue}>${afterTaxDividends.toLocaleString()}/year</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>XRP Collateral ({100 - neverSellInputs.percentToConvert}%)</span>
                          <span style={styles.resultValue}>${holdPortion.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Tax-Free Loan Available</span>
                          <span style={styles.resultValue}>${loanAmount.toLocaleString()}</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>Total Annual Access</span>
                          <span style={styles.resultValueBig}>${(afterTaxDividends + loanAmount * 0.1).toLocaleString()}</span>
                          <span style={styles.resultSub}>Dividends + 10% loan drawdown</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'stablecoin' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Sell XRP → Stablecoins</span>
                          <span style={styles.resultValue}>${totalValue.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Capital Gains Tax ({neverSellInputs.taxRate}%)</span>
                          <span style={{...styles.resultValue, color: '#ff6b9d'}}>-${(totalValue * neverSellInputs.taxRate / 100 * 0.5).toLocaleString()}</span>
                          <span style={styles.resultSub}>Assuming 50% is gains</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Net to Lend</span>
                          <span style={styles.resultValue}>${(totalValue * (1 - neverSellInputs.taxRate / 100 * 0.5)).toLocaleString()}</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>Annual Lending Income ({neverSellInputs.lendingAPY || 8}% APY)</span>
                          <span style={styles.resultValueBig}>${(totalValue * (1 - neverSellInputs.taxRate / 100 * 0.5) * ((neverSellInputs.lendingAPY || 8) / 100)).toLocaleString()}</span>
                          <span style={styles.resultSub}>${((totalValue * (1 - neverSellInputs.taxRate / 100 * 0.5) * ((neverSellInputs.lendingAPY || 8) / 100)) / 12).toLocaleString()}/month • Taxed as income</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'yield' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>XRP Holdings (Keep)</span>
                          <span style={styles.resultValue}>{neverSellInputs.xrpHoldings.toLocaleString()} XRP</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Current Value</span>
                          <span style={styles.resultValue}>${totalValue.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Conservative Staking (3% APY)</span>
                          <span style={styles.resultValue}>${(totalValue * 0.03).toLocaleString()}/year</span>
                          <span style={styles.resultSub}>Low risk, exchange staking</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>At Your Rate ({neverSellInputs.stakingYield || 5}% APY)</span>
                          <span style={styles.resultValueBig}>${(totalValue * ((neverSellInputs.stakingYield || 5) / 100)).toLocaleString()}/year</span>
                          <span style={styles.resultSub}>${((totalValue * ((neverSellInputs.stakingYield || 5) / 100)) / 12).toLocaleString()}/month</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>⚠️ Note</span>
                          <span style={{...styles.resultValue, fontSize: 13, color: '#ffaa00'}}>Yields vary. AMM/LP has impermanent loss risk.</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'insurance' && (
                      <>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Sell XRP for Premiums</span>
                          <span style={styles.resultValue}>${totalValue.toLocaleString()}</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Annual Premium ({neverSellInputs.premiumYears || 10} years)</span>
                          <span style={styles.resultValue}>${(totalValue / (neverSellInputs.premiumYears || 10)).toLocaleString()}/year</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Est. Cash Value (Year {(neverSellInputs.premiumYears || 10) + 5})</span>
                          <span style={styles.resultValue}>${(totalValue * 1.4).toLocaleString()}</span>
                          <span style={styles.resultSub}>~4% growth, tax-deferred</span>
                        </div>
                        <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                          <span style={styles.resultLabel}>Tax-Free Loans Available</span>
                          <span style={styles.resultValueBig}>${(totalValue * 1.4 * 0.9).toLocaleString()}</span>
                          <span style={styles.resultSub}>~90% of cash value, no taxes</span>
                        </div>
                        <div style={styles.resultCard}>
                          <span style={styles.resultLabel}>Death Benefit to Heirs</span>
                          <span style={styles.resultValue}>${(totalValue * 2).toLocaleString()}+</span>
                          <span style={styles.resultSub}>Tax-free to beneficiaries</span>
                        </div>
                      </>
                    )}

                    {activeStrategy === 'portfolio' && (() => {
                      const price = neverSellInputs.targetPrice;
                      const taxRate = neverSellInputs.taxRate / 100;
                      
                      // Calculate each allocation
                      const allocDividends = neverSellInputs.allocDividends || 0;
                      const allocStablecoin = neverSellInputs.allocStablecoin || 0;
                      const allocYield = neverSellInputs.allocYield || 0;
                      const allocBorrow = neverSellInputs.allocBorrow || 0;
                      const allocHold = neverSellInputs.allocHold || 0;
                      
                      const totalAllocated = allocDividends + allocStablecoin + allocYield + allocBorrow + allocHold;
                      const unallocated = neverSellInputs.xrpHoldings - totalAllocated;
                      
                      // Dividend income (sell XRP, buy dividend stocks)
                      const dividendValue = allocDividends * price;
                      const dividendTax = dividendValue * taxRate * 0.5; // Assume 50% is gains
                      const dividendNet = dividendValue - dividendTax;
                      const dividendIncome = dividendNet * (neverSellInputs.dividendYield / 100);
                      const dividendAfterTax = dividendIncome * (1 - taxRate * 0.5); // Qualified dividend rate
                      
                      // Stablecoin lending income
                      const stablecoinValue = allocStablecoin * price;
                      const stablecoinTax = stablecoinValue * taxRate * 0.5;
                      const stablecoinNet = stablecoinValue - stablecoinTax;
                      const stablecoinIncome = stablecoinNet * ((neverSellInputs.lendingAPY || 8) / 100);
                      
                      // Staking/LP yield (no sale, XRP yield in kind)
                      const yieldValue = allocYield * price;
                      const yieldIncome = yieldValue * ((neverSellInputs.stakingYield || 5) / 100);
                      
                      // Borrow (no sale, get loan)
                      const borrowValue = allocBorrow * price;
                      const loanAvailable = borrowValue * (neverSellInputs.loanToValue / 100);
                      const loanInterest = loanAvailable * (neverSellInputs.loanInterestRate / 100);
                      
                      // HODL (just hold)
                      const holdValue = allocHold * price;
                      
                      // Totals - Before and After Tax
                      const totalBeforeTax = dividendIncome + stablecoinIncome + yieldIncome;
                      const stablecoinAfterTax = stablecoinIncome * (1 - taxRate); // Interest taxed as ordinary income
                      const yieldAfterTax = yieldIncome * (1 - taxRate * 0.5); // Crypto yield - assume partial tax
                      const totalAfterTax = dividendAfterTax + stablecoinAfterTax + yieldAfterTax;
                      const totalLoanAccess = loanAvailable;
                      const totalXRPKept = allocYield + allocBorrow + allocHold;
                      
                      return (
                        <>
                          {allocDividends > 0 && (
                            <div style={{...styles.resultCard, borderLeft: '3px solid #00ff88'}}>
                              <span style={styles.resultLabel}>📈 Dividends ({allocDividends.toLocaleString()} XRP)</span>
                              <span style={styles.resultValue}>${dividendIncome.toLocaleString()}/yr → ${dividendAfterTax.toLocaleString()}/yr</span>
                              <span style={styles.resultSub}>Before → After tax • ${(dividendAfterTax / 12).toLocaleString()}/mo</span>
                            </div>
                          )}
                          
                          {allocStablecoin > 0 && (
                            <div style={{...styles.resultCard, borderLeft: '3px solid #3b82f6'}}>
                              <span style={styles.resultLabel}>💵 Stablecoin Lending ({allocStablecoin.toLocaleString()} XRP)</span>
                              <span style={styles.resultValue}>${stablecoinIncome.toLocaleString()}/yr → ${stablecoinAfterTax.toLocaleString()}/yr</span>
                              <span style={styles.resultSub}>{neverSellInputs.lendingAPY || 8}% APY • After tax: ${(stablecoinAfterTax / 12).toLocaleString()}/mo</span>
                            </div>
                          )}
                          
                          {allocYield > 0 && (
                            <div style={{...styles.resultCard, borderLeft: '3px solid #22c55e'}}>
                              <span style={styles.resultLabel}>🌾 Staking/LP ({allocYield.toLocaleString()} XRP)</span>
                              <span style={styles.resultValue}>${yieldIncome.toLocaleString()}/yr → ${yieldAfterTax.toLocaleString()}/yr</span>
                              <span style={styles.resultSub}>{neverSellInputs.stakingYield || 5}% yield • Keeps XRP exposure</span>
                            </div>
                          )}
                          
                          {allocBorrow > 0 && (
                            <div style={{...styles.resultCard, borderLeft: '3px solid #a78bfa'}}>
                              <span style={styles.resultLabel}>🏦 Borrow ({allocBorrow.toLocaleString()} XRP collateral)</span>
                              <span style={styles.resultValue}>${loanAvailable.toLocaleString()} available</span>
                              <span style={styles.resultSub}>{neverSellInputs.loanToValue}% LTV • ${loanInterest.toLocaleString()}/yr interest • Tax-free!</span>
                            </div>
                          )}
                          
                          {allocHold > 0 && (
                            <div style={{...styles.resultCard, borderLeft: '3px solid #f59e0b'}}>
                              <span style={styles.resultLabel}>💎 HODL ({allocHold.toLocaleString()} XRP)</span>
                              <span style={styles.resultValue}>${holdValue.toLocaleString()}</span>
                              <span style={styles.resultSub}>Future upside potential</span>
                            </div>
                          )}
                          
                          {unallocated > 0 && (
                            <div style={{...styles.resultCard, background: 'rgba(255, 107, 107, 0.1)', border: '1px solid rgba(255, 107, 107, 0.3)'}}>
                              <span style={{...styles.resultLabel, color: '#ff6b6b'}}>⚠️ Unallocated</span>
                              <span style={{...styles.resultValue, color: '#ff6b6b'}}>{unallocated.toLocaleString()} XRP</span>
                            </div>
                          )}
                          
                          {/* Before Tax Total */}
                          <div style={{...styles.resultCard, marginTop: 12, background: 'rgba(100, 116, 139, 0.1)', borderLeft: '3px solid #64748b'}}>
                            <span style={styles.resultLabel}>💰 Total Income (Before Tax)</span>
                            <span style={{...styles.resultValue, color: '#94a3b8'}}>${totalBeforeTax.toLocaleString()}/yr</span>
                            <span style={styles.resultSub}>${(totalBeforeTax / 12).toLocaleString()}/month</span>
                          </div>
                          
                          {/* After Tax Total - Highlighted */}
                          <div style={{...styles.resultCard, ...styles.resultHighlight}}>
                            <span style={styles.resultLabel}>✨ Total Income (After Tax)</span>
                            <span style={styles.resultValueBig}>${totalAfterTax.toLocaleString()}/yr</span>
                            <span style={styles.resultSub}>${(totalAfterTax / 12).toLocaleString()}/month • What you actually keep</span>
                          </div>
                          
                          {totalLoanAccess > 0 && (
                            <div style={styles.resultCard}>
                              <span style={styles.resultLabel}>+ Tax-Free Loan Access</span>
                              <span style={styles.resultValue}>${totalLoanAccess.toLocaleString()}</span>
                              <span style={styles.resultSub}>No taxable event</span>
                            </div>
                          )}
                          
                          <div style={styles.resultCard}>
                            <span style={styles.resultLabel}>XRP Exposure Retained</span>
                            <span style={styles.resultValue}>{totalXRPKept.toLocaleString()} XRP</span>
                            <span style={styles.resultSub}>{((totalXRPKept / neverSellInputs.xrpHoldings) * 100).toFixed(0)}% of holdings</span>
                          </div>
                        </>
                      );
                    })()}
                  </>
                );
              })()}
            </div>
          </div>

          {/* Strategy Explainers */}
          <div style={styles.strategyExplainers}>
            {activeStrategy === 'portfolio' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>🎯 Portfolio Builder</h4>
                <p style={styles.explainerText}>
                  <strong>Diversify your exit strategy.</strong> Instead of going all-in on one approach, 
                  split your XRP across multiple wealth-building strategies. Get income from dividends AND lending, 
                  maintain upside exposure with staking, AND have loan access — all at once.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Enter your total XRP holdings</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Allocate XRP amounts to each strategy</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Adjust rates (yields, LTV, tax) to your situation</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> See combined annual income & loan access</div>
                </div>
                <p style={{...styles.explainerText, marginTop: 12, fontSize: 12, color: '#f59e0b'}}>
                  💡 Tip: Balance income-generating strategies (dividends, lending) with XRP-preserving ones (staking, borrow, HODL) 
                  to maintain upside while securing cash flow.
                </p>
              </div>
            )}

            {activeStrategy === 'dividend' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>📈 The Dividend Strategy</h4>
                <p style={styles.explainerText}>
                  Convert a portion of your XRP gains into high-yield dividend stocks (SCHD, JEPI, O, VYM). 
                  Live off the dividend income while <strong>never touching the principal</strong>. 
                  The remaining XRP stays for future upside.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Sell portion of XRP at target price</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Deploy into 4-5% yield dividend portfolio</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Live on dividends, principal grows forever</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> Pass to heirs with step-up basis</div>
                </div>
              </div>
            )}

            {activeStrategy === 'borrow' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>🏦 Buy, Borrow, Die Strategy</h4>
                <p style={styles.explainerText}>
                  The billionaire playbook: <strong>Never sell appreciated assets.</strong> Instead, borrow against them 
                  at low interest rates. You get cash, pay no capital gains tax, and when you die, 
                  heirs get a stepped-up cost basis — wiping out the tax liability forever.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Hold appreciating assets (XRP)</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Borrow against them (50-70% LTV)</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Use loan proceeds — tax free!</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> Die → heirs inherit at stepped-up basis</div>
                </div>
              </div>
            )}

            {activeStrategy === 'hybrid' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>⚡ Hybrid: Best of Both Worlds</h4>
                <p style={styles.explainerText}>
                  Combine reliable dividend income with the tax-free borrowing strategy. 
                  Dividends cover regular expenses, loans cover large purchases. 
                  Your XRP stays intact for maximum future upside and generational wealth transfer.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Split: 50% to dividends, 50% stays XRP</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Dividends → regular living expenses</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Borrow against XRP → big purchases</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> XRP moons? Borrow more, still never sell</div>
                </div>
              </div>
            )}

            {activeStrategy === 'stablecoin' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>💵 Sell → Lend Stablecoins Strategy</h4>
                <p style={styles.explainerText}>
                  Sell your crypto, convert to stablecoins (USDC, USDT), and lend them on platforms like 
                  Nexo, Aave, or Crypto.com for <strong>6-12% annual yields</strong>. No more price volatility — 
                  predictable income from stable assets.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Sell XRP at target price (pay cap gains)</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Convert to USDC/USDT stablecoins</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Deposit on lending platforms (Nexo, Aave)</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> Earn 6-12% APY, reinvest or withdraw</div>
                </div>
              </div>
            )}

            {activeStrategy === 'yield' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>🌾 Staking & Liquidity Providing</h4>
                <p style={styles.explainerText}>
                  Keep your XRP and earn yield by staking on exchanges (simple, 1-3% APY) or providing 
                  liquidity to AMMs (higher yields, but risk of impermanent loss). 
                  <strong>You keep your position</strong> while earning passive income.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Choose: Exchange staking (simple) or AMM LP (higher yield)</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Exchange: Deposit XRP, earn 1-3% APY</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> AMM: Provide XRP + USDC pair, earn fees + rewards</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> ⚠️ AMM has impermanent loss risk if prices move</div>
                </div>
              </div>
            )}

            {activeStrategy === 'insurance' && (
              <div style={styles.explainerCard}>
                <h4 style={styles.explainerTitle}>🛡️ Infinite Banking / Life Insurance</h4>
                <p style={styles.explainerText}>
                  Use crypto gains to fund a <strong>whole life or IUL policy</strong>. Build tax-deferred cash value, 
                  borrow against it tax-free for investments or expenses, and leave a tax-free death benefit 
                  to heirs. Become your own bank.
                </p>
                <div style={styles.explainerSteps}>
                  <div style={styles.step}><span style={styles.stepNum}>1</span> Sell XRP, pay taxes, use proceeds for premiums</div>
                  <div style={styles.step}><span style={styles.stepNum}>2</span> Overfund whole life policy for max cash value</div>
                  <div style={styles.step}><span style={styles.stepNum}>3</span> Cash value grows 4-5% tax-deferred</div>
                  <div style={styles.step}><span style={styles.stepNum}>4</span> Borrow against policy tax-free, invest in assets</div>
                  <div style={styles.step}><span style={styles.stepNum}>5</span> Death benefit passes to heirs tax-free</div>
                </div>
              </div>
            )}
          </div>

          {/* Quick Presets */}
          <div style={styles.presetsSection}>
            <h4 style={styles.presetsTitle}>⚡ Quick Scenarios</h4>
            <div style={styles.presetButtons} className="preset-buttons">
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 100, targetPrice: 50})}
                style={styles.presetButton}
              >
                🦐 100 XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 1000, targetPrice: 50})}
                style={styles.presetButton}
              >
                🐟 1K XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 5000, targetPrice: 75})}
                style={styles.presetButton}
              >
                🐬 5K XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 10000, targetPrice: 100})}
                style={styles.presetButton}
              >
                🦈 10K XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 25000, targetPrice: 150})}
                style={styles.presetButton}
              >
                25K XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 50000, targetPrice: 200})}
                style={styles.presetButton}
              >
                🐳 50K XRP
              </button>
              <button 
                onClick={() => setNeverSellInputs({...neverSellInputs, xrpHoldings: 100000, targetPrice: 350})}
                style={styles.presetButton}
              >
                🐋 100K XRP
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Trading */}
      {selectedView === 'trading' && (
        <section style={{
          fontFamily: "'DM Mono', 'Fira Code', 'Courier New', monospace",
          position: 'relative',
        }}>
          {/* Background grid */}
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `
              linear-gradient(rgba(0,229,192,0.03) 1px, transparent 1px),
              linear-gradient(90deg, rgba(0,229,192,0.03) 1px, transparent 1px)
            `,
            backgroundSize: '40px 40px',
            pointerEvents: 'none',
            zIndex: 0,
          }} />

          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: 28,
            position: 'relative',
            zIndex: 1,
            flexWrap: 'wrap',
            gap: 16,
          }}>
            <div>
              <div style={{
                fontSize: 26,
                fontWeight: 700,
                color: '#e2e8f0',
                letterSpacing: '0.03em',
                display: 'flex',
                alignItems: 'center',
                gap: 12,
              }}>
                <span style={{ color: '#00e5c0', fontSize: 24 }}>⬡</span> Hyperliquid Bot Monitor
              </div>
              <div style={{ fontSize: 14, color: '#64748b', marginTop: 6, letterSpacing: '0.06em' }}>
                Live XMR Perps · Dual Strategy · Auto-refresh 30s
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 13, color: '#64748b', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>Combined Equity</div>
              <div style={{ fontSize: 32, fontWeight: 700, color: '#00e5c0', letterSpacing: '0.02em' }}>{fmtUSD(hlTotalEquity)}</div>
              <button 
                onClick={refreshHLBots}
                style={{
                  marginTop: 10,
                  background: 'transparent',
                  border: '1px solid #00e5c066',
                  color: '#00e5c0',
                  padding: '8px 18px',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 14,
                  letterSpacing: '0.04em',
                  fontFamily: 'inherit',
                  fontWeight: 600,
                }}
              >
                ↺ Refresh
              </button>
              {hlLastUpdated && (
                <div style={{ fontSize: 12, color: '#475569', marginTop: 6 }}>
                  Updated {hlLastUpdated.toLocaleTimeString()}
                </div>
              )}
            </div>
          </div>

          {/* Combined PNL Chart */}
          {hlChartMerged.length > 0 && (
            <div style={{
              background: 'linear-gradient(180deg, #0d1a1a 0%, #0a1212 100%)',
              border: '2px solid #00e5c066',
              borderRadius: 12,
              padding: '28px 28px 20px',
              marginBottom: 28,
              position: 'relative',
              zIndex: 1,
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 28,
              }}>
                <div style={{
                  fontSize: 20,
                  color: '#e2e8f0',
                  letterSpacing: '0.02em',
                  fontWeight: 700,
                }}>Cumulative PNL</div>
                <div style={{ fontSize: 16, color: '#94a3b8' }}>
                  Current: <span style={{ color: '#c084fc', fontWeight: 700, fontSize: 20 }}>
                    {fmtUSD(hlChartMerged[hlChartMerged.length - 1]?.Combined || 0)}
                  </span>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={340}>
                <LineChart data={hlChartMerged} margin={{ top: 10, right: 30, bottom: 10, left: 15 }}>
                  <CartesianGrid 
                    strokeDasharray="0" 
                    stroke="#2a4545" 
                    strokeOpacity={0.6}
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="date" 
                    tick={{ fill: '#94a3b8', fontSize: 13, fontWeight: 500 }} 
                    axisLine={{ stroke: '#2a4545' }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis 
                    tick={{ fill: '#94a3b8', fontSize: 14, fontWeight: 500 }} 
                    tickFormatter={(v) => `$${v}`}
                    axisLine={false}
                    tickLine={false}
                    dx={-8}
                  />
                  <Tooltip 
                    contentStyle={{
                      background: 'rgba(13, 26, 26, 0.98)',
                      border: '1px solid #3a5a5a',
                      borderRadius: 10,
                      padding: '16px 20px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                    }}
                    labelStyle={{ color: '#e2e8f0', marginBottom: 12, fontSize: 15, fontWeight: 600 }}
                    formatter={(value, name) => {
                      const color = name === 'Combined' ? '#c084fc' : 
                                    name.includes('LONG') ? '#34d399' : '#fbbf24';
                      return [
                        <span style={{ color, fontWeight: 700, fontSize: 16 }}>{fmtUSD(value)}</span>,
                        <span style={{ color: '#cbd5e1' }}>{name}</span>
                      ];
                    }}
                  />
                  <Legend 
                    wrapperStyle={{ paddingTop: 24 }}
                    formatter={(value) => <span style={{ color: '#cbd5e1', fontSize: 14, fontWeight: 500 }}>{value}</span>}
                  />
                  <Line
                    type="monotone"
                    dataKey="Combined"
                    stroke="#c084fc"
                    strokeWidth={4}
                    dot={false}
                    connectNulls
                    name="Combined"
                  />
                  <Line
                    type="monotone"
                    dataKey={HYPERLIQUID_BOTS[0].label}
                    stroke="#34d399"
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls
                  />
                  <Line
                    type="monotone"
                    dataKey={HYPERLIQUID_BOTS[1].label}
                    stroke="#fbbf24"
                    strokeWidth={2.5}
                    dot={false}
                    connectNulls
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Monthly Returns Chart */}
          {hlMonthlyData.length > 0 && (
            <div style={{
              background: 'linear-gradient(180deg, #0d1a1a 0%, #0a1212 100%)',
              border: '2px solid #00e5c066',
              borderRadius: 12,
              padding: '28px 28px 20px',
              marginBottom: 28,
              position: 'relative',
              zIndex: 1,
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: 28,
                flexWrap: 'wrap',
                gap: 20,
              }}>
                <div>
                  <div style={{
                    fontSize: 20,
                    color: '#e2e8f0',
                    letterSpacing: '0.02em',
                    fontWeight: 700,
                    marginBottom: 8,
                  }}>Monthly Returns</div>
                  <div style={{ fontSize: 15, color: '#94a3b8' }}>
                    {(() => {
                      const totalGain = hlMonthlyData.reduce((sum, m) => sum + m.gain, 0);
                      const avgPct = hlMonthlyData.reduce((sum, m) => sum + m.pct, 0) / hlMonthlyData.length;
                      return (
                        <>
                          Total: <span style={{ color: totalGain >= 0 ? '#34d399' : '#f87171', fontWeight: 700, fontSize: 17 }}>{fmtUSD(totalGain)}</span>
                          <span style={{ margin: '0 12px', color: '#475569' }}>•</span>
                          Avg: <span style={{ color: avgPct >= 0 ? '#34d399' : '#f87171', fontWeight: 700, fontSize: 17 }}>{avgPct >= 0 ? '+' : ''}{avgPct.toFixed(1)}%</span>/mo
                        </>
                      );
                    })()}
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <label style={{ fontSize: 14, color: '#94a3b8', fontWeight: 500 }}>Starting Capital:</label>
                  <input
                    type="number"
                    value={hlStartingCapital}
                    onChange={(e) => setHlStartingCapital(parseFloat(e.target.value) || 1000)}
                    style={{
                      background: '#0d1a1a',
                      border: '1px solid #3a5a5a',
                      borderRadius: 8,
                      padding: '10px 14px',
                      color: '#e2e8f0',
                      fontSize: 16,
                      width: 110,
                      fontFamily: 'inherit',
                      fontWeight: 600,
                    }}
                  />
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={hlMonthlyData} margin={{ top: 10, right: 30, bottom: 10, left: 15 }} barCategoryGap="20%">
                  <CartesianGrid 
                    strokeDasharray="0" 
                    stroke="#2a4545" 
                    strokeOpacity={0.6}
                    vertical={false}
                  />
                  <XAxis 
                    dataKey="month" 
                    tick={{ fill: '#94a3b8', fontSize: 14, fontWeight: 500 }}
                    axisLine={{ stroke: '#2a4545' }}
                    tickLine={false}
                    dy={10}
                  />
                  <YAxis 
                    tick={{ fill: '#94a3b8', fontSize: 14, fontWeight: 500 }} 
                    tickFormatter={(v) => `${v}%`}
                    axisLine={false}
                    tickLine={false}
                    dx={-8}
                  />
                  <ReferenceLine y={0} stroke="#5a7a7a" strokeWidth={2} />
                  <Tooltip
                    cursor={false}
                    contentStyle={{
                      background: 'rgba(13, 26, 26, 0.98)',
                      border: '1px solid #3a5a5a',
                      borderRadius: 10,
                      padding: '16px 20px',
                      boxShadow: '0 10px 40px rgba(0,0,0,0.5)',
                    }}
                    labelStyle={{ color: '#e2e8f0', marginBottom: 10, fontSize: 16, fontWeight: 600 }}
                    formatter={(value, name, props) => {
                      const gain = props.payload.gain;
                      const isPartial = props.payload.isPartial;
                      const color = value >= 0 ? '#34d399' : '#f87171';
                      return [
                        <div style={{ lineHeight: 1.7 }}>
                          <div style={{ color, fontWeight: 700, fontSize: 24 }}>{value >= 0 ? '+' : ''}{value}%</div>
                          <div style={{ color: '#cbd5e1', fontSize: 15 }}>{fmtUSD(gain)} profit</div>
                          {isPartial && <div style={{ color: '#f59e0b', fontSize: 12, marginTop: 4 }}>⚡ Started Feb 9th</div>}
                        </div>,
                        ''
                      ];
                    }}
                  />
                  <Bar dataKey="pct" radius={[8, 8, 0, 0]}>
                    {hlMonthlyData.map((entry, index) => (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={entry.pct >= 0 ? '#34d399' : '#f87171'} 
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              
              {/* February partial month note */}
              <div style={{
                textAlign: 'center',
                fontSize: 11,
                color: '#f59e0b',
                marginTop: 8,
                padding: '6px 12px',
                background: 'rgba(245, 158, 11, 0.1)',
                borderRadius: 6,
                display: 'inline-block',
                marginLeft: '50%',
                transform: 'translateX(-50%)',
              }}>
                ⚡ February: Started trading on the 9th (partial month)
              </div>
            </div>
          )}

          {/* Side-by-side bot panels */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(500px, 1fr))',
            gap: 24,
            position: 'relative',
            zIndex: 1,
          }}>
            {HYPERLIQUID_BOTS.map((bot) => (
              <BotPanel
                key={bot.id}
                bot={bot}
                data={hlBotData[bot.id]}
                fills={hlBotFills[bot.id]}
                loading={hlLoading[bot.id]}
                error={hlErrors[bot.id]}
              />
            ))}
          </div>

          <div style={{
            marginTop: 32,
            textAlign: 'center',
            fontSize: 10,
            color: '#1e293b',
            letterSpacing: '0.08em',
            position: 'relative',
            zIndex: 1,
          }}>
            Data sourced directly from Hyperliquid Info API · Read-only · No credentials stored
          </div>
          
          {/* Setup Guide Section */}
          <div style={{
            marginTop: 40,
            padding: 24,
            background: 'linear-gradient(135deg, rgba(0, 207, 255, 0.05) 0%, rgba(0, 40, 60, 0.3) 100%)',
            borderRadius: 16,
            border: '2px solid rgba(0, 207, 255, 0.3)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Decorative grid pattern */}
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: 'linear-gradient(rgba(0,207,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,207,255,0.03) 1px, transparent 1px)',
              backgroundSize: '20px 20px',
              pointerEvents: 'none',
            }} />
            
            <div style={{ position: 'relative', textAlign: 'center' }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 10,
                marginBottom: 16,
              }}>
                <span style={{ fontSize: 24 }}>🛠️</span>
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#00CFFF',
                  margin: 0,
                  letterSpacing: '0.05em',
                }}>
                  Setup Guide
                </h3>
              </div>
              
              <div style={{
                display: 'inline-block',
                padding: '12px 28px',
                background: 'rgba(0, 207, 255, 0.1)',
                borderRadius: 30,
                border: '1px dashed rgba(0, 207, 255, 0.3)',
              }}>
                <span style={{
                  fontSize: 14,
                  color: '#5a7a9a',
                  fontWeight: 500,
                  letterSpacing: '0.1em',
                }}>
                  🚧 Coming Soon 🚧
                </span>
              </div>
              
              <p style={{
                marginTop: 16,
                fontSize: 12,
                color: '#4a5a6a',
                maxWidth: 400,
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: 1.6,
              }}>
                Step-by-step instructions for setting up your own automated trading bots using TradingView webhooks and Pine Script strategies.
              </p>
            </div>
          </div>

          {/* TradingView Strategies - Premium Section */}
          <div style={{
            marginTop: 32,
            padding: 28,
            background: 'linear-gradient(135deg, rgba(0, 229, 192, 0.08) 0%, rgba(0, 50, 80, 0.4) 100%)',
            borderRadius: 16,
            border: '2px solid rgba(0, 229, 192, 0.3)',
            position: 'relative',
            overflow: 'hidden',
          }}>
            {/* Background glow effects */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 300,
              height: 300,
              background: 'radial-gradient(circle, rgba(0,229,192,0.15) 0%, transparent 70%)',
              pointerEvents: 'none',
            }} />
            
            <div style={{ position: 'relative', textAlign: 'center' }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                marginBottom: 8,
              }}>
                <h3 style={{
                  fontSize: 22,
                  fontWeight: 700,
                  color: '#00e5c0',
                  margin: 0,
                  letterSpacing: '0.03em',
                }}>
                  📊 TradingView Strategies
                </h3>
                <span style={{
                  padding: '4px 12px',
                  background: 'rgba(245, 158, 11, 0.2)',
                  border: '1px solid rgba(245, 158, 11, 0.4)',
                  borderRadius: 20,
                  fontSize: 11,
                  color: '#f59e0b',
                  fontWeight: 600,
                  letterSpacing: '0.05em',
                }}>
                  🔒 PREMIUM
                </span>
              </div>
              
              <p style={{
                fontSize: 14,
                color: '#94a3b8',
                marginBottom: 24,
                maxWidth: 500,
                marginLeft: 'auto',
                marginRight: 'auto',
                lineHeight: 1.6,
              }}>
                Get access to my exact Pine Script strategies powering the bots above. Includes the Madrid Ribbon DCA system with all configurations.
              </p>
              
              {/* Vault Buy Button - Centered (DISABLED) */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                marginBottom: 24,
              }}>
                <div
                  style={{
                    width: 140,
                    height: 140,
                    borderRadius: '50%',
                    background: 'linear-gradient(145deg, #1a2a2a 0%, #0a1515 100%)',
                    border: '3px solid rgba(100, 100, 100, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 20px rgba(100, 100, 100, 0.15), inset 0 0 20px rgba(0, 0, 0, 0.3)',
                    position: 'relative',
                    opacity: 0.6,
                  }}
                >
                  <RippleLogo size={90} />
                </div>
                
                <div style={{
                  marginTop: 16,
                  fontSize: 13,
                  color: '#6b7a8a',
                  fontWeight: 600,
                  letterSpacing: '0.08em',
                }}>
                  COMING SOON
                </div>
                
                <div style={{
                  marginTop: 8,
                  fontSize: 20,
                  fontWeight: 700,
                  color: '#6b7a8a',
                }}>
                  Price TBD
                </div>
                
                <div style={{
                  marginTop: 6,
                  fontSize: 11,
                  color: '#4a5a6a',
                  letterSpacing: '0.05em',
                }}>
                  RLUSD · USDC · USDT · PYUSD
                </div>
              </div>
              
              {/* Alternative unlock option */}
              <div style={{
                padding: '12px 20px',
                background: 'rgba(131, 58, 180, 0.15)',
                border: '1px solid rgba(131, 58, 180, 0.3)',
                borderRadius: 10,
                display: 'inline-block',
              }}>
                <span style={{ fontSize: 12, color: '#6b7a8a' }}>or unlock via </span>
                <span style={{ fontSize: 13, color: '#a78bfa', fontWeight: 600 }}>📺 TradingView Username</span>
              </div>
              
              <p style={{
                marginTop: 16,
                fontSize: 11,
                color: '#4a5a6a',
              }}>
                Contact via email or Discord to purchase. Scripts shared to your TradingView account or available for download.
              </p>
            </div>
          </div>

          {/* ChartPrime Affiliate Section */}
          <a 
            href="https://chartprime.com/?rfsn=9027980.dc0db5" 
            target="_blank" 
            rel="noopener noreferrer"
            style={{
              display: 'block',
              marginTop: 32,
              padding: 28,
              background: 'linear-gradient(135deg, #1a0a2e 0%, #2d1b4e 50%, #1a0a2e 100%)',
              borderRadius: 16,
              border: '2px solid rgba(131, 58, 180, 0.4)',
              textDecoration: 'none',
              position: 'relative',
              overflow: 'hidden',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
          >
            {/* Purple glow effect */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '150%',
              height: '150%',
              background: 'radial-gradient(ellipse at center, rgba(131, 58, 180, 0.15) 0%, transparent 60%)',
              pointerEvents: 'none',
            }} />
            
            <div style={{ position: 'relative', textAlign: 'center' }}>
              {/* ChartPrime Logo/Title */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                marginBottom: 16,
              }}>
                <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
                  <path d="M8 32L16 18L24 26L32 8" stroke="#f59e0b" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 32L16 18L24 26L32 8" stroke="url(#chartGrad)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                  <defs>
                    <linearGradient id="chartGrad" x1="8" y1="32" x2="32" y2="8">
                      <stop offset="0%" stopColor="#f59e0b"/>
                      <stop offset="100%" stopColor="#ffffff"/>
                    </linearGradient>
                  </defs>
                </svg>
                <span style={{
                  fontSize: 28,
                  fontWeight: 700,
                  letterSpacing: '0.02em',
                }}>
                  <span style={{ color: '#f59e0b' }}>CHART</span>
                  <span style={{ color: '#ffffff' }}>PRIME</span>
                </span>
              </div>
              
              <h3 style={{
                fontSize: 26,
                fontWeight: 800,
                color: '#ffffff',
                margin: '0 0 8px 0',
                letterSpacing: '-0.01em',
              }}>
                Best-in-class trading indicators
              </h3>
              
              <p style={{
                fontSize: 14,
                color: '#a0a0b0',
                marginBottom: 20,
                maxWidth: 450,
                marginLeft: 'auto',
                marginRight: 'auto',
              }}>
                Designed by top-tier developers and world class traders to help you trade smarter.
              </p>
              
              {/* Stats */}
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                gap: 40,
                marginBottom: 24,
                flexWrap: 'wrap',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>👥</span>
                  <span style={{ color: '#ffffff', fontWeight: 600 }}>100,000+ active users</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontSize: 18 }}>⭐</span>
                  <span style={{ color: '#ffffff', fontWeight: 600 }}>50+ premium features</span>
                </div>
              </div>
              
              {/* CTA Button */}
              <div style={{
                display: 'inline-block',
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #2d1b4e 100%)',
                border: '2px solid rgba(245, 158, 11, 0.6)',
                borderRadius: 30,
                color: '#ffffff',
                fontWeight: 600,
                fontSize: 15,
                letterSpacing: '0.02em',
              }}>
                Try risk-free for 7 days →
              </div>
              
              {/* Trust badges */}
              <div style={{
                marginTop: 20,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}>
                  <span style={{ color: '#f59e0b' }}>★★★★★</span>
                  <span style={{ color: '#8080a0', fontSize: 12 }}>4.8 star user rating</span>
                </div>
              </div>
              
              {/* Affiliate badge */}
              <div style={{
                marginTop: 16,
                fontSize: 10,
                color: '#606080',
                letterSpacing: '0.05em',
              }}>
                AFFILIATE LINK
              </div>
            </div>
          </a>
        </section>
      )}

      {/* Learn */}
      {selectedView === 'learn' && (
        <section style={styles.learnSection}>
          {/* Theories Section */}
          <div style={styles.theoriesSection}>
            <div style={styles.theoriesHeader}>
              <div>
                <h2 style={styles.sectionTitle}>🧠 XRP Theories</h2>
                <p style={styles.sectionSubtitle}>Popular investment theses from Jake Claver (Digital Ascension Group)</p>
              </div>
              <a href="https://x.com/beyond_broke" target="_blank" rel="noopener noreferrer" style={styles.theoriesFollowLink}>
                @beyond_broke ↗
              </a>
            </div>
            
            {/* Theory Progress Summary */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: 16,
              padding: '12px 16px',
              background: 'rgba(0, 207, 255, 0.05)',
              borderRadius: 8,
              marginBottom: 20,
              border: '1px solid rgba(0, 207, 255, 0.1)',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}>
                <span style={{ fontSize: 24 }}>📊</span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>
                    Theory Tracker
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7a8a' }}>
                    Check off theories as they occur
                  </div>
                </div>
              </div>
              <div style={{ marginLeft: 'auto', display: 'flex', gap: 16 }}>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#00ff88' }}>
                    {Object.values(theoryStatus).filter(s => s.occurred).length}
                  </div>
                  <div style={{ fontSize: 11, color: '#00ff88' }}>Occurred</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#f59e0b' }}>
                    {claverTheories.length - Object.values(theoryStatus).filter(s => s.occurred).length}
                  </div>
                  <div style={{ fontSize: 11, color: '#f59e0b' }}>Pending</div>
                </div>
                <div style={{ textAlign: 'center' }}>
                  <div style={{ fontSize: 20, fontWeight: 700, color: '#00CFFF' }}>
                    {claverTheories.length}
                  </div>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Total</div>
                </div>
              </div>
            </div>
            
            {/* Constellation Star Map - Interactive */}
            {(() => {
              const starPositions = [
                { x: 8, y: 35 },   // 1 - Supply Shock
                { x: 22, y: 60 },  // 2 - Crisis
                { x: 38, y: 30 },  // 3 - Liquidity
                { x: 50, y: 55 },  // 4 - ISO (center)
                { x: 62, y: 35 },  // 5 - NDA
                { x: 76, y: 60 },  // 6 - Carry Trade
                { x: 88, y: 45 },  // 7 - Domino (leads to rocket)
              ];
              const occurredCount = Object.values(theoryStatus).filter(s => s.occurred).length;
              const allOccurred = occurredCount === claverTheories.length;
              
              return (
                <div style={{
                  background: 'radial-gradient(ellipse at center, #0a1628 0%, #050a14 50%, #020408 100%)',
                  borderRadius: 16,
                  padding: '20px 16px',
                  marginBottom: 24,
                  border: '1px solid rgba(100, 150, 200, 0.2)',
                  position: 'relative',
                  overflow: 'hidden',
                  minHeight: 280,
                }}>
                  {/* Twinkling stars background */}
                  <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
                    {[...Array(40)].map((_, i) => (
                      <div key={i} style={{
                        position: 'absolute',
                        width: Math.random() * 2 + 1,
                        height: Math.random() * 2 + 1,
                        background: '#fff',
                        borderRadius: '50%',
                        left: `${Math.random() * 100}%`,
                        top: `${Math.random() * 100}%`,
                        opacity: Math.random() * 0.4 + 0.1,
                        animation: `twinkle ${Math.random() * 3 + 2}s infinite`,
                      }} />
                    ))}
                  </div>
                  
                  {/* Header */}
                  <h4 style={{
                    fontSize: 14,
                    fontWeight: 300,
                    color: '#fff',
                    marginBottom: 8,
                    textAlign: 'center',
                    letterSpacing: 2,
                    position: 'relative',
                  }}>
                    <span style={{ opacity: 0.6 }}>✦</span> XRP Theory Constellation <span style={{ opacity: 0.6 }}>✦</span>
                  </h4>
                  <p style={{ color: '#5a7a9a', fontSize: 11, margin: '0 0 16px 0', textAlign: 'center', fontStyle: 'italic' }}>
                    Click stars to track catalysts • All aligned = Liftoff 🚀
                  </p>
                  
                  {/* Constellation Map */}
                  <div style={{ position: 'relative', height: 180 }}>
                    {/* SVG Lines connecting stars */}
                    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
                      {starPositions.map((pos, i) => {
                        if (i === starPositions.length - 1) return null;
                        const next = starPositions[i + 1];
                        const theory1 = claverTheories[i];
                        const theory2 = claverTheories[i + 1];
                        const status1 = theoryStatus[theory1?.id] || { occurred: false };
                        const status2 = theoryStatus[theory2?.id] || { occurred: false };
                        const bothOccurred = status1.occurred && status2.occurred;
                        return (
                          <line
                            key={i}
                            x1={`${pos.x}%`}
                            y1={`${pos.y}%`}
                            x2={`${next.x}%`}
                            y2={`${next.y}%`}
                            stroke={bothOccurred ? '#00ff88' : 'rgba(100,150,200,0.25)'}
                            strokeWidth={bothOccurred ? 2 : 1}
                            strokeDasharray={bothOccurred ? 'none' : '4,4'}
                            style={{ transition: 'all 0.5s ease' }}
                          />
                        );
                      })}
                      {/* Line to rocket */}
                      <line
                        x1={`${starPositions[6].x}%`}
                        y1={`${starPositions[6].y}%`}
                        x2="96%"
                        y2="45%"
                        stroke={allOccurred ? '#00ff88' : 'rgba(100,150,200,0.25)'}
                        strokeWidth={allOccurred ? 2 : 1}
                        strokeDasharray={allOccurred ? 'none' : '4,4'}
                      />
                    </svg>
                    
                    {/* Interactive Stars */}
                    {claverTheories.slice(0, 7).map((theory, index) => {
                      const pos = starPositions[index];
                      const status = theoryStatus[theory.id] || { occurred: false };
                      return (
                        <div
                          key={theory.id}
                          onClick={() => toggleTheoryOccurred(theory.id)}
                          style={{
                            position: 'absolute',
                            left: `${pos.x}%`,
                            top: `${pos.y}%`,
                            transform: 'translate(-50%, -50%)',
                            cursor: 'pointer',
                            zIndex: 2,
                          }}
                        >
                          {/* Star glow */}
                          <div style={{
                            position: 'absolute',
                            inset: -12,
                            background: status.occurred 
                              ? 'radial-gradient(circle, rgba(0,255,136,0.5) 0%, transparent 70%)'
                              : 'radial-gradient(circle, rgba(100,150,200,0.2) 0%, transparent 70%)',
                            borderRadius: '50%',
                            transition: 'all 0.3s ease',
                          }} />
                          
                          {/* Star */}
                          <div style={{
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            background: status.occurred
                              ? 'radial-gradient(circle, #00ff88 0%, #00aa55 100%)'
                              : 'radial-gradient(circle, #5a7a9a 0%, #3a4a5a 100%)',
                            border: `2px solid ${status.occurred ? '#00ff88' : '#5a7a9a'}`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 14,
                            boxShadow: status.occurred 
                              ? '0 0 20px rgba(0,255,136,0.6)' 
                              : '0 0 10px rgba(100,150,200,0.3)',
                            transition: 'all 0.3s ease',
                          }}>
                            {theory.emoji}
                          </div>
                          
                          {/* Label */}
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            marginTop: 4,
                            fontSize: 8,
                            color: status.occurred ? '#00ff88' : '#5a7a9a',
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: 0.3,
                          }}>
                            {theory.title.split(' ')[0]}
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Rocket destination */}
                    <div style={{
                      position: 'absolute',
                      right: '1%',
                      top: '45%',
                      transform: 'translateY(-50%)',
                      fontSize: 32,
                      filter: allOccurred ? 'drop-shadow(0 0 20px #ff6600)' : 'grayscale(0.5) opacity(0.4)',
                      transition: 'all 0.5s ease',
                      animation: allOccurred ? 'rocketShake 0.3s infinite' : 'none',
                    }}>
                      🚀
                    </div>
                    
                    {/* Stats overlay */}
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      right: 8,
                      display: 'flex',
                      gap: 12,
                      background: 'rgba(0,0,0,0.5)',
                      padding: '6px 12px',
                      borderRadius: 16,
                      backdropFilter: 'blur(10px)',
                    }}>
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#00ff88' }}>{occurredCount}</div>
                        <div style={{ fontSize: 8, color: '#00ff88', textTransform: 'uppercase' }}>Aligned</div>
                      </div>
                      <div style={{ width: 1, background: 'rgba(255,255,255,0.1)' }} />
                      <div style={{ textAlign: 'center' }}>
                        <div style={{ fontSize: 16, fontWeight: 700, color: '#f59e0b' }}>{claverTheories.length - occurredCount}</div>
                        <div style={{ fontSize: 8, color: '#f59e0b', textTransform: 'uppercase' }}>Pending</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* All aligned celebration */}
                  {allOccurred && (
                    <div style={{
                      marginTop: 12,
                      padding: '10px 20px',
                      background: 'linear-gradient(135deg, rgba(0,255,136,0.2), rgba(167,139,250,0.2))',
                      borderRadius: 20,
                      border: '1px solid rgba(0,255,136,0.4)',
                      textAlign: 'center',
                    }}>
                      <span style={{ color: '#00ff88', fontWeight: 600, fontSize: 13 }}>
                        ✨ The stars have aligned! All catalysts confirmed ✨
                      </span>
                    </div>
                  )}
                </div>
              );
            })()}
            <div style={styles.theoriesGrid} className="theory-grid">
              {claverTheories.map((theory) => {
                const status = theoryStatus[theory.id] || { occurred: false, date: '' };
                return (
                <div key={theory.id} style={{
                  ...styles.theoryCard,
                  borderColor: status.occurred ? '#00ff8840' : 'rgba(0, 207, 255, 0.2)',
                  background: status.occurred ? 'linear-gradient(135deg, #0d1117 0%, #0a1a0a 100%)' : styles.theoryCard.background,
                }}>
                  {/* Occurred Checkbox Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: 12,
                    paddingBottom: 12,
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                  }}>
                    <label style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      cursor: 'pointer',
                      fontSize: 13,
                      color: status.occurred ? '#00ff88' : '#6b7a8a',
                    }}>
                      <input
                        type="checkbox"
                        checked={status.occurred}
                        onChange={() => toggleTheoryOccurred(theory.id)}
                        style={{
                          width: 18,
                          height: 18,
                          accentColor: '#00ff88',
                          cursor: 'pointer',
                        }}
                      />
                      {status.occurred ? '✓ OCCURRED' : 'Mark as Occurred'}
                    </label>
                    {status.occurred && (
                      <input
                        type="text"
                        value={status.date}
                        onChange={(e) => updateTheoryDate(theory.id, e.target.value)}
                        placeholder="Date"
                        style={{
                          background: 'rgba(0,255,136,0.1)',
                          border: '1px solid #00ff8840',
                          borderRadius: 4,
                          padding: '4px 8px',
                          color: '#00ff88',
                          fontSize: 12,
                          width: 90,
                          textAlign: 'center',
                        }}
                      />
                    )}
                  </div>
                  
                  <div style={styles.theoryHeader}>
                    <span style={styles.theoryEmoji}>{theory.emoji}</span>
                    <div style={styles.theoryMeta}>
                      <span style={{
                        ...styles.confidenceBadge,
                        background: status.occurred ? '#00ff8820' :
                                   theory.confidence === 'High' ? '#00ff8820' : 
                                   theory.confidence === 'Medium' ? '#f59e0b20' : '#ff6b9d20',
                        color: status.occurred ? '#00ff88' :
                               theory.confidence === 'High' ? '#00ff88' : 
                               theory.confidence === 'Medium' ? '#f59e0b' : '#ff6b9d',
                      }}>
                        {status.occurred ? '✓ Confirmed' : theory.confidence}
                      </span>
                      <span style={styles.theoryTimeframe}>{theory.timeframe}</span>
                    </div>
                  </div>
                  <h3 style={{
                    ...styles.theoryTitle,
                    color: status.occurred ? '#00ff88' : styles.theoryTitle.color,
                  }}>{theory.title}</h3>
                  <p style={styles.theorySummary}>{theory.summary}</p>
                  <p style={styles.theoryDetails}>{theory.details}</p>
                </div>
              )})}
            </div>
            <div style={styles.theoriesDisclaimer}>
              ⚠️ These are community theories for educational purposes only. Not financial advice. Always DYOR.
            </div>
          </div>

          {/* Global Financial Indicators - Early Warning System */}
          <div style={styles.indicatorsSection}>
            <h3 style={styles.indicatorsSectionTitle}>🚨 Early Warning System</h3>
            <p style={styles.indicatorsSubtitle}>Key metrics to watch for reverse carry trade unwind signals</p>
            
            {/* Warning Thresholds Box */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(255, 107, 0, 0.1) 0%, rgba(255, 59, 59, 0.05) 100%)',
              borderRadius: 12,
              padding: 16,
              marginBottom: 20,
              border: '1px solid rgba(255, 107, 0, 0.3)',
            }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: '#ff6b00', marginBottom: 12 }}>
                📊 What a Full Unwind Scenario Looks Like:
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Oil</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#ff6b00' }}>$110–$140</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>USD/JPY</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#ff6b00' }}>150–165</div>
                </div>
                <div style={{ background: 'rgba(0,0,0,0.3)', padding: 10, borderRadius: 8 }}>
                  <div style={{ fontSize: 11, color: '#6b7a8a' }}>Oil in Yen</div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: '#ff6b00' }}>¥16,000–¥23,000</div>
                </div>
              </div>
              <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 12, lineHeight: 1.5 }}>
                At these levels: Japan's trade deficit explodes → BOJ forced to tighten → Hedge funds unwind yen-funded positions → Yen spikes stronger rapidly
              </div>
            </div>

            <div style={styles.indicatorsGrid}>
              <div style={{...styles.indicatorCard, borderColor: 'rgba(255, 107, 0, 0.4)'}}>
                <span style={styles.indicatorEmoji}>🇯🇵</span>
                <span style={styles.indicatorName}>Japan 10-Year Bond Yield</span>
                <span style={{...styles.indicatorValue, color: '#f59e0b'}}>~1.2%</span>
                <div style={{ fontSize: 10, color: '#6b7a8a', marginTop: 4, lineHeight: 1.6 }}>
                  • Above 2% = significant<br/>
                  • Above 2.5% = carry trade breaks<br/>
                  • Above 3% = crisis
                </div>
              </div>
              <div style={{...styles.indicatorCard, borderColor: 'rgba(0, 207, 255, 0.4)'}}>
                <span style={styles.indicatorEmoji}>💱</span>
                <span style={styles.indicatorName}>USD/JPY Exchange Rate</span>
                <span style={{...styles.indicatorValue, color: '#00CFFF'}}>~156 ¥/$</span>
                <div style={{ fontSize: 10, color: '#6b7a8a', marginTop: 4, lineHeight: 1.6 }}>
                  • Below 140 = major disruption<br/>
                  • Rapid movement = stress<br/>
                  • Extreme volatility = unwinding
                </div>
              </div>
              <div style={{...styles.indicatorCard, borderColor: 'rgba(167, 139, 250, 0.4)'}}>
                <span style={styles.indicatorEmoji}>🏛️</span>
                <span style={styles.indicatorName}>US Treasury Auctions</span>
                <span style={{...styles.indicatorValue, color: '#a78bfa'}}>Weekly</span>
                <div style={{ fontSize: 10, color: '#6b7a8a', marginTop: 4, lineHeight: 1.6 }}>
                  • Weak demand (bid-to-cover &lt;2.0)<br/>
                  • Rising yields = more interest<br/>
                  • Foreign buyers reducing
                </div>
              </div>
              <div style={styles.indicatorCard}>
                <span style={styles.indicatorEmoji}>🛢️</span>
                <span style={styles.indicatorName}>Oil Price (Brent/Dubai)</span>
                <span style={styles.indicatorValue}>~$78/barrel</span>
                <div style={{ fontSize: 10, color: '#6b7a8a', marginTop: 4, lineHeight: 1.6 }}>
                  • $110+ triggers Japan trade deficit stress<br/>
                  • Energy crisis = liquidity event<br/>
                  • Watch geopolitical tensions
                </div>
              </div>
            </div>
            <div style={styles.indicatorsDisclaimer}>
              💡 These values are approximate. Check live data on TradingView or your broker for real-time prices.
            </div>
          </div>

          {/* Articles Section */}
          <div style={styles.learnHeader}>
            <h2 style={styles.sectionTitle}>📚 Educational Articles</h2>
            <p style={styles.sectionSubtitle}>Expert guides and analysis to help you invest smarter</p>
          </div>

          <div style={styles.articleGrid} className="article-grid">
            {educationArticles.map((article, index) => (
              <ArticleCard 
                key={index} 
                article={article} 
                onClick={() => setSelectedArticle(article)}
              />
            ))}
          </div>

          {/* Wealth Strategy Section */}
          <div style={{...styles.learnHeader, marginTop: 40}}>
            <h2 style={styles.sectionTitle}>💰 Wealth Building Strategies</h2>
            <p style={styles.sectionSubtitle}>Exit strategies, yield farming, lending, insurance, and tax optimization — the complete playbook</p>
          </div>

          <div style={styles.articleGrid} className="article-grid">
            {wealthStrategyArticles.map((article, index) => (
              <ArticleCard 
                key={index} 
                article={article} 
                onClick={() => setSelectedArticle(article)}
              />
            ))}
          </div>

          {/* XRPFi DeFi Guides Section */}
          <div style={{...styles.learnHeader, marginTop: 40}}>
            <h2 style={styles.sectionTitle}>🔥 XRPFi How-To Guides</h2>
            <p style={styles.sectionSubtitle}>Step-by-step tutorials for earning yield on your XRP through DeFi protocols</p>
          </div>

          <div style={styles.articleGrid} className="article-grid">
            {defiGuides.map((article, index) => (
              <ArticleCard 
                key={index} 
                article={article} 
                onClick={() => setSelectedArticle(article)}
              />
            ))}
          </div>

          <div style={styles.faqSection}>
            <h3 style={styles.faqTitle}>Frequently Asked Questions</h3>
            <div style={styles.faqGrid}>
              <FAQItem question="What is an XRP ETF?" answer="An XRP ETF is an exchange-traded fund that holds actual XRP tokens and trades on traditional stock exchanges. When you buy shares, you gain exposure to XRP's price without managing crypto wallets or private keys." />
              <FAQItem question="Which XRP ETF has the lowest fees?" answer="Franklin Templeton's XRPZ has the lowest expense ratio at 0.19%, making it the most cost-effective option for long-term holders." />
              <FAQItem question="Can I buy XRP ETFs in my 401k or IRA?" answer="Yes, if your plan offers a self-directed brokerage option (like Vanguard's PCRA), you can purchase XRP ETFs. Check with your plan administrator for availability." />
              <FAQItem question="What does AUM mean?" answer="Assets Under Management (AUM) represents the total market value of XRP held by an ETF. Higher AUM generally indicates more institutional confidence and better liquidity." />
              <FAQItem question="How is the XRP secured?" answer="ETF issuers use institutional custodians like Coinbase Custody, BitGo, and BNY Mellon to secure the underlying XRP in cold storage with insurance coverage." />
              <FAQItem question="What are ETF inflows and outflows?" answer="Inflows occur when investors buy ETF shares, requiring the fund to purchase more XRP. Outflows happen when shares are sold. Net flows indicate overall institutional sentiment." />
              <FAQItem question="What is the 'Buy, Borrow, Die' strategy?" answer="A wealth preservation strategy where you never sell appreciated assets. Instead, you borrow against them (no capital gains tax), live off the loan proceeds, and when you die, your heirs inherit at stepped-up basis — wiping out the tax liability." />
              <FAQItem question="What dividend yield should I expect?" answer="A conservative dividend portfolio (SCHD, VYM, JEPI, O) typically yields 4-5% annually. On $5M, that's $200-250K/year pre-tax, or roughly $130-160K after taxes depending on your bracket." />
              <FAQItem question="What is stepped-up cost basis?" answer="When you die, your heirs inherit assets at their current market value, not your original purchase price. This means all unrealized capital gains disappear — a legal way to pass wealth tax-free." />
            </div>
          </div>
        </section>
      )}

      <footer style={styles.footer}>
        <p style={styles.footerText}>Data for informational purposes only. Not investment advice. DYOR and consult a financial advisor.</p>
        
        {/* Share Section */}
        <div style={{ 
          marginBottom: 20, 
          padding: '16px 24px', 
          background: 'rgba(0, 207, 255, 0.05)', 
          borderRadius: 12,
          border: '1px solid rgba(0, 207, 255, 0.1)',
        }}>
          <p style={{ fontSize: 14, color: '#fff', marginBottom: 12, fontWeight: 600 }}>
            📤 Share XRP Vault with the community
          </p>
          <ShareButtons />
        </div>

        {/* Tip Jar Section */}
        <TipJar />
        
        <div style={{ display: 'flex', gap: 20, justifyContent: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
          <button onClick={() => setModalView('about')} style={styles.footerLink}>About</button>
          <button onClick={() => setModalView('privacy')} style={styles.footerLink}>Privacy Policy</button>
          <button onClick={() => setModalView('terms')} style={styles.footerLink}>Terms of Service</button>
          <a href="mailto:contact@xrp-vault.com" style={styles.footerLink}>Contact</a>
        </div>
        <p style={styles.footerBrand}>
          <RippleLogo size={20} />
          <span style={{ marginLeft: 8 }}>Built for the XRP Community • © 2026 XRP Vault</span>
        </p>
      </footer>

      {/* Legal/Info Modals */}
      {modalView && (
        <div style={styles.modalOverlay} onClick={() => setModalView(null)}>
          <div style={styles.legalModal} onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setModalView(null)} style={styles.modalCloseBtn}>×</button>
            
            {modalView === 'about' && (
              <>
                <h2 style={styles.legalTitle}>About XRP Vault</h2>
                <div style={styles.legalContent}>
                  <p><strong>XRP Vault</strong> is an independent, educational resource for the XRP community. We provide real-time tracking of XRP ETFs, market data, wealth-building strategies, and educational content.</p>
                  <p><strong>Our Mission:</strong> To help XRP holders make informed decisions by providing accurate, up-to-date information about XRP ETFs, institutional adoption, and wealth-building opportunities.</p>
                  <p><strong>What We Do:</strong></p>
                  <ul>
                    <li>Track all 6 US spot XRP ETFs with live pricing</li>
                    <li>Monitor XRP market data from CoinGecko</li>
                    <li>Provide wealth strategy calculators</li>
                    <li>Curate educational content from the XRP community</li>
                  </ul>
                  <p><strong>Disclaimer:</strong> XRP Vault is not affiliated with Ripple Labs, any ETF issuer, or financial institution. We do not provide financial advice. All information is for educational purposes only.</p>
                  <p><strong>Contact:</strong> contact@xrp-vault.com</p>
                </div>
              </>
            )}
            
            {modalView === 'privacy' && (
              <>
                <h2 style={styles.legalTitle}>Privacy Policy</h2>
                <div style={styles.legalContent}>
                  <p><strong>Last Updated:</strong> January 2026</p>
                  <p><strong>Data Collection:</strong> XRP Vault does not collect, store, or process any personal information. We do not:</p>
                  <ul>
                    <li>Require user accounts or registration</li>
                    <li>Store cookies or tracking pixels</li>
                    <li>Collect wallet addresses or financial information</li>
                    <li>Share any data with third parties</li>
                  </ul>
                  <p><strong>Third-Party Services:</strong> Our site fetches public market data from:</p>
                  <ul>
                    <li>CoinGecko API (XRP price data)</li>
                    <li>Polygon.io API (ETF prices, volume data)</li>
                    <li>XRPScan API (wallet balances, escrow data)</li>
                    <li>DefiLlama API (DeFi TVL data)</li>
                  </ul>
                  <p>These services have their own privacy policies. We do not control their data practices.</p>
                  <p><strong>Local Storage:</strong> Any preferences you set are stored locally in your browser only and never transmitted to our servers.</p>
                  <p><strong>Analytics:</strong> We use Firebase Hosting which may collect anonymous usage statistics. No personally identifiable information is collected.</p>
                  <p><strong>Contact:</strong> For privacy questions, email contact@xrp-vault.com</p>
                </div>
              </>
            )}
            
            {modalView === 'terms' && (
              <>
                <h2 style={styles.legalTitle}>Terms of Service</h2>
                <div style={styles.legalContent}>
                  <p><strong>Last Updated:</strong> January 2026</p>
                  <p><strong>1. Acceptance:</strong> By using XRP Vault, you agree to these terms. If you disagree, please do not use the site.</p>
                  <p><strong>2. Educational Purpose Only:</strong> All content on XRP Vault is for informational and educational purposes only. Nothing on this site constitutes:</p>
                  <ul>
                    <li>Financial, investment, or trading advice</li>
                    <li>Recommendation to buy, sell, or hold any asset</li>
                    <li>Legal, tax, or accounting advice</li>
                  </ul>
                  <p><strong>3. No Warranty:</strong> Information is provided "as is" without warranty. We do not guarantee accuracy, completeness, or timeliness of data.</p>
                  <p><strong>4. Risk Disclosure:</strong> Cryptocurrency and ETF investments carry significant risk. You could lose some or all of your investment. Past performance does not guarantee future results.</p>
                  <p><strong>5. Not Financial Advice:</strong> Always do your own research (DYOR) and consult qualified financial advisors before making investment decisions.</p>
                  <p><strong>6. Third-Party Content:</strong> We may link to external resources. We are not responsible for third-party content or services.</p>
                  <p><strong>7. Limitation of Liability:</strong> XRP Vault and its creators shall not be liable for any losses or damages arising from use of this site.</p>
                  <p><strong>8. Changes:</strong> We may update these terms at any time. Continued use constitutes acceptance of changes.</p>
                  <p><strong>Contact:</strong> contact@xrp-vault.com</p>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Article Modal */}
      <ArticleModal article={selectedArticle} onClose={() => setSelectedArticle(null)} />
    </div>
  );
}

// Social Share Buttons Component
function ShareButtons({ compact = false }) {
  const [copied, setCopied] = React.useState(false);
  const [showShareMenu, setShowShareMenu] = React.useState(false);
  
  const siteUrl = 'https://xrp-vault.com';
  const shareText = '🚀 Track all XRP ETFs in one place! Live prices, AUM, whale data, and DeFi yields. Check out XRP Vault:';
  const hashtags = 'XRP,XRPETF,Crypto,Ripple';
  
  const shareToX = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(siteUrl)}&hashtags=${hashtags}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(siteUrl)}&quote=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const shareToLinkedIn = () => {
    const url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(siteUrl)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const shareToReddit = () => {
    const url = `https://reddit.com/submit?url=${encodeURIComponent(siteUrl)}&title=${encodeURIComponent('XRP Vault - Track All XRP ETFs in One Place')}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const shareToTelegram = () => {
    const url = `https://t.me/share/url?url=${encodeURIComponent(siteUrl)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, '_blank', 'width=550,height=420');
  };
  
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(siteUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = siteUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  
  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'XRP Vault - XRP ETF Tracker',
          text: shareText,
          url: siteUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          setShowShareMenu(true);
        }
      }
    } else {
      setShowShareMenu(true);
    }
  };
  
  const buttonStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: compact ? '6px 10px' : '8px 14px',
    borderRadius: 8,
    border: 'none',
    cursor: 'pointer',
    fontSize: compact ? 12 : 13,
    fontWeight: 600,
    transition: 'all 0.2s ease',
  };
  
  if (compact) {
    return (
      <button
        onClick={nativeShare}
        style={{
          ...buttonStyle,
          background: 'rgba(0, 207, 255, 0.15)',
          color: '#00CFFF',
          border: '1px solid rgba(0, 207, 255, 0.3)',
        }}
        onMouseOver={(e) => e.target.style.background = 'rgba(0, 207, 255, 0.25)'}
        onMouseOut={(e) => e.target.style.background = 'rgba(0, 207, 255, 0.15)'}
      >
        📤 Share
      </button>
    );
  }
  
  return (
    <>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
        {/* X/Twitter */}
        <button
          onClick={shareToX}
          style={{
            ...buttonStyle,
            background: '#000',
            color: '#fff',
          }}
          title="Share on X"
        >
          𝕏
        </button>
        
        {/* Facebook */}
        <button
          onClick={shareToFacebook}
          style={{
            ...buttonStyle,
            background: '#1877F2',
            color: '#fff',
          }}
          title="Share on Facebook"
        >
          f
        </button>
        
        {/* Reddit */}
        <button
          onClick={shareToReddit}
          style={{
            ...buttonStyle,
            background: '#FF4500',
            color: '#fff',
          }}
          title="Share on Reddit"
        >
          ↗
        </button>
        
        {/* Telegram */}
        <button
          onClick={shareToTelegram}
          style={{
            ...buttonStyle,
            background: '#0088cc',
            color: '#fff',
          }}
          title="Share on Telegram"
        >
          ✈
        </button>
        
        {/* Copy Link */}
        <button
          onClick={copyLink}
          style={{
            ...buttonStyle,
            background: copied ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 255, 255, 0.1)',
            color: copied ? '#00ff88' : '#fff',
            border: `1px solid ${copied ? '#00ff88' : 'rgba(255, 255, 255, 0.2)'}`,
          }}
          title="Copy Link"
        >
          {copied ? '✓ Copied!' : '🔗 Copy'}
        </button>
        
        {/* Native Share (mobile) */}
        {navigator.share && (
          <button
            onClick={nativeShare}
            style={{
              ...buttonStyle,
              background: 'linear-gradient(135deg, #00CFFF 0%, #a78bfa 100%)',
              color: '#fff',
            }}
            title="More sharing options"
          >
            📤 More
          </button>
        )}
      </div>
      
      {/* Share Menu Modal */}
      {showShareMenu && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowShareMenu(false)}
        >
          <div 
            style={{
              background: 'linear-gradient(145deg, #1a2332 0%, #0d1117 100%)',
              borderRadius: 16,
              padding: 24,
              maxWidth: 400,
              width: '90%',
              border: '1px solid rgba(0, 207, 255, 0.2)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 16 }}>
              📤 Share XRP Vault
            </h3>
            <p style={{ fontSize: 13, color: '#9ca3af', marginBottom: 20 }}>
              Share with fellow XRP holders!
            </p>
            
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
              <button onClick={shareToX} style={{ ...buttonStyle, background: '#000', color: '#fff', width: '100%' }}>
                𝕏 Twitter/X
              </button>
              <button onClick={shareToFacebook} style={{ ...buttonStyle, background: '#1877F2', color: '#fff', width: '100%' }}>
                Facebook
              </button>
              <button onClick={shareToReddit} style={{ ...buttonStyle, background: '#FF4500', color: '#fff', width: '100%' }}>
                Reddit
              </button>
              <button onClick={shareToTelegram} style={{ ...buttonStyle, background: '#0088cc', color: '#fff', width: '100%' }}>
                Telegram
              </button>
              <button onClick={shareToLinkedIn} style={{ ...buttonStyle, background: '#0A66C2', color: '#fff', width: '100%' }}>
                LinkedIn
              </button>
              <button onClick={copyLink} style={{ ...buttonStyle, background: copied ? '#00ff88' : '#374151', color: copied ? '#000' : '#fff', width: '100%' }}>
                {copied ? '✓ Copied!' : '🔗 Copy Link'}
              </button>
            </div>
            
            <p style={{ fontSize: 11, color: '#6b7a8a', marginTop: 16, textAlign: 'center' }}>
              📸 Instagram: Copy link and share to your Story!
            </p>
            
            <button 
              onClick={() => setShowShareMenu(false)}
              style={{
                marginTop: 16,
                width: '100%',
                padding: '10px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: 8,
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </>
  );
}

// Tip Jar Component
function TipJar() {
  const [copiedAddress, setCopiedAddress] = React.useState(null);
  const [showQR, setShowQR] = React.useState(null);
  
  const tipAddresses = [
    { 
      symbol: 'XRP', 
      name: 'XRP', 
      address: 'rH9VCsymw7oJbCtgCGhwJqqUP3zyZEYag',
      network: 'XRPL',
      color: '#00cfff',
      icon: '✕'
    },
    { 
      symbol: 'RLUSD', 
      name: 'RLUSD', 
      address: 'rH9VCsymw7oJbCtgCGhwJqqUP3zyZEYag',
      network: 'XRPL',
      color: '#00cfff',
      icon: '💵'
    },
    { 
      symbol: 'USDC', 
      name: 'USDC', 
      address: '0xeba3ddb799198e47f133184bea3d4cd33724f9a6',
      network: 'Ethereum',
      color: '#2775ca',
      icon: '🔵'
    },
    { 
      symbol: 'USDT', 
      name: 'Tether', 
      address: '0xeba3ddb799198e47f133184bea3d4cd33724f9a6',
      network: 'Ethereum',
      color: '#26a17b',
      icon: '💲'
    },
    { 
      symbol: 'PYUSD', 
      name: 'PayPal USD', 
      address: '0xeba3ddb799198e47f133184bea3d4cd33724f9a6',
      network: 'Ethereum',
      color: '#003087',
      icon: '🅿️'
    },
  ];
  
  const copyAddress = async (symbol, address) => {
    try {
      await navigator.clipboard.writeText(address);
      setCopiedAddress(symbol);
      setTimeout(() => setCopiedAddress(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = address;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedAddress(symbol);
      setTimeout(() => setCopiedAddress(null), 2000);
    }
  };
  
  return (
    <div style={{ 
      marginBottom: 20, 
      padding: '16px 24px', 
      background: 'linear-gradient(135deg, rgba(0, 207, 255, 0.08) 0%, rgba(0, 150, 255, 0.05) 100%)', 
      borderRadius: 12,
      border: '1px solid rgba(0, 207, 255, 0.15)',
    }}>
      <p style={{ fontSize: 14, color: '#fff', marginBottom: 12, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 18 }}>☕</span> Support XRP Vault
      </p>
      <p style={{ fontSize: 12, color: '#8b9caa', marginBottom: 16 }}>
        Help keep this resource free. Tips go toward data API costs and development.
      </p>
      
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {tipAddresses.map((tip) => (
          <button
            key={tip.symbol}
            onClick={() => copyAddress(tip.symbol, tip.address)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              background: copiedAddress === tip.symbol ? 'rgba(0, 255, 136, 0.2)' : 'rgba(255, 255, 255, 0.05)',
              border: `1px solid ${copiedAddress === tip.symbol ? '#00ff88' : 'rgba(255, 255, 255, 0.1)'}`,
              borderRadius: 8,
              color: copiedAddress === tip.symbol ? '#00ff88' : '#fff',
              fontSize: 12,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            title={`${tip.network}: ${tip.address}`}
          >
            <span>{tip.icon}</span>
            <span>{copiedAddress === tip.symbol ? 'Copied!' : tip.symbol}</span>
          </button>
        ))}
      </div>
      
      <div style={{ marginTop: 12, fontSize: 10, color: '#6b7a8a' }}>
        <span style={{ marginRight: 12 }}>XRPL: XRP, RLUSD</span>
        <span>Ethereum: USDC, USDT, PYUSD</span>
      </div>
    </div>
  );
}

function StatCard({ label, value, subValue, positive, highlight, isLive }) {
  return (
    <div style={{...styles.statCard, ...(highlight ? styles.statCardHighlight : {})}}>
      <div style={styles.statIconWrap}><RippleIcon size={22} /></div>
      <div style={styles.statLabelRow}>
        <span style={styles.statLabel}>{label}</span>
        {isLive && <span style={styles.liveBadge}>Live</span>}
      </div>
      <div style={styles.statValue}>{value}</div>
      <div style={{...styles.statSubValue, ...(positive !== undefined ? (positive ? styles.positive : styles.negative) : {})}}>
        {subValue}
      </div>
    </div>
  );
}

function ETFCard({ etf, rank, isExpanded, onToggle, totalXRP }) {
  const xrpPercentage = ((etf.xrpLocked / totalXRP) * 100).toFixed(1);
  
  return (
    <div style={{...styles.etfCard, ...(etf.isIndex ? styles.etfCardIndex : {}), ...(isExpanded ? styles.etfCardExpanded : {})}} onClick={onToggle}>
      <div style={styles.etfHeader}>
        <div style={styles.etfRank}>#{rank}</div>
        <div style={styles.etfTicker}>{etf.ticker}</div>
        {etf.isIndex && <span style={styles.indexBadge}>Index</span>}
      </div>
      
      <div style={styles.etfName}>{etf.name}</div>
      
      {!etf.isIndex && (
        <div style={styles.etfPrice}>
          <span style={styles.priceValue}>${etf.price?.toFixed(2)}</span>
          <span style={{...styles.priceChange, ...(etf.change > 0 ? styles.positive : styles.negative)}}>
            {etf.change > 0 ? '+' : ''}{etf.change?.toFixed(2)}%
          </span>
        </div>
      )}
      
      {etf.isIndex && (
        <div style={styles.xrpWeight}>
          <span style={styles.weightLabel}>XRP Weight</span>
          <span style={styles.weightValue}>{etf.xrpWeight}%</span>
        </div>
      )}
      
      <div style={styles.etfStats}>
        <div style={styles.etfStat}><span style={styles.etfStatLabel}>AUM</span><span style={styles.etfStatValue}>${etf.aum}M</span></div>
        <div style={styles.etfStat}><span style={styles.etfStatLabel}>XRP</span><span style={styles.etfStatValue}>{etf.xrpLocked}M</span></div>
        <div style={styles.etfStat}><span style={styles.etfStatLabel}>Fee</span><span style={styles.etfStatValue}>{etf.fee}%</span></div>
      </div>

      <div style={styles.shareBar}><div style={{...styles.shareFill, width: `${xrpPercentage}%`}} /></div>
      <div style={styles.shareLabel}>{xrpPercentage}% of ETF XRP</div>

      {isExpanded && (
        <div style={styles.expandedInfo}>
          <div style={styles.expandedRow}><span>Exchange</span><span style={styles.expandedValue}>{etf.exchange}</span></div>
          <div style={styles.expandedRow}><span>Launch</span><span style={styles.expandedValue}>{etf.launchDate}</span></div>
          {!etf.isIndex && (
            <>
              <div style={styles.expandedRow}>
                <span>24h Volume</span>
                <span style={styles.expandedValue}>
                  ${etf.volume?.toFixed(2) || '0.00'}M
                  <span style={{ fontSize: 10, color: '#6b7a8a', marginLeft: 4 }}>
                    ({etf.shareVolume ? (etf.shareVolume / 1000).toFixed(0) + 'K' : '0'} shares)
                  </span>
                </span>
              </div>
              <div style={styles.expandedRow}>
                <span>Avg Volume</span>
                <span style={styles.expandedValue}>
                  {etf.avgVolume ? (etf.avgVolume / 1000).toFixed(0) + 'K' : '--'} shares/day
                </span>
              </div>
              <div style={styles.expandedRow}>
                <span>Market</span>
                <span style={{
                  ...styles.expandedValue,
                  color: etf.marketState === 'REGULAR' ? '#00ff88' : 
                         etf.marketState === 'PRE' || etf.marketState === 'POST' ? '#f59e0b' : '#6b7a8a'
                }}>
                  {etf.marketState === 'REGULAR' ? '🟢 Open' : 
                   etf.marketState === 'PRE' ? '🟡 Pre-Market' :
                   etf.marketState === 'POST' ? '🟡 After-Hours' : '⚫ Closed'}
                </span>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function NewsCard({ item }) {
  const categoryColors = {
    official: '#00CFFF',
    bank: '#00ff88',
    payments: '#a78bfa',
    institutional: '#f59e0b',
    'ripple-owned': '#00E5FF',
    analyst: '#ff6b9d',
    news: '#60a5fa',
  };

  return (
    <div style={styles.newsCard}>
      <div style={styles.newsCardHeader}>
        <div style={styles.newsAuthor}>
          <span style={styles.newsHandle}>@{item.handle}</span>
          <span style={styles.newsTime}>{item.time}</span>
        </div>
        <span style={{...styles.newsCategoryBadge, background: `${categoryColors[item.category]}18`, color: categoryColors[item.category], borderColor: `${categoryColors[item.category]}40`}}>
          {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
        </span>
      </div>
      <p style={styles.newsText}>{item.text}</p>
      <div style={styles.newsStats}>
        <span style={styles.newsStat}>♡ {item.likes.toLocaleString()}</span>
        <span style={styles.newsStat}>⟲ {item.retweets.toLocaleString()}</span>
      </div>
    </div>
  );
}

function ArticleCard({ article, onClick }) {
  return (
    <div 
      style={{...styles.articleCard, cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s'}} 
      onClick={onClick}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-4px)';
        e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 207, 255, 0.3)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      <div style={styles.articleIcon}>{article.icon}</div>
      <div style={styles.articleCategory}>{article.category}</div>
      <h3 style={styles.articleTitle}>{article.title}</h3>
      <p style={styles.articleDescription}>{article.description}</p>
      <div style={styles.articleFooter}>
        <span style={styles.articleReadTime}>{article.readTime} read</span>
        <span style={{...styles.articleLink, color: '#00CFFF'}}>Read →</span>
      </div>
    </div>
  );
}

function ArticleModal({ article, onClose }) {
  if (!article) return null;
  
  // Parse markdown-style formatting
  const formatContent = (text) => {
    if (!text) return <p style={{ color: '#888', fontStyle: 'italic', textAlign: 'center', padding: 40 }}>Full article coming soon...</p>;
    
    const lines = text.split('\n');
    return lines.map((line, i) => {
      // Headers
      if (line.startsWith('**') && line.endsWith('**')) {
        const headerText = line.slice(2, -2);
        return <h3 key={i} style={{ color: '#00CFFF', marginTop: 24, marginBottom: 12, fontSize: 18 }}>{headerText}</h3>;
      }
      // Bold sections within lines
      if (line.includes('**')) {
        const parts = line.split(/\*\*(.*?)\*\*/g);
        return (
          <p key={i} style={{ marginBottom: 8, lineHeight: 1.7 }}>
            {parts.map((part, j) => j % 2 === 1 ? <strong key={j} style={{ color: '#fff' }}>{part}</strong> : part)}
          </p>
        );
      }
      // Bullet points
      if (line.startsWith('• ') || line.startsWith('✓ ') || line.startsWith('✗ ')) {
        return <p key={i} style={{ marginBottom: 6, marginLeft: 16, lineHeight: 1.6 }}>{line}</p>;
      }
      // Empty lines
      if (line.trim() === '') {
        return <div key={i} style={{ height: 12 }} />;
      }
      // Regular text
      return <p key={i} style={{ marginBottom: 8, lineHeight: 1.7 }}>{line}</p>;
    });
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: 20,
      }}
      onClick={onClose}
    >
      <div 
        style={{
          background: 'linear-gradient(135deg, #0d1117 0%, #161b22 100%)',
          borderRadius: 16,
          border: '1px solid rgba(0, 207, 255, 0.3)',
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 0 60px rgba(0, 207, 255, 0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '24px 24px 16px',
          borderBottom: '1px solid rgba(0, 207, 255, 0.2)',
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16 }}>
            <span style={{ fontSize: 40 }}>{article.icon}</span>
            <div>
              <div style={{ 
                fontSize: 12, 
                color: '#00CFFF', 
                textTransform: 'uppercase', 
                letterSpacing: 1,
                marginBottom: 8,
              }}>
                {article.category} • {article.readTime} read
              </div>
              <h2 style={{ 
                margin: 0, 
                fontSize: 24, 
                fontWeight: 700,
                color: '#fff',
              }}>
                {article.title}
              </h2>
            </div>
          </div>
          <button 
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: 8,
              width: 36,
              height: 36,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 20,
              color: '#888',
              flexShrink: 0,
            }}
          >
            ✕
          </button>
        </div>
        
        {/* Content */}
        <div style={{
          padding: 24,
          overflowY: 'auto',
          color: '#c9d1d9',
          fontSize: 15,
        }}>
          {formatContent(article.content)}
        </div>
        
        {/* Footer */}
        <div style={{
          padding: '16px 24px',
          borderTop: '1px solid rgba(0, 207, 255, 0.2)',
          display: 'flex',
          justifyContent: 'flex-end',
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'linear-gradient(135deg, #00CFFF 0%, #0090CC 100%)',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              color: '#000',
              fontWeight: 600,
              cursor: 'pointer',
              fontSize: 14,
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div style={styles.faqItem} onClick={() => setIsOpen(!isOpen)}>
      <div style={styles.faqQuestion}>
        <span>{question}</span>
        <span style={styles.faqToggle}>{isOpen ? '−' : '+'}</span>
      </div>
      {isOpen && <p style={styles.faqAnswer}>{answer}</p>}
    </div>
  );
}

// Ripple brand colors - BRIGHTER/GLOWING
const rippleBlue = '#00CFFF';
const rippleBright = '#00E5FF';
const rippleDark = '#0090CC';
const rippleGray = '#6b7a8a';

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0a0f14',
    color: '#e7e9ea',
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '16px',
    overflowX: 'hidden',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 20,
    paddingBottom: 16,
    borderBottom: `1px solid ${rippleBlue}20`,
    flexWrap: 'wrap',
    gap: 12,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  logoText: {
    fontSize: 22,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    letterSpacing: '-0.5px',
    color: '#fff',
    textShadow: '0 0 20px rgba(0, 207, 255, 0.3)',
  },
  logoAccent: {
    color: rippleBlue,
    fontWeight: 500,
    textShadow: `0 0 20px ${rippleBlue}80`,
  },
  nav: {
    display: 'flex',
    gap: 6,
    flexWrap: 'wrap',
    justifyContent: 'center',
  },
  navButton: {
    background: 'transparent',
    border: `1px solid ${rippleGray}50`,
    color: rippleGray,
    padding: '8px 12px',
    fontSize: 12,
    fontWeight: 500,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  },
  navButtonActive: {
    background: `${rippleBlue}20`,
    borderColor: rippleBlue,
    color: rippleBright,
    boxShadow: `0 0 20px ${rippleBlue}40, inset 0 0 20px ${rippleBlue}10`,
    textShadow: `0 0 10px ${rippleBlue}`,
  },
  liveIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  liveDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: rippleBright,
    boxShadow: `0 0 15px ${rippleBright}, 0 0 30px ${rippleBlue}80`,
  },
  liveDotPulse: {},
  liveText: {
    fontSize: 13,
    fontWeight: 600,
    color: rippleBright,
    textShadow: `0 0 10px ${rippleBlue}`,
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 24,
  },
  statCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 16,
    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 ${rippleBlue}15`,
  },
  statCardHighlight: {
    border: `1px solid ${rippleBlue}60`,
    background: `linear-gradient(145deg, ${rippleBlue}15 0%, #0d1318 100%)`,
    boxShadow: `0 0 30px ${rippleBlue}25, inset 0 1px 0 ${rippleBlue}30`,
  },
  statIconWrap: {
    marginBottom: 12,
  },
  statLabelRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  statLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  liveBadge: {
    fontSize: 10,
    fontWeight: 600,
    background: `${rippleBright}25`,
    color: rippleBright,
    padding: '3px 8px',
    borderRadius: 4,
    boxShadow: `0 0 10px ${rippleBlue}40`,
    textShadow: `0 0 8px ${rippleBlue}`,
  },
  statValue: {
    fontSize: 26,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 4,
    textShadow: '0 0 20px rgba(255, 255, 255, 0.15)',
  },
  statSubValue: {
    fontSize: 13,
    fontWeight: 500,
    color: rippleGray,
  },
  positive: {
    color: '#00ff88',
    textShadow: '0 0 15px rgba(0, 255, 136, 0.5)',
  },
  negative: {
    color: '#ff4466',
    textShadow: '0 0 15px rgba(255, 68, 102, 0.5)',
  },
  controls: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
  },
  controlGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  controlLabel: {
    fontSize: 13,
    fontWeight: 500,
    color: rippleGray,
  },
  sortButton: {
    background: 'transparent',
    border: `1px solid ${rippleGray}40`,
    color: rippleGray,
    padding: '8px 14px',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  sortButtonActive: {
    borderColor: rippleBlue,
    color: rippleBright,
    background: `${rippleBlue}15`,
    boxShadow: `0 0 15px ${rippleBlue}30`,
    textShadow: `0 0 8px ${rippleBlue}`,
  },
  updateTime: {
    fontSize: 12,
    color: rippleGray,
  },
  etfGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
    gap: 16,
    marginBottom: 32,
  },
  etfCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}20`,
    borderRadius: 12,
    padding: 20,
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  },
  etfCardIndex: {
    background: `linear-gradient(145deg, ${rippleDark}20 0%, #0d1318 100%)`,
    borderColor: `${rippleDark}50`,
    boxShadow: `0 4px 20px ${rippleDark}20`,
  },
  etfCardExpanded: {
    borderColor: rippleBlue,
    boxShadow: `0 0 40px ${rippleBlue}30, inset 0 0 30px ${rippleBlue}05`,
  },
  etfHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 8,
  },
  etfRank: {
    fontSize: 13,
    fontWeight: 500,
    color: rippleGray,
  },
  etfTicker: {
    fontSize: 20,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: rippleBright,
    textShadow: `0 0 15px ${rippleBlue}60`,
  },
  indexBadge: {
    fontSize: 11,
    fontWeight: 600,
    background: `${rippleDark}40`,
    color: rippleBright,
    padding: '3px 8px',
    borderRadius: 4,
    boxShadow: `0 0 10px ${rippleDark}40`,
  },
  etfName: {
    fontSize: 14,
    color: rippleGray,
    marginBottom: 16,
  },
  etfPrice: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 12,
    marginBottom: 16,
  },
  priceValue: {
    fontSize: 24,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 600,
    color: '#fff',
  },
  priceChange: {
    fontSize: 14,
    fontWeight: 600,
  },
  xrpWeight: {
    marginBottom: 16,
  },
  weightLabel: {
    fontSize: 12,
    color: rippleGray,
    display: 'block',
    marginBottom: 4,
  },
  weightValue: {
    fontSize: 24,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 600,
    color: rippleDark,
  },
  etfStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(80px, 1fr))',
    gap: 10,
    marginBottom: 16,
  },
  etfStat: {
    textAlign: 'center',
  },
  etfStatLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: rippleGray,
    display: 'block',
    marginBottom: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  etfStatValue: {
    fontSize: 15,
    fontWeight: 600,
    color: '#fff',
  },
  shareBar: {
    height: 6,
    background: `${rippleGray}25`,
    borderRadius: 3,
    overflow: 'hidden',
    marginBottom: 8,
  },
  shareFill: {
    height: '100%',
    background: `linear-gradient(90deg, ${rippleBlue}, ${rippleBright})`,
    borderRadius: 3,
    transition: 'width 0.3s ease',
    boxShadow: `0 0 15px ${rippleBlue}60`,
  },
  shareLabel: {
    fontSize: 12,
    color: rippleGray,
    textAlign: 'center',
  },
  futureETFCard: {
    background: `linear-gradient(145deg, ${rippleGray}08 0%, transparent 100%)`,
    border: `2px dashed ${rippleBlue}40`,
    borderRadius: 12,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: 200,
    boxShadow: `inset 0 0 30px ${rippleBlue}08`,
  },
  futureETFIcon: {
    width: 48,
    height: 48,
    borderRadius: '50%',
    background: `${rippleBlue}20`,
    border: `2px dashed ${rippleBlue}60`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    color: rippleBright,
    marginBottom: 16,
    boxShadow: `0 0 20px ${rippleBlue}30`,
    textShadow: `0 0 10px ${rippleBlue}`,
  },
  futureETFTitle: {
    fontSize: 16,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 600,
    color: '#fff',
    marginBottom: 8,
    textShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
  },
  futureETFText: {
    fontSize: 13,
    color: rippleGray,
    lineHeight: 1.5,
    marginBottom: 16,
    maxWidth: 220,
  },
  futureETFPending: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: '#ffbb00',
    background: 'rgba(255, 187, 0, 0.15)',
    padding: '6px 12px',
    borderRadius: 20,
    boxShadow: '0 0 15px rgba(255, 187, 0, 0.2)',
    textShadow: '0 0 8px rgba(255, 187, 0, 0.5)',
  },
  pendingDot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: '#ffbb00',
    boxShadow: '0 0 10px #ffbb00',
    animation: 'pulse 2s infinite',
  },
  expandedInfo: {
    marginTop: 16,
    paddingTop: 16,
    borderTop: `1px solid ${rippleGray}30`,
  },
  expandedRow: {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 13,
    color: rippleGray,
    marginBottom: 8,
  },
  expandedValue: {
    color: '#fff',
    fontWeight: 500,
  },
  quickStats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: 12,
    marginBottom: 24,
    padding: 16,
    background: `linear-gradient(145deg, ${rippleBlue}12 0%, ${rippleBlue}05 100%)`,
    border: `1px solid ${rippleBlue}35`,
    borderRadius: 12,
    boxShadow: `0 0 30px ${rippleBlue}15, inset 0 1px 0 ${rippleBlue}20`,
  },
  quickStat: {
    textAlign: 'center',
  },
  quickStatLabel: {
    fontSize: 11,
    fontWeight: 500,
    color: rippleGray,
    display: 'block',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  quickStatValue: {
    fontSize: 14,
    fontWeight: 600,
    color: rippleBright,
    textShadow: `0 0 12px ${rippleBlue}80`,
  },
  newsSection: {
    marginBottom: 32,
  },
  newsSectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 24,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 4,
  },
  sectionSubtitle: {
    fontSize: 14,
    color: rippleGray,
  },
  manageButton: {
    background: `${rippleBlue}15`,
    border: `1px solid ${rippleBlue}50`,
    color: rippleBright,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 8,
    cursor: 'pointer',
    fontFamily: 'inherit',
    boxShadow: `0 0 15px ${rippleBlue}25`,
    textShadow: `0 0 8px ${rippleBlue}`,
  },
  accountManager: {
    background: '#121a22',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 20,
    marginBottom: 24,
  },
  accountManagerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    flexWrap: 'wrap',
    gap: 12,
  },
  accountManagerTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
  },
  addAccountRow: {
    display: 'flex',
    gap: 10,
  },
  accountInput: {
    background: `${rippleGray}20`,
    border: `1px solid ${rippleGray}40`,
    borderRadius: 6,
    padding: '10px 14px',
    color: '#fff',
    fontSize: 14,
    fontFamily: 'inherit',
    width: 160,
  },
  addButton: {
    background: `${rippleBlue}20`,
    border: `1px solid ${rippleBlue}40`,
    color: rippleBlue,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    borderRadius: 6,
  },
  accountList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 10,
  },
  accountItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: `${rippleGray}15`,
    padding: '10px 14px',
    borderRadius: 6,
    border: `1px solid ${rippleGray}30`,
  },
  accountInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  accountHandle: {
    fontSize: 13,
    fontWeight: 500,
    color: '#fff',
  },
  verifiedBadge: {
    fontSize: 12,
    color: rippleBlue,
  },
  accountCategory: {
    fontSize: 11,
    color: rippleGray,
    background: `${rippleGray}30`,
    padding: '2px 8px',
    borderRadius: 4,
  },
  removeButton: {
    background: 'transparent',
    border: 'none',
    color: '#ef4444',
    fontSize: 18,
    cursor: 'pointer',
    padding: '0 4px',
  },
  newsFilters: {
    display: 'flex',
    gap: 10,
    marginBottom: 24,
    flexWrap: 'wrap',
  },
  filterButton: {
    background: 'transparent',
    border: `1px solid ${rippleGray}40`,
    color: rippleGray,
    padding: '8px 16px',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    fontFamily: 'inherit',
    borderRadius: 6,
    transition: 'all 0.2s ease',
  },
  filterButtonActive: {
    borderColor: rippleBlue,
    color: rippleBright,
    background: `${rippleBlue}20`,
    boxShadow: `0 0 15px ${rippleBlue}30`,
    textShadow: `0 0 8px ${rippleBlue}`,
  },
  newsFeed: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  newsCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}20`,
    borderRadius: 12,
    padding: 20,
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  },
  newsCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  newsAuthor: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  newsHandle: {
    fontSize: 14,
    fontWeight: 600,
    color: rippleBright,
    textShadow: `0 0 10px ${rippleBlue}60`,
  },
  newsTime: {
    fontSize: 13,
    color: rippleGray,
  },
  newsCategoryBadge: {
    fontSize: 11,
    fontWeight: 500,
    padding: '4px 10px',
    borderRadius: 4,
    border: '1px solid',
  },
  newsText: {
    fontSize: 15,
    lineHeight: 1.65,
    color: '#e7e9ea',
    marginBottom: 16,
  },
  newsStats: {
    display: 'flex',
    gap: 24,
  },
  newsStat: {
    fontSize: 13,
    color: rippleGray,
  },
  apiNotice: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginTop: 24,
    padding: 16,
    background: '#f59e0b12',
    border: '1px solid #f59e0b30',
    borderRadius: 10,
    fontSize: 13,
    color: '#f59e0b',
  },
  apiNoticeIcon: {
    fontSize: 18,
  },
  learnSection: {
    marginBottom: 32,
  },
  learnHeader: {
    marginBottom: 32,
  },
  articleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))',
    gap: 20,
    marginBottom: 40,
  },
  articleCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}20`,
    borderRadius: 12,
    padding: 24,
    cursor: 'pointer',
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  },
  articleIcon: {
    fontSize: 32,
    marginBottom: 16,
    filter: 'drop-shadow(0 0 8px rgba(255, 255, 255, 0.3))',
  },
  articleCategory: {
    fontSize: 11,
    fontWeight: 600,
    color: rippleBright,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 10,
    textShadow: `0 0 10px ${rippleBlue}60`,
  },
  articleTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 600,
    color: '#fff',
    marginBottom: 10,
    lineHeight: 1.35,
  },
  articleDescription: {
    fontSize: 14,
    color: rippleGray,
    lineHeight: 1.6,
    marginBottom: 16,
  },
  articleFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  articleReadTime: {
    fontSize: 12,
    color: rippleGray,
  },
  articleLink: {
    fontSize: 13,
    fontWeight: 500,
    color: rippleBright,
    textShadow: `0 0 8px ${rippleBlue}60`,
  },
  faqSection: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}20`,
    borderRadius: 12,
    padding: 32,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },
  faqTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    marginBottom: 24,
    color: '#fff',
    textShadow: '0 0 15px rgba(255, 255, 255, 0.1)',
  },
  faqGrid: {
    display: 'grid',
    gap: 12,
  },
  faqItem: {
    background: `${rippleGray}10`,
    border: `1px solid ${rippleBlue}15`,
    borderRadius: 8,
    padding: 16,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  faqQuestion: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: 14,
    fontWeight: 500,
    color: '#fff',
  },
  faqToggle: {
    fontSize: 20,
    color: rippleBright,
    textShadow: `0 0 10px ${rippleBlue}`,
  },
  faqAnswer: {
    marginTop: 14,
    paddingTop: 14,
    borderTop: `1px solid ${rippleGray}25`,
    fontSize: 14,
    color: rippleGray,
    lineHeight: 1.65,
  },
  footer: {
    textAlign: 'center',
    paddingTop: 32,
    borderTop: `1px solid ${rippleBlue}20`,
  },
  footerText: {
    fontSize: 12,
    color: rippleGray,
    marginBottom: 12,
  },
  footerBrand: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 500,
    color: rippleBright,
    textShadow: `0 0 15px ${rippleBlue}60`,
  },
  footerLink: {
    background: 'none',
    border: 'none',
    color: rippleGray,
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'none',
    transition: 'color 0.2s ease',
  },
  legalModal: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    borderRadius: 16,
    padding: 32,
    maxWidth: 600,
    maxHeight: '80vh',
    overflowY: 'auto',
    position: 'relative',
    border: `1px solid ${rippleBlue}30`,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
  },
  legalTitle: {
    fontSize: 24,
    fontWeight: 700,
    marginBottom: 20,
    color: '#fff',
    paddingRight: 40,
  },
  legalContent: {
    fontSize: 14,
    color: '#d1d5db',
    lineHeight: 1.7,
  },
  modalCloseBtn: {
    position: 'absolute',
    top: 16,
    right: 16,
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: 28,
    cursor: 'pointer',
    width: 36,
    height: 36,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 8,
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: 20,
  },
  // Whales Section Styles
  whalesSection: {
    marginBottom: 32,
  },
  whalesHeader: {
    marginBottom: 24,
  },
  whaleStatsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 12,
    marginBottom: 24,
  },
  whaleStat: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 16,
    textAlign: 'center',
    boxShadow: `0 4px 20px rgba(0, 0, 0, 0.3), inset 0 1px 0 ${rippleBlue}15`,
  },
  whaleStatValue: {
    fontSize: 28,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: rippleBright,
    display: 'block',
    marginBottom: 6,
    textShadow: `0 0 20px ${rippleBlue}60`,
  },
  whaleStatLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  tiersSection: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  tiersTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 6,
  },
  tiersSubtitle: {
    fontSize: 13,
    color: rippleGray,
    marginBottom: 20,
  },
  tiersGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    gap: 10,
  },
  tierCard: {
    background: `${rippleGray}10`,
    border: '1px solid',
    borderRadius: 10,
    padding: 14,
    textAlign: 'center',
    transition: 'all 0.2s ease',
  },
  tierEmoji: {
    fontSize: 28,
    marginBottom: 8,
  },
  tierName: {
    fontSize: 11,
    fontWeight: 600,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  tierLabel: {
    fontSize: 14,
    fontWeight: 700,
    marginBottom: 8,
  },
  tierBalance: {
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 4,
  },
  tierHolders: {
    fontSize: 11,
    color: rippleGray,
  },
  richListSection: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  richListTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 16,
  },
  richListTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  richListHeader: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 120px 100px 100px',
    gap: 12,
    padding: '12px 16px',
    background: `${rippleBlue}10`,
    borderRadius: 8,
    fontSize: 11,
    fontWeight: 600,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  richListRow: {
    display: 'grid',
    gridTemplateColumns: '50px 1fr 120px 100px 100px',
    gap: 12,
    padding: '14px 16px',
    background: `${rippleGray}08`,
    borderRadius: 8,
    alignItems: 'center',
    transition: 'all 0.2s ease',
  },
  richListColRank: {
    fontSize: 14,
    fontWeight: 600,
    color: rippleGray,
  },
  richListColName: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  },
  walletName: {
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
  },
  walletAddress: {
    fontSize: 11,
    color: rippleGray,
    fontFamily: 'monospace',
  },
  richListColBalance: {
    fontSize: 14,
    fontWeight: 600,
    color: rippleBright,
    textShadow: `0 0 10px ${rippleBlue}40`,
  },
  richListColPercent: {
    fontSize: 13,
    fontWeight: 500,
    color: '#fff',
  },
  richListColType: {},
  typeTag: {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 4,
    textTransform: 'capitalize',
  },
  distributionSection: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
    marginBottom: 24,
  },
  distributionTitle: {
    fontSize: 16,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 16,
  },
  distributionBar: {
    display: 'flex',
    height: 48,
    borderRadius: 8,
    overflow: 'hidden',
    marginBottom: 16,
  },
  distributionSegment: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px 8px',
    minWidth: 60,
  },
  segmentLabel: {
    fontSize: 10,
    fontWeight: 500,
    color: 'rgba(255, 255, 255, 0.9)',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  segmentValue: {
    fontSize: 14,
    fontWeight: 700,
    color: '#fff',
    textShadow: '0 1px 2px rgba(0,0,0,0.5)',
  },
  distributionLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 20,
    justifyContent: 'center',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    fontSize: 12,
    color: rippleGray,
  },
  legendDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    boxShadow: '0 0 8px currentColor',
  },
  dataSource: {
    textAlign: 'center',
    fontSize: 12,
    color: rippleGray,
    padding: 16,
  },
  // Theories Section Styles
  theoriesSection: {
    marginBottom: 40,
  },
  theoriesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  theoriesFollowLink: {
    fontSize: 13,
    fontWeight: 600,
    color: rippleBright,
    textDecoration: 'none',
    padding: '8px 16px',
    background: `${rippleBlue}15`,
    border: `1px solid ${rippleBlue}40`,
    borderRadius: 8,
    textShadow: `0 0 10px ${rippleBlue}60`,
    transition: 'all 0.2s ease',
  },
  theoriesGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: 16,
  },
  theoryCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 20,
    transition: 'all 0.25s ease',
    boxShadow: '0 4px 15px rgba(0, 0, 0, 0.3)',
  },
  theoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  theoryEmoji: {
    fontSize: 28,
  },
  theoryMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  confidenceBadge: {
    fontSize: 11,
    fontWeight: 600,
    padding: '4px 10px',
    borderRadius: 4,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  theoryTimeframe: {
    fontSize: 11,
    color: rippleGray,
    fontWeight: 500,
  },
  theoryTitle: {
    fontSize: 16,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 10,
  },
  theorySummary: {
    fontSize: 14,
    fontWeight: 500,
    color: rippleBright,
    marginBottom: 12,
    lineHeight: 1.5,
    textShadow: `0 0 20px ${rippleBlue}30`,
  },
  theoryDetails: {
    fontSize: 13,
    color: rippleGray,
    lineHeight: 1.6,
  },
  theoriesDisclaimer: {
    marginTop: 20,
    padding: 16,
    background: `${rippleGray}10`,
    border: `1px solid ${rippleGray}30`,
    borderRadius: 8,
    fontSize: 12,
    color: rippleGray,
    textAlign: 'center',
  },
  // Never Sell Section Styles
  neverSellSection: {
    marginBottom: 32,
  },
  neverSellHeader: {
    marginBottom: 24,
  },
  strategySelector: {
    display: 'flex',
    gap: 12,
    marginBottom: 24,
  },
  strategyButton: {
    flex: 1,
    background: 'transparent',
    border: `1px solid ${rippleGray}40`,
    color: rippleGray,
    padding: '14px 20px',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 10,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  strategyButtonActive: {
    background: `${rippleBlue}20`,
    borderColor: rippleBlue,
    color: rippleBright,
    boxShadow: `0 0 20px ${rippleBlue}30`,
    textShadow: `0 0 10px ${rippleBlue}`,
  },
  calculatorGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    marginBottom: 24,
  },
  calcInputs: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
  },
  calcResults: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
  },
  calcTitle: {
    fontSize: 16,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 20,
  },
  inputGroup: {
    marginBottom: 16,
  },
  inputLabel: {
    display: 'block',
    fontSize: 12,
    fontWeight: 500,
    color: rippleGray,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  calcInput: {
    width: '100%',
    padding: '12px 14px',
    fontSize: 16,
    fontWeight: 600,
    background: `${rippleGray}15`,
    border: `1px solid ${rippleGray}30`,
    borderRadius: 8,
    color: '#fff',
    fontFamily: 'inherit',
    outline: 'none',
  },
  rangeInput: {
    width: '100%',
    height: 6,
    borderRadius: 3,
    background: `${rippleGray}30`,
    cursor: 'pointer',
    accentColor: rippleBlue,
  },
  rangeValue: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    color: rippleBright,
    marginTop: 6,
    textShadow: `0 0 10px ${rippleBlue}60`,
  },
  resultCard: {
    background: `${rippleGray}10`,
    border: `1px solid ${rippleGray}20`,
    borderRadius: 8,
    padding: 14,
    marginBottom: 10,
  },
  resultHighlight: {
    background: `${rippleBlue}15`,
    border: `1px solid ${rippleBlue}40`,
    boxShadow: `0 0 20px ${rippleBlue}20`,
  },
  resultLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 4,
  },
  resultValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
  },
  resultValueBig: {
    fontSize: 24,
    fontWeight: 700,
    color: rippleBright,
    textShadow: `0 0 20px ${rippleBlue}60`,
    display: 'block',
  },
  resultSub: {
    display: 'block',
    fontSize: 12,
    color: rippleGray,
    marginTop: 4,
  },
  strategyExplainers: {
    marginBottom: 24,
  },
  explainerCard: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 24,
  },
  explainerTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 12,
  },
  explainerText: {
    fontSize: 14,
    color: rippleGray,
    lineHeight: 1.7,
    marginBottom: 20,
  },
  explainerSteps: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 10,
  },
  step: {
    background: `${rippleGray}10`,
    border: `1px solid ${rippleGray}20`,
    borderRadius: 8,
    padding: 10,
    fontSize: 11,
    color: '#fff',
    textAlign: 'center',
  },
  stepNum: {
    display: 'block',
    fontSize: 18,
    fontWeight: 700,
    color: rippleBright,
    marginBottom: 6,
    textShadow: `0 0 10px ${rippleBlue}60`,
  },
  presetsSection: {
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 12,
    padding: 20,
  },
  presetsTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 14,
  },
  presetButtons: {
    display: 'flex',
    gap: 10,
    flexWrap: 'wrap',
  },
  presetButton: {
    background: `${rippleGray}15`,
    border: `1px solid ${rippleGray}30`,
    color: rippleGray,
    padding: '10px 16px',
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 8,
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: 'inherit',
  },
  // Exchange Reserves Styles
  exchangeReservesSection: {
    marginTop: 32,
    background: 'linear-gradient(145deg, #151d26 0%, #0d1318 100%)',
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 16,
    padding: 24,
  },
  reservesHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  reservesTitle: {
    fontSize: 18,
    fontFamily: '"Plus Jakarta Sans", sans-serif',
    fontWeight: 700,
    color: '#fff',
    marginBottom: 4,
  },
  reservesSubtitle: {
    fontSize: 13,
    color: rippleGray,
  },
  reservesLink: {
    fontSize: 12,
    fontWeight: 600,
    color: rippleBright,
    textDecoration: 'none',
    padding: '6px 12px',
    background: `${rippleBlue}15`,
    border: `1px solid ${rippleBlue}40`,
    borderRadius: 6,
    textShadow: `0 0 10px ${rippleBlue}60`,
  },
  reserveStatsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginBottom: 20,
  },
  reserveMainCard: {
    background: `${rippleGray}10`,
    border: `1px solid ${rippleGray}20`,
    borderRadius: 12,
    padding: 16,
    textAlign: 'center',
  },
  reserveMainLabel: {
    display: 'block',
    fontSize: 11,
    fontWeight: 500,
    color: rippleGray,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: 8,
  },
  reserveMainValue: {
    display: 'block',
    fontSize: 32,
    fontWeight: 700,
    color: rippleBright,
    textShadow: `0 0 20px ${rippleBlue}60`,
    marginBottom: 4,
  },
  reserveMainSub: {
    display: 'block',
    fontSize: 12,
    color: rippleGray,
    marginBottom: 12,
  },
  reserveChange: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
  },
  reserveChangeNegative: {
    fontSize: 13,
    fontWeight: 600,
    color: '#00ff88',
    textShadow: '0 0 10px #00ff8860',
  },
  reserveChangePositive: {
    fontSize: 13,
    fontWeight: 600,
    color: '#00ff88',
    textShadow: '0 0 10px #00ff8860',
  },
  reserveChangePeriod: {
    fontSize: 11,
    color: rippleGray,
  },
  exchangeGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: 10,
    marginBottom: 20,
  },
  exchangeCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    background: `${rippleGray}08`,
    border: `1px solid ${rippleGray}15`,
    borderRadius: 10,
    padding: 14,
  },
  exchangeIcon: {
    fontSize: 24,
  },
  exchangeInfo: {
    flex: 1,
  },
  exchangeName: {
    display: 'block',
    fontSize: 13,
    fontWeight: 600,
    color: '#fff',
  },
  exchangeReserve: {
    display: 'block',
    fontSize: 12,
    color: rippleGray,
  },
  exchangeTrend: {
    textAlign: 'right',
  },
  trendDown: {
    fontSize: 12,
    fontWeight: 600,
    color: '#00ff88',
    textShadow: '0 0 8px #00ff8860',
  },
  trendNeutral: {
    fontSize: 12,
    fontWeight: 600,
    color: rippleGray,
  },
  reserveHistorySection: {
    marginBottom: 16,
  },
  reserveHistoryTitle: {
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    marginBottom: 16,
  },
  reserveTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  timelineDate: {
    width: 70,
    fontSize: 11,
    color: rippleGray,
    fontWeight: 500,
  },
  timelineBar: {
    flex: 1,
    height: 8,
    background: `${rippleGray}20`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  timelineBarFill: {
    height: '100%',
    background: `linear-gradient(90deg, ${rippleBlue}, ${rippleBright})`,
    borderRadius: 4,
    transition: 'width 0.5s ease',
  },
  timelineValue: {
    width: 60,
    fontSize: 12,
    fontWeight: 600,
    color: '#fff',
    textAlign: 'right',
  },
  reservesFooter: {
    padding: 14,
    background: `${rippleBlue}10`,
    border: `1px solid ${rippleBlue}25`,
    borderRadius: 8,
    fontSize: 12,
    color: rippleGray,
    lineHeight: 1.5,
  },
  // ETF Timeline Styles
  etfTimelineSection: {
    marginTop: 20,
    padding: 20,
    background: 'linear-gradient(135deg, rgba(0, 207, 255, 0.05) 0%, rgba(138, 43, 226, 0.05) 100%)',
    borderRadius: 12,
    border: '1px solid rgba(0, 207, 255, 0.15)',
  },
  etfTimelineGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: 12,
    marginTop: 15,
  },
  etfTimelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '10px 12px',
    background: 'rgba(0, 207, 255, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(0, 207, 255, 0.15)',
  },
  etfTimelineDot: {
    width: 10,
    height: 10,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00ff88, #00CFFF)',
    flexShrink: 0,
  },
  etfTimelineContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
  },
  etfTimelineDate: {
    fontSize: 11,
    color: '#00ff88',
    fontWeight: 600,
  },
  etfTimelineName: {
    fontSize: 12,
    color: '#fff',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Macro Indicators Styles
  macroSection: {
    marginTop: 20,
    padding: 20,
    background: 'linear-gradient(135deg, rgba(255, 159, 67, 0.05) 0%, rgba(239, 68, 68, 0.05) 100%)',
    borderRadius: 12,
    border: '1px solid rgba(255, 159, 67, 0.15)',
  },
  macroGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
    gap: 12,
  },
  macroCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
    padding: '12px 14px',
    background: 'rgba(255, 159, 67, 0.08)',
    borderRadius: 8,
    border: '1px solid rgba(255, 159, 67, 0.2)',
  },
  macroLabel: {
    fontSize: 12,
    color: '#9ca3af',
  },
  macroValue: {
    fontSize: 18,
    fontWeight: 700,
    color: '#fff',
  },
  macroChange: {
    fontSize: 11,
    color: '#f59e0b',
  },
  // Whale Search Styles
  searchContainer: {
    position: 'relative',
    marginBottom: 15,
  },
  whaleSearchInput: {
    width: '100%',
    padding: '12px 40px 12px 15px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8,
    color: '#ffffff',
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box',
  },
  clearSearchBtn: {
    position: 'absolute',
    right: 10,
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.1)',
    border: 'none',
    borderRadius: '50%',
    width: 24,
    height: 24,
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: 12,
  },
  // Financial Indicators Styles
  indicatorsSection: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 16,
    padding: 24,
    marginTop: 30,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  indicatorsSectionTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 8,
  },
  indicatorsSubtitle: {
    fontSize: 14,
    color: rippleGray,
    marginBottom: 20,
  },
  indicatorsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 15,
  },
  indicatorCard: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: 12,
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  indicatorEmoji: {
    fontSize: 24,
  },
  indicatorName: {
    fontSize: 13,
    fontWeight: 600,
    color: rippleGray,
  },
  indicatorValue: {
    fontSize: 22,
    fontWeight: 700,
    color: '#00CFFF',
  },
  indicatorNote: {
    fontSize: 11,
    color: 'rgba(255,255,255,0.5)',
    fontStyle: 'italic',
  },
  indicatorsDisclaimer: {
    marginTop: 15,
    padding: 12,
    background: 'rgba(245, 158, 11, 0.1)',
    borderRadius: 8,
    fontSize: 12,
    color: '#f59e0b',
    textAlign: 'center',
  },
  // ETF Timeline Styles
  etfTimelineSection: {
    background: 'rgba(255,255,255,0.03)',
    borderRadius: 16,
    padding: 24,
    marginTop: 30,
    border: '1px solid rgba(255,255,255,0.08)',
  },
  etfTimelineTitle: {
    fontSize: 20,
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: 20,
  },
  etfTimeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  etfTimelineItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 15,
    padding: '12px 16px',
    background: 'rgba(0, 207, 255, 0.05)',
    borderRadius: 10,
    border: '1px solid rgba(0, 207, 255, 0.15)',
  },
  etfTimelineDot: {
    width: 28,
    height: 28,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00CFFF, #00ff88)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    color: '#0a0a0a',
    flexShrink: 0,
  },
  etfTimelineContent: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px 16px',
    flex: 1,
  },
  etfTimelineTicker: {
    fontSize: 16,
    fontWeight: 700,
    color: '#00CFFF',
    minWidth: 60,
  },
  etfTimelineName: {
    fontSize: 14,
    color: '#ffffff',
    minWidth: 140,
  },
  etfTimelineDate: {
    fontSize: 13,
    color: '#00ff88',
    fontWeight: 600,
  },
  etfTimelineExchange: {
    fontSize: 12,
    color: rippleGray,
    background: 'rgba(255,255,255,0.08)',
    padding: '3px 8px',
    borderRadius: 4,
  },
};
